/**
 * Dequantizing ggml block formats.
 *
 * Ten formats: the five legacy 32-element blocks (`Q4_0`, `Q4_1`, `Q5_0`,
 * `Q5_1`, `Q8_0`) and the five K-quant 256-element super-blocks (`Q2_K`,
 * `Q3_K`, `Q4_K`, `Q5_K`, `Q6_K`). Together they are the overwhelming majority
 * of published GGUF weights. The IQ* and TQ* families are not here: they carry
 * lookup-table codebooks, which is a different shape of problem and a separate
 * piece of evidence.
 *
 * ## The rule these had to satisfy before shipping
 *
 * Plan §4: **a decoder ships only when it has been measured against ground
 * truth.** A decoder that unpacks the wrong nibble produces a plausible
 * distribution rather than an error, and a histogram is exactly the artifact
 * where nobody can tell.
 *
 * That is not a general caution here, it is the specific failure mode. Take
 * `Q4_0`: the low nibble of byte `j` is element `j`, and the high nibble is
 * element `j + 16` — the two halves of the block are interleaved, not adjacent.
 * A decoder that walks the nibbles in order produces **a permutation of the
 * correct values**. Every statistic this app shows from a tensor — the
 * histogram, the min, the max, the mean, the standard deviation, the quantile
 * table — is invariant under permutation. The bug would be invisible in every
 * artifact the page draws. Only a positional comparison finds it, which is why
 * the evidence is positional.
 *
 * ## How they were verified
 *
 * `docs/evidence/ggml-dequant-golden.json` holds ggml's own output: the fixture
 * was generated by calling `dequantize_row_q4_0` and its nine siblings directly,
 * linked against ggml at commit `74ce157`, on synthesised quantized *bytes*.
 * `scripts/gen-ggml-golden.c` is the generator, checked in so the fixture can be
 * reproduced rather than trusted.
 *
 * Bytes in rather than floats in, because the decoder's job is byte to value —
 * that is the direction pinned, and it can place codes ggml's own quantizer
 * would never choose, which is where a transcription error hides. Four
 * adversarial variants per format on the reviewer's condition (2026-08-10): a
 * walking pattern touching every nibble position, alternating extremes,
 * sign-boundary codes, and a denormal-adjacent scale.
 *
 * The fixture is an oracle rather than a restatement of this file's assumptions:
 * nothing about the block layout was transcribed into the generator. It hands
 * ggml raw bytes and ggml's own struct definitions interpret them. So the
 * mapping it records is ggml's, and the code below is an independent
 * transcription checked against it.
 *
 * `Q8_0` additionally has an end-to-end check against a real repo — the BF16
 * twin of a Q8_0 vision projector in `prism-ml/Ternary-Bonsai-27B-gguf`,
 * correlation 0.99998, in `docs/evidence/gguf-q8-0-decode.md`. That covers what
 * golden vectors cannot: that the bytes arriving from a real fetch are the bytes
 * the decoder expects. The nine formats added later share that fetch path
 * exactly and inherit the check only to the extent the path is shared — where
 * they do differ is block size, and `rowRunRanges` asserts block alignment for
 * each of them from `GGML_BLOCK_BYTES` below, throwing rather than decoding a
 * misaligned slice.
 *
 * ## Why `Math.fround` appears in the middle of the arithmetic
 *
 * ggml computes in C `float`, rounding to 32 bits after *every* operation. JS
 * computes in f64 and rounds once, on the store into a `Float32Array`. For a
 * single multiply those agree exactly. For `scale * q + min` they can differ by
 * an ulp, because C rounds twice and JS rounds once. `f32()` marks each place
 * the reference rounds and the store does not, so these functions reproduce
 * ggml bit-for-bit rather than approximately. An ulp would not matter to a
 * histogram; being able to assert exactness rather than closeness matters to
 * the fixture, and a decoder that is exactly right needs no tolerance argued
 * for it.
 *
 * Pure: bytes in, floats out. No I/O.
 */

import { decodeFloat16Bits, decodeFloats } from "./dtype.js";

/** One C `float` rounding step. See the header. */
const f32 = Math.fround;

/** Elements per legacy block, and per K-quant super-block. */
const QK = 32;
const QK_K = 256;

/** Read a `ggml_half` (f16, little-endian) at `at`. */
function half(view: DataView, at: number): number {
  return decodeFloat16Bits(view.getUint16(at, true));
}

/**
 * Decode one block into `out[base .. base + blockElems)`.
 *
 * Writing into a caller-owned array rather than returning one: a tensor is
 * millions of blocks, and a `Float32Array` per block would be the dominant cost
 * of the whole read.
 */
type DecodeBlock = (
  view: DataView,
  at: number,
  out: Float32Array,
  base: number,
) => void;

interface GgmlBlockFormat {
  readonly blockBytes: number;
  readonly blockElems: number;
  readonly decodeBlock: DecodeBlock;
}

/*
 * ---------------------------------------------------------------------------
 * Legacy blocks: 32 elements, low nibbles first then high nibbles.
 * ---------------------------------------------------------------------------
 *
 * All four 4/5-bit formats share the interleave described in the header:
 * `qs[j]`'s low nibble is element `j` and its high nibble is element `j + 16`.
 */

/** `block_q4_0 { ggml_half d; uint8_t qs[16]; }` — 18 bytes. */
const decodeQ4_0: DecodeBlock = (view, at, out, base) => {
  const d = half(view, at);
  for (let j = 0; j < QK / 2; j++) {
    const b = view.getUint8(at + 2 + j);
    // The `- 8` is the zero point: codes 0..15 centre on 8, so the block is
    // signed without storing a sign. Reading it unsigned shifts every value in
    // the tensor by `8 * d` — a histogram of the right shape in the wrong place.
    out[base + j] = ((b & 0x0f) - 8) * d;
    out[base + QK / 2 + j] = ((b >> 4) - 8) * d;
  }
};

/** `block_q4_1 { ggml_half d, m; uint8_t qs[16]; }` — 20 bytes. */
const decodeQ4_1: DecodeBlock = (view, at, out, base) => {
  const d = half(view, at);
  const m = half(view, at + 2);
  for (let j = 0; j < QK / 2; j++) {
    const b = view.getUint8(at + 4 + j);
    // Affine rather than symmetric: an explicit min replaces the fixed zero
    // point, so the codes here are unsigned and there is no `- 8`.
    out[base + j] = f32((b & 0x0f) * d) + m;
    out[base + QK / 2 + j] = f32((b >> 4) * d) + m;
  }
};

/** `block_q5_0 { ggml_half d; uint8_t qh[4]; uint8_t qs[16]; }` — 22 bytes. */
const decodeQ5_0: DecodeBlock = (view, at, out, base) => {
  const d = half(view, at);
  /*
   * The fifth bit of all 32 quants, as one little-endian u32. Bit `j` belongs
   * to element `j` and bit `j + 16` to element `j + 16` — the same interleave
   * as the nibbles, which is what makes the two halves line up.
   *
   * `>>>` rather than `>>`: a qh with the top bit set is ≥ 2^31, and JS's
   * signed shift would smear that bit downward across the whole word.
   */
  const qh = view.getUint32(at + 2, true);
  for (let j = 0; j < QK / 2; j++) {
    const b = view.getUint8(at + 6 + j);
    const h0 = ((qh >>> j) & 1) << 4;
    const h1 = ((qh >>> (j + QK / 2)) & 1) << 4;
    out[base + j] = (((b & 0x0f) | h0) - 16) * d;
    out[base + QK / 2 + j] = (((b >> 4) | h1) - 16) * d;
  }
};

/** `block_q5_1 { ggml_half d, m; uint8_t qh[4]; uint8_t qs[16]; }` — 24 bytes. */
const decodeQ5_1: DecodeBlock = (view, at, out, base) => {
  const d = half(view, at);
  const m = half(view, at + 2);
  const qh = view.getUint32(at + 4, true);
  for (let j = 0; j < QK / 2; j++) {
    const b = view.getUint8(at + 8 + j);
    const h0 = ((qh >>> j) & 1) << 4;
    const h1 = ((qh >>> (j + QK / 2)) & 1) << 4;
    out[base + j] = f32(((b & 0x0f) | h0) * d) + m;
    out[base + QK / 2 + j] = f32(((b >> 4) | h1) * d) + m;
  }
};

/** `block_q8_0 { ggml_half d; int8_t qs[32]; }` — 34 bytes. */
const decodeQ8_0: DecodeBlock = (view, at, out, base) => {
  const d = half(view, at);
  for (let i = 0; i < QK; i++) {
    /*
     * `getInt8`, not `getUint8`: the quants are signed, and reading them
     * unsigned folds the negative half of every weight onto +128..255 — a
     * distribution that looks like a plausible bimodal model and is nonsense.
     *
     * No interleave here and no zero point. `Q8_0` is the one format where the
     * bytes are simply the values in order, which is why it was verified first.
     */
    out[base + i] = d * view.getInt8(at + 2 + i);
  }
};

/*
 * ---------------------------------------------------------------------------
 * K-quants: 256-element super-blocks with quantized per-group scales.
 * ---------------------------------------------------------------------------
 *
 * The family's idea: rather than one scale per 32 elements stored at full f16
 * width, store one f16 scale for all 256 and a *quantized* scale per 16- or
 * 32-element group. That buys most of the fidelity of fine-grained scaling at a
 * fraction of the overhead, and it means the scale unpacking is itself a bit
 * layout that can be got wrong — `Q3_K` packs 16 six-bit scales into 12 bytes.
 */

/**
 * `Q4_K` and `Q5_K` scale/min unpacking — ggml's `get_scale_min_k4`.
 *
 * Eight 6-bit scales and eight 6-bit mins in 12 bytes. The first four of each
 * are plain low-6-bit reads; the last four are split, with the low nibble in
 * one byte and the high two bits stolen from the top of another. Two functions
 * rather than one returning a pair, to keep this allocation-free in a loop that
 * runs once per 64 elements of every tensor.
 */
/** `Q3_K`'s scale-shuffle masks: two bits per lane, and four bits per lane. */
const KMASK1 = 0x03030303;
const KMASK2 = 0x0f0f0f0f;

function k4Scale(view: DataView, q: number, j: number): number {
  if (j < 4) return view.getUint8(q + j) & 63;
  return (view.getUint8(q + j + 4) & 0x0f) | ((view.getUint8(q + j - 4) >> 6) << 4);
}

function k4Min(view: DataView, q: number, j: number): number {
  if (j < 4) return view.getUint8(q + j + 4) & 63;
  return (view.getUint8(q + j + 4) >> 4) | ((view.getUint8(q + j) >> 6) << 4);
}

/**
 * `block_q2_K { uint8_t scales[16]; uint8_t qs[64]; ggml_half d, dmin; }` — 84 bytes.
 *
 * 16 groups of 16. Each `scales` byte carries the group's scale in its low
 * nibble and its min in its high nibble, both further scaled by the
 * super-block's `d` and `dmin`.
 */
const decodeQ2_K: DecodeBlock = (view, at, out, base) => {
  const d = half(view, at + 80);
  const min = half(view, at + 82);
  let is = 0;
  let y = base;
  for (let n = 0; n < QK_K; n += 128) {
    // Two bits per element, so 128 elements come from 32 bytes, read four
    // times over at shifts 0, 2, 4, 6.
    const q = at + 16 + (n >> 2);
    for (let shift = 0; shift < 8; shift += 2) {
      for (let hi = 0; hi < 32; hi += 16) {
        const sc = view.getUint8(at + is);
        is += 1;
        const dl = f32(d * (sc & 0x0f));
        const ml = f32(min * (sc >> 4));
        for (let l = 0; l < 16; l++) {
          out[y] = f32(dl * ((view.getUint8(q + hi + l) >> shift) & 3)) - ml;
          y += 1;
        }
      }
    }
  }
};

/**
 * `block_q3_K { uint8_t hmask[32]; uint8_t qs[64]; uint8_t scales[12]; ggml_half d; }` — 110 bytes.
 *
 * The awkward one. Two low bits per element in `qs`, the third bit in `hmask`,
 * and 16 six-bit *signed-by-bias* scales packed into 12 bytes by a shuffle that
 * only makes sense as a transcription of the reference.
 */
const decodeQ3_K: DecodeBlock = (view, at, out, base) => {
  const dAll = half(view, at + 108);

  /*
   * ggml's scale unpack, transcribed literally through a 16-byte scratch: it
   * treats the 12 bytes as three u32s, redistributes their bits into four, and
   * reads the result back as 16 signed bytes. Reproducing the u32 shuffle
   * rather than deriving a per-byte closed form keeps this checkable against
   * `dequantize_row_q3_K` line by line — and the derivation is exactly the kind
   * of step that is easy to get subtly wrong and impossible to see afterwards.
   *
   * The reads of `a0`/`a1` are taken before either is overwritten, as in the
   * reference where `aux[2]` and `aux[3]` are computed first for that reason.
   */
  const a0 = view.getUint32(at + 96, true);
  const a1 = view.getUint32(at + 100, true);
  const tmp = view.getUint32(at + 104, true);
  const scales = new DataView(new ArrayBuffer(16));
  scales.setUint32(0, ((a0 & KMASK2) | (((tmp >>> 0) & KMASK1) << 4)) >>> 0, true);
  scales.setUint32(4, ((a1 & KMASK2) | (((tmp >>> 2) & KMASK1) << 4)) >>> 0, true);
  scales.setUint32(
    8,
    (((a0 >>> 4) & KMASK2) | (((tmp >>> 4) & KMASK1) << 4)) >>> 0,
    true,
  );
  scales.setUint32(
    12,
    (((a1 >>> 4) & KMASK2) | (((tmp >>> 6) & KMASK1) << 4)) >>> 0,
    true,
  );

  let is = 0;
  let y = base;
  /*
   * `m` walks one bit per group across all 8 groups of the super-block and is
   * *not* reset between the two halves — 32 hmask bytes × 8 bits is exactly one
   * high bit per element, and resetting would read the first four bits twice.
   */
  let m = 1;
  for (let n = 0; n < QK_K; n += 128) {
    const q = at + 32 + (n >> 2);
    for (let shift = 0; shift < 8; shift += 2) {
      for (let hi = 0; hi < 32; hi += 16) {
        const dl = f32(dAll * (scales.getInt8(is) - 32));
        is += 1;
        for (let l = 0; l < 16; l++) {
          /*
           * Inverted: a *set* mask bit means the high bit is 0 and nothing is
           * subtracted; a clear bit subtracts 4. Reading it the intuitive way
           * round negates the third bit of every element.
           *
           * `hmask` is at the block start and is never advanced — unlike `qs`,
           * one set of 32 bytes serves both halves.
           */
          const h = (view.getUint8(at + hi + l) & m) !== 0 ? 0 : 4;
          out[y] = dl * (((view.getUint8(q + hi + l) >> shift) & 3) - h);
          y += 1;
        }
      }
      m <<= 1;
    }
  }
};

/**
 * `block_q4_K { ggml_half d, dmin; uint8_t scales[12]; uint8_t qs[128]; }` — 144 bytes.
 *
 * 8 groups of 32. The most common weight format in the published corpus.
 */
const decodeQ4_K: DecodeBlock = (view, at, out, base) => {
  const d = half(view, at);
  const min = half(view, at + 2);
  const sc = at + 4;
  let is = 0;
  let y = base;
  for (let j = 0; j < QK_K; j += 64) {
    const d1 = f32(d * k4Scale(view, sc, is));
    const m1 = f32(min * k4Min(view, sc, is));
    const d2 = f32(d * k4Scale(view, sc, is + 1));
    const m2 = f32(min * k4Min(view, sc, is + 1));
    // 32 bytes serve 64 elements: low nibbles are the first group, high nibbles
    // the second. Contiguous per group here, unlike the legacy interleave.
    const q = at + 16 + (j >> 1);
    for (let l = 0; l < 32; l++) {
      out[y] = f32(d1 * (view.getUint8(q + l) & 0x0f)) - m1;
      y += 1;
    }
    for (let l = 0; l < 32; l++) {
      out[y] = f32(d2 * (view.getUint8(q + l) >> 4)) - m2;
      y += 1;
    }
    is += 2;
  }
};

/**
 * `block_q5_K { ggml_half d, dmin; uint8_t scales[12]; uint8_t qh[32]; uint8_t qs[128]; }` — 176 bytes.
 *
 * `Q4_K` plus a fifth bit per element, taken from a 32-byte mask that — like
 * `Q3_K`'s — serves the whole super-block rather than advancing with `qs`.
 */
const decodeQ5_K: DecodeBlock = (view, at, out, base) => {
  const d = half(view, at);
  const min = half(view, at + 2);
  const sc = at + 4;
  const qh = at + 16;
  let is = 0;
  let y = base;
  // Two bits of `qh[l]` per 64-element pass, stepping up by two each time.
  let u1 = 1;
  let u2 = 2;
  for (let j = 0; j < QK_K; j += 64) {
    const d1 = f32(d * k4Scale(view, sc, is));
    const m1 = f32(min * k4Min(view, sc, is));
    const d2 = f32(d * k4Scale(view, sc, is + 1));
    const m2 = f32(min * k4Min(view, sc, is + 1));
    const ql = at + 48 + (j >> 1);
    for (let l = 0; l < 32; l++) {
      const h = (view.getUint8(qh + l) & u1) !== 0 ? 16 : 0;
      out[y] = f32(d1 * ((view.getUint8(ql + l) & 0x0f) + h)) - m1;
      y += 1;
    }
    for (let l = 0; l < 32; l++) {
      const h = (view.getUint8(qh + l) & u2) !== 0 ? 16 : 0;
      out[y] = f32(d2 * ((view.getUint8(ql + l) >> 4) + h)) - m2;
      y += 1;
    }
    is += 2;
    u1 <<= 2;
    u2 <<= 2;
  }
};

/**
 * `block_q6_K { uint8_t ql[128]; uint8_t qh[64]; int8_t scales[16]; ggml_half d; }` — 210 bytes.
 *
 * 16 groups of 16, and the only K-quant whose group scales are **signed** —
 * `int8_t`, not `uint8_t`. Reading them unsigned turns every negative scale
 * into a large positive one and mirrors part of the distribution.
 */
const decodeQ6_K: DecodeBlock = (view, at, out, base) => {
  const d = half(view, at + 208);
  for (let n = 0; n < 2; n++) {
    const ql = at + n * 64;
    const qh = at + 128 + n * 32;
    const sc = at + 192 + n * 8;
    const y = base + n * 128;
    for (let l = 0; l < 32; l++) {
      // Four elements per iteration, 32 apart in the output and drawn from two
      // `ql` bytes and one `qh` byte, each contributing a different bit pair.
      const is = l >> 4;
      const h = view.getUint8(qh + l);
      const lo = view.getUint8(ql + l);
      const hi = view.getUint8(ql + l + 32);
      const q1 = ((lo & 0x0f) | (((h >> 0) & 3) << 4)) - 32;
      const q2 = ((hi & 0x0f) | (((h >> 2) & 3) << 4)) - 32;
      const q3 = ((lo >> 4) | (((h >> 4) & 3) << 4)) - 32;
      const q4 = ((hi >> 4) | (((h >> 6) & 3) << 4)) - 32;
      out[y + l] = f32(d * view.getInt8(sc + is)) * q1;
      out[y + l + 32] = f32(d * view.getInt8(sc + is + 2)) * q2;
      out[y + l + 64] = f32(d * view.getInt8(sc + is + 4)) * q3;
      out[y + l + 96] = f32(d * view.getInt8(sc + is + 6)) * q4;
    }
  }
};

/**
 * The formats this module decodes.
 *
 * Adding one is adding a row here plus a case in `docs/evidence/`'s generator.
 * The golden test walks this map against the fixture, so an entry that is not
 * measured fails rather than ships.
 */
/*
 * ---------------------------------------------------------------------------
 * The IQ family: a codebook instead of a linear scale.
 * ---------------------------------------------------------------------------
 *
 * Everything above stores a *linear* code — the quant is a small integer, and
 * the value is that integer times a scale, optionally offset. The i-quants do
 * not. The 4-bit code is an **index into a fixed table** of sixteen values
 * spaced non-uniformly, chosen because weight distributions are bell-shaped and
 * a linear grid spends most of its codes where almost no weights are.
 *
 * That table is the whole difference, and it is why these two come first. Every
 * higher-mass IQ type (IQ3_XXS, IQ2_XXS, IQ1_S…) reuses the same
 * index-into-a-table mechanism with a bigger table; proving it here proves it
 * once (reviewer, 2026-08-13).
 *
 * Added after the report button's first three reports turned out to be one
 * coverage gap: 65-92% of the bytes in the reported checkpoints are stored in
 * schemes this module could not read.
 */

/**
 * `kvalues_iq4nl` — the sixteen values a 4-bit i-quant code selects.
 *
 * Copied from ggml's `ggml-common.h` at the pinned commit and **not** derived:
 * these are fitted constants, not samples of a curve, so a generated
 * approximation would be wrong in a way that still looks plausible on a
 * histogram. The golden vectors are what hold them honest — every entry is
 * exercised, because the fixture's walking pattern visits all sixteen codes.
 *
 * Note the asymmetry: -127 at one end, 113 at the other, and the zero code is
 * `1` rather than `0`. A decoder that "tidied" that into a symmetric table
 * would shift every weight in the tensor.
 */
const KVALUES_IQ4NL = new Int8Array([
  -127, -104, -83, -65, -49, -35, -22, -10, 1, 13, 25, 38, 53, 69, 89, 113,
]);

/**
 * IQ3_XXS's three constant tables, taken from ggml at the pinned commit.
 *
 * **Extracted, not transcribed.** A 256-entry grid is precisely the kind of
 * table `ggml-types.ts` warns about — one that looks right and is checked by
 * nothing — so these were emitted by a C program compiled against the same
 * `ggml-common.h` the goldens were generated from, rather than copied by eye.
 * The IQ4_NL table above could be read in one glance and was copied; a thousand
 * bytes cannot, and the difference in method is the difference in risk.
 *
 * The grid is stored **flattened to little-endian bytes** rather than as 256
 * `uint32`s. ggml reads each entry through a `uint8 *`, so what it consumes is
 * the host byte order, and flattening at extraction time settles that once here
 * instead of leaving an endianness assumption in the decode loop.
 *
 * `ksigns_iq2xs` maps a 7-bit code to a sign byte, and `kmask_iq2xs` is the bit
 * each of the eight positions tests. Both are shared with the IQ2 family, which
 * is why they are named for it.
 */
const IQ3XXS_GRID = new Uint8Array([
  4, 4, 4, 4, 20, 4, 4, 4, 36, 4, 4, 4, 12, 12, 4, 4, 28, 12, 4, 4, 62, 12, 4, 4, 4, 20,
  4, 4, 20, 20, 4, 4, 12, 28, 4, 4, 20, 36, 4, 4, 28, 62, 4, 4, 44, 62, 4, 4, 12, 4, 12,
  4, 28, 4, 12, 4, 4, 12, 12, 4, 20, 12, 12, 4, 12, 20, 12, 4, 44, 20, 12, 4, 4, 28, 12,
  4, 20, 28, 12, 4, 12, 36, 12, 4, 36, 44, 12, 4, 4, 62, 12, 4, 4, 4, 20, 4, 20, 4, 20,
  4, 36, 4, 20, 4, 12, 12, 20, 4, 4, 20, 20, 4, 20, 20, 20, 4, 12, 28, 20, 4, 28, 28,
  20, 4, 62, 28, 20, 4, 12, 44, 20, 4, 62, 44, 20, 4, 44, 62, 20, 4, 12, 4, 28, 4, 62,
  4, 28, 4, 4, 12, 28, 4, 20, 12, 28, 4, 44, 20, 28, 4, 4, 62, 28, 4, 28, 12, 36, 4, 62,
  28, 36, 4, 36, 36, 36, 4, 62, 44, 36, 4, 28, 62, 36, 4, 44, 62, 36, 4, 12, 4, 44, 4,
  62, 4, 44, 4, 20, 28, 44, 4, 20, 44, 44, 4, 44, 28, 52, 4, 36, 52, 52, 4, 4, 12, 62,
  4, 36, 12, 62, 4, 52, 12, 62, 4, 28, 36, 62, 4, 12, 52, 62, 4, 12, 4, 4, 12, 28, 4, 4,
  12, 4, 12, 4, 12, 20, 12, 4, 12, 12, 20, 4, 12, 28, 20, 4, 12, 4, 28, 4, 12, 20, 28,
  4, 12, 36, 28, 4, 12, 62, 36, 4, 12, 4, 44, 4, 12, 4, 4, 12, 12, 20, 4, 12, 12, 12,
  12, 12, 12, 4, 20, 12, 12, 20, 20, 12, 12, 12, 4, 20, 12, 28, 4, 20, 12, 4, 12, 20,
  12, 20, 12, 20, 12, 12, 20, 20, 12, 4, 28, 20, 12, 20, 62, 20, 12, 4, 4, 28, 12, 20,
  4, 28, 12, 4, 20, 28, 12, 12, 28, 28, 12, 52, 36, 28, 12, 52, 52, 28, 12, 12, 4, 36,
  12, 44, 4, 36, 12, 4, 44, 36, 12, 4, 20, 44, 12, 36, 20, 44, 12, 52, 36, 44, 12, 12,
  62, 44, 12, 44, 4, 52, 12, 20, 20, 62, 12, 4, 36, 62, 12, 4, 4, 4, 20, 20, 4, 4, 20,
  12, 12, 4, 20, 28, 12, 4, 20, 4, 20, 4, 20, 20, 20, 4, 20, 52, 20, 4, 20, 12, 28, 4,
  20, 20, 36, 4, 20, 12, 4, 12, 20, 28, 4, 12, 20, 44, 4, 12, 20, 4, 12, 12, 20, 20, 12,
  12, 20, 12, 20, 12, 20, 4, 28, 12, 20, 28, 52, 12, 20, 62, 52, 12, 20, 4, 62, 12, 20,
  4, 4, 20, 20, 20, 4, 20, 20, 12, 12, 20, 20, 62, 12, 20, 20, 4, 20, 20, 20, 20, 20,
  20, 20, 62, 28, 20, 20, 4, 36, 20, 20, 44, 44, 20, 20, 12, 4, 28, 20, 4, 12, 28, 20,
  36, 12, 28, 20, 4, 62, 28, 20, 36, 62, 28, 20, 44, 28, 36, 20, 28, 44, 36, 20, 28, 4,
  44, 20, 62, 20, 44, 20, 12, 36, 44, 20, 36, 62, 44, 20, 12, 4, 62, 20, 28, 4, 62, 20,
  52, 12, 62, 20, 44, 36, 62, 20, 12, 4, 4, 28, 4, 12, 4, 28, 20, 12, 4, 28, 12, 20, 4,
  28, 28, 20, 4, 28, 4, 44, 4, 28, 44, 52, 4, 28, 20, 62, 4, 28, 4, 4, 12, 28, 20, 4,
  12, 28, 4, 20, 12, 28, 12, 28, 12, 28, 36, 36, 12, 28, 52, 36, 12, 28, 12, 4, 20, 28,
  28, 4, 20, 28, 4, 12, 20, 28, 44, 20, 20, 28, 20, 44, 20, 28, 20, 62, 20, 28, 12, 12,
  28, 28, 28, 28, 28, 28, 4, 28, 36, 28, 62, 36, 36, 28, 20, 62, 36, 28, 4, 4, 44, 28,
  52, 4, 44, 28, 20, 20, 44, 28, 44, 44, 44, 28, 36, 12, 52, 28, 52, 28, 52, 28, 28, 52,
  52, 28, 28, 28, 62, 28, 4, 52, 62, 28, 36, 4, 4, 36, 62, 12, 4, 36, 44, 28, 4, 36, 62,
  28, 4, 36, 28, 44, 4, 36, 62, 44, 4, 36, 36, 62, 12, 36, 4, 20, 20, 36, 62, 28, 20,
  36, 4, 36, 20, 36, 4, 52, 20, 36, 52, 52, 20, 36, 62, 4, 28, 36, 44, 36, 28, 36, 36,
  4, 36, 36, 12, 44, 36, 36, 36, 52, 36, 36, 44, 20, 44, 36, 28, 36, 44, 36, 4, 62, 44,
  36, 44, 4, 62, 36, 4, 12, 62, 36, 20, 12, 62, 36, 4, 28, 62, 36, 20, 12, 4, 44, 12,
  36, 4, 44, 4, 62, 4, 44, 4, 4, 12, 44, 52, 4, 12, 44, 52, 20, 12, 44, 44, 44, 12, 44,
  36, 12, 20, 44, 20, 28, 20, 44, 20, 62, 20, 44, 20, 4, 28, 44, 28, 44, 28, 44, 4, 12,
  36, 44, 28, 20, 36, 44, 62, 20, 36, 44, 20, 62, 36, 44, 20, 4, 44, 44, 12, 28, 44, 44,
  4, 44, 52, 44, 36, 20, 62, 44, 20, 36, 62, 44, 36, 20, 4, 52, 36, 36, 4, 52, 52, 36,
  4, 52, 36, 52, 4, 52, 12, 20, 12, 52, 12, 52, 12, 52, 62, 12, 20, 52, 36, 52, 20, 52,
  4, 28, 28, 52, 52, 28, 28, 52, 36, 36, 36, 52, 44, 4, 44, 52, 20, 44, 44, 52, 28, 28,
  52, 52, 28, 4, 62, 52, 12, 20, 62, 52, 28, 4, 4, 62, 44, 4, 4, 62, 62, 4, 4, 62, 4,
  12, 4, 62, 20, 28, 4, 62, 20, 44, 4, 62, 52, 20, 12, 62, 4, 36, 12, 62, 20, 12, 20,
  62, 44, 36, 20, 62, 20, 44, 20, 62, 4, 4, 28, 62, 44, 12, 28, 62, 28, 28, 28, 62, 4,
  52, 28, 62, 12, 20, 36, 62, 12, 36, 36, 62, 4, 4, 44, 62, 20, 4, 44, 62, 36, 20, 44,
  62, 4, 28, 52, 62,
]);

const KSIGNS_IQ2XS = new Uint8Array([
  0, 129, 130, 3, 132, 5, 6, 135, 136, 9, 10, 139, 12, 141, 142, 15, 144, 17, 18, 147,
  20, 149, 150, 23, 24, 153, 154, 27, 156, 29, 30, 159, 160, 33, 34, 163, 36, 165, 166,
  39, 40, 169, 170, 43, 172, 45, 46, 175, 48, 177, 178, 51, 180, 53, 54, 183, 184, 57,
  58, 187, 60, 189, 190, 63, 192, 65, 66, 195, 68, 197, 198, 71, 72, 201, 202, 75, 204,
  77, 78, 207, 80, 209, 210, 83, 212, 85, 86, 215, 216, 89, 90, 219, 92, 221, 222, 95,
  96, 225, 226, 99, 228, 101, 102, 231, 232, 105, 106, 235, 108, 237, 238, 111, 240,
  113, 114, 243, 116, 245, 246, 119, 120, 249, 250, 123, 252, 125, 126, 255,
]);

const KMASK_IQ2XS = new Uint8Array([1, 2, 4, 8, 16, 32, 64, 128]);

/**
 * `block_iq4_nl { ggml_half d; uint8_t qs[16]; }` — 18 bytes, 32 elements.
 *
 * The nibble order is the legacy one: low nibbles fill the first half of the
 * block and high nibbles the second, so `qs[j]` contributes to `y[j]` and
 * `y[j+16]` rather than to a neighbouring pair.
 */
const decodeIQ4_NL: DecodeBlock = (view, at, out, base) => {
  const d = half(view, at);
  for (let j = 0; j < QK / 2; j++) {
    const b = view.getUint8(at + 2 + j);
    /*
     * No `f32` here, and that is deliberate rather than an omission.
     *
     * `out` is a `Float32Array`, so the store rounds. With a single multiply
     * between the read and the store, an explicit `Math.fround` would round the
     * same value twice, which is idempotent — it buys nothing. I wrote one
     * anyway, claimed in a comment that it was load-bearing, then removed it as
     * a mutation check and every golden still passed. The claim was false, so it
     * is gone. Contrast `decodeIQ4_XS`, where an intermediate makes the same
     * call genuinely necessary.
     *
     * `as number` because the index is a 4-bit value and the table has sixteen
     * entries, so it cannot be out of range — the same narrowing the shape
     * readers in `packed-int.ts` and `block-scaled.ts` make. A `?? 0` here would
     * quietly substitute a plausible weight for a bug.
     */
    out[base + j] = d * (KVALUES_IQ4NL[b & 0x0f] as number);
    out[base + QK / 2 + j] = d * (KVALUES_IQ4NL[b >> 4] as number);
  }
};

/**
 * `block_iq4_xs { ggml_half d; uint16_t scales_h; uint8_t scales_l[4]; uint8_t qs[128]; }`
 * — 136 bytes, 256 elements.
 *
 * IQ4_NL's codebook with K-quant structure over it: eight sub-blocks of 32, each
 * with its own 6-bit scale. The scale is **split across two planes** — four low
 * bits packed two-per-byte in `scales_l`, two high bits in `scales_h` — and
 * recombined as `low | (high << 4)`, then biased by `-32` so it can go negative.
 *
 * That bias is the part worth stating: a sub-block whose scale index is below 32
 * has a *negative* `dl`, which flips the codebook, and it is also where ggml's
 * `-0.0` comes from — a zero code under a negative scale. The fixture pins those
 * signs deliberately.
 */
const decodeIQ4_XS: DecodeBlock = (view, at, out, base) => {
  const d = half(view, at);
  const scalesH = view.getUint16(at + 2, true);
  for (let ib = 0; ib < QK_K / 32; ib++) {
    const low = (view.getUint8(at + 4 + (ib >> 1)) >> (4 * (ib & 1))) & 0x0f;
    const high = (scalesH >> (2 * ib)) & 3;
    const ls = low | (high << 4);
    /*
     * **`f32` here is load-bearing**, unlike the single-multiply case in
     * `decodeIQ4_NL`. ggml holds `dl` in a C `float`, so the product is rounded
     * to 32 bits *before* it is multiplied by the codebook value; leaving it in
     * a double rounds once at the end instead. Double-rounding and
     * single-rounding can disagree in the last bit, and the goldens are compared
     * with `Object.is`.
     *
     * Honest caveat: **the current fixture does not catch this.** Removing this
     * `f32` leaves every golden passing, so the argument above is from the
     * arithmetic — `d` has 24 mantissa bits and the codebook entry up to 7 more,
     * which overflows float32's mantissa where the two roundings can part — and
     * not from a measurement. A byte pattern that separates them would make a
     * good addition to the fixture's variants.
     */
    const dl = f32(d * (ls - 32));
    const qs = at + 8 + ib * 16;
    const dst = base + ib * 32;
    for (let j = 0; j < 16; j++) {
      const b = view.getUint8(qs + j);
      // No `f32` on the store, for the reason given in `decodeIQ4_NL`: the
      // `Float32Array` already rounds, and rounding twice changes nothing.
      out[dst + j] = dl * (KVALUES_IQ4NL[b & 0x0f] as number);
      out[dst + 16 + j] = dl * (KVALUES_IQ4NL[b >> 4] as number);
    }
  }
};

/**
 * `iq2xxs_grid`, extracted from ggml at the pinned commit on the same terms as
 * `IQ3XXS_GRID` — emitted by a compiled program, not copied by eye, and
 * flattened to little-endian bytes because that is how ggml reads it.
 *
 * Eight bytes per entry rather than four, and only three distinct magnitudes in
 * the whole table: 8, 25 and 43. That is what "(almost) true 2-bit" means here
 * — each of eight positions takes one of three magnitudes, with the sign
 * supplied separately from `KSIGNS_IQ2XS`.
 *
 * **No zero anywhere in it**, which is worth knowing before reading a
 * histogram: an IQ2_XXS tensor's sparsity is 0.00% by construction, not because
 * the model failed to learn any zeros.
 */
const IQ2XXS_GRID = new Uint8Array([
  8, 8, 8, 8, 8, 8, 8, 8, 43, 8, 8, 8, 8, 8, 8, 8, 25, 25, 8, 8, 8, 8, 8, 8, 8, 43, 8,
  8, 8, 8, 8, 8, 43, 43, 8, 8, 8, 8, 8, 8, 25, 8, 25, 8, 8, 8, 8, 8, 8, 25, 25, 8, 8, 8,
  8, 8, 8, 8, 43, 8, 8, 8, 8, 8, 43, 8, 43, 8, 8, 8, 8, 8, 8, 43, 43, 8, 8, 8, 8, 8, 43,
  43, 43, 8, 8, 8, 8, 8, 25, 8, 8, 25, 8, 8, 8, 8, 8, 25, 8, 25, 8, 8, 8, 8, 8, 8, 25,
  25, 8, 8, 8, 8, 8, 43, 25, 25, 8, 8, 8, 8, 25, 8, 43, 25, 8, 8, 8, 8, 8, 25, 43, 25,
  8, 8, 8, 8, 8, 8, 8, 43, 8, 8, 8, 8, 43, 8, 8, 43, 8, 8, 8, 8, 43, 43, 8, 43, 8, 8, 8,
  8, 43, 8, 43, 43, 8, 8, 8, 8, 25, 8, 8, 8, 25, 8, 8, 8, 8, 25, 8, 8, 25, 8, 8, 8, 8,
  8, 25, 8, 25, 8, 8, 8, 25, 25, 25, 8, 25, 8, 8, 8, 8, 8, 8, 25, 25, 8, 8, 8, 8, 25, 8,
  43, 25, 8, 8, 8, 8, 43, 25, 43, 25, 8, 8, 8, 8, 8, 8, 8, 43, 8, 8, 8, 43, 8, 8, 8, 43,
  8, 8, 8, 43, 8, 43, 8, 43, 8, 8, 8, 43, 8, 8, 43, 43, 8, 8, 8, 25, 8, 8, 8, 8, 25, 8,
  8, 8, 25, 8, 8, 8, 25, 8, 8, 8, 8, 25, 8, 8, 25, 8, 8, 25, 8, 43, 8, 8, 25, 8, 8, 8,
  25, 43, 8, 8, 25, 8, 8, 8, 8, 8, 25, 8, 25, 8, 8, 43, 8, 8, 25, 8, 25, 8, 8, 8, 43, 8,
  25, 8, 25, 8, 8, 8, 8, 43, 25, 8, 25, 8, 8, 25, 8, 8, 43, 8, 25, 8, 8, 8, 25, 8, 43,
  8, 25, 8, 8, 8, 8, 25, 43, 8, 25, 8, 8, 8, 25, 43, 43, 8, 25, 8, 8, 8, 8, 8, 8, 25,
  25, 8, 8, 43, 8, 8, 8, 25, 25, 8, 8, 8, 43, 8, 8, 25, 25, 8, 8, 8, 8, 43, 8, 25, 25,
  8, 8, 43, 25, 8, 25, 25, 25, 8, 8, 25, 43, 43, 25, 25, 25, 8, 8, 8, 8, 8, 43, 25, 25,
  8, 8, 25, 8, 25, 43, 25, 25, 8, 8, 25, 43, 8, 8, 43, 25, 8, 8, 8, 8, 25, 8, 43, 25, 8,
  8, 8, 8, 8, 25, 43, 25, 8, 8, 8, 25, 8, 43, 43, 25, 8, 8, 8, 25, 43, 43, 43, 25, 8, 8,
  8, 8, 8, 8, 8, 43, 8, 8, 25, 25, 8, 8, 8, 43, 8, 8, 8, 43, 8, 8, 8, 43, 8, 8, 8, 25,
  25, 8, 8, 43, 8, 8, 8, 43, 43, 8, 8, 43, 8, 8, 25, 8, 8, 25, 8, 43, 8, 8, 8, 25, 8,
  25, 8, 43, 8, 8, 8, 8, 25, 25, 8, 43, 8, 8, 43, 8, 25, 25, 8, 43, 8, 8, 8, 43, 8, 43,
  8, 43, 8, 8, 8, 25, 8, 8, 25, 43, 8, 8, 8, 8, 8, 25, 25, 43, 8, 8, 43, 8, 8, 8, 43,
  43, 8, 8, 8, 25, 25, 8, 43, 43, 8, 8, 25, 8, 8, 8, 8, 8, 25, 8, 8, 25, 8, 8, 8, 8, 25,
  8, 8, 8, 25, 8, 8, 8, 25, 8, 25, 8, 43, 8, 8, 8, 25, 8, 8, 8, 8, 25, 8, 8, 25, 8, 8,
  8, 43, 25, 8, 8, 25, 8, 8, 25, 8, 43, 8, 8, 25, 8, 8, 8, 25, 43, 8, 8, 25, 8, 25, 25,
  25, 43, 8, 8, 25, 8, 8, 8, 8, 8, 25, 8, 25, 8, 8, 43, 8, 8, 25, 8, 25, 8, 8, 8, 43, 8,
  25, 8, 25, 8, 8, 8, 25, 25, 25, 8, 25, 8, 43, 43, 25, 25, 25, 8, 25, 8, 8, 8, 8, 43,
  25, 8, 25, 8, 8, 25, 43, 8, 43, 8, 25, 8, 25, 25, 8, 25, 43, 8, 25, 8, 8, 8, 8, 8, 8,
  25, 25, 8, 8, 43, 8, 8, 8, 25, 25, 8, 8, 8, 43, 8, 8, 25, 25, 8, 25, 25, 43, 8, 8, 25,
  25, 8, 25, 43, 8, 25, 8, 25, 25, 8, 8, 8, 8, 43, 8, 25, 25, 8, 8, 43, 25, 8, 25, 25,
  25, 8, 43, 8, 43, 25, 25, 25, 25, 8, 8, 8, 8, 8, 43, 25, 25, 8, 43, 25, 25, 8, 43, 25,
  25, 8, 25, 8, 8, 8, 8, 43, 25, 8, 8, 25, 8, 8, 8, 43, 25, 8, 8, 8, 25, 8, 8, 43, 25,
  8, 8, 8, 8, 25, 8, 43, 25, 8, 25, 8, 8, 43, 8, 43, 25, 8, 8, 8, 8, 8, 25, 43, 25, 8,
  25, 25, 8, 8, 25, 43, 25, 8, 8, 8, 43, 43, 25, 43, 25, 8, 25, 8, 25, 25, 43, 43, 25,
  8, 8, 8, 8, 8, 8, 8, 43, 8, 43, 8, 8, 8, 8, 8, 43, 8, 43, 43, 8, 8, 8, 8, 43, 8, 8,
  25, 8, 25, 8, 8, 43, 8, 25, 8, 43, 25, 8, 8, 43, 8, 8, 8, 8, 43, 8, 8, 43, 8, 43, 8,
  8, 43, 8, 8, 43, 8, 25, 43, 43, 8, 25, 8, 43, 8, 8, 43, 8, 25, 25, 8, 43, 8, 8, 8, 8,
  8, 43, 8, 43, 8, 43, 8, 8, 8, 43, 8, 43, 8, 25, 8, 8, 8, 8, 25, 43, 8, 8, 25, 8, 8, 8,
  25, 43, 8, 8, 8, 25, 8, 8, 25, 43, 8, 8, 8, 8, 25, 8, 25, 43, 8, 43, 25, 25, 25, 8,
  25, 43, 8, 8, 8, 8, 8, 25, 25, 43, 8, 25, 8, 8, 25, 25, 25, 43, 8, 8, 25, 43, 25, 25,
  25, 43, 8, 8, 8, 25, 43, 43, 25, 43, 8, 8, 43, 8, 8, 8, 43, 43, 8, 8, 8, 43, 8, 8, 43,
  43, 8, 8, 25, 25, 43, 8, 43, 43, 8, 8, 25, 8, 25, 43, 43, 43, 8, 25, 8, 8, 8, 8, 8, 8,
  25, 8, 25, 8, 8, 8, 8, 8, 25, 8, 8, 25, 8, 8, 8, 8, 25, 8, 43, 25, 8, 8, 8, 8, 25, 25,
  8, 43, 8, 8, 8, 8, 25, 8, 25, 43, 8, 8, 8, 8, 25, 8, 8, 8, 25, 8, 8, 8, 25, 8, 43, 8,
  25, 8, 8, 8, 25, 43, 25, 25, 25, 8, 8, 8, 25, 8, 8, 43, 25, 8, 8, 8, 25, 25, 8, 8, 43,
  8, 8, 8, 25, 8, 25, 8, 43, 8, 8, 8, 25, 8, 8, 25, 43, 8, 8, 8, 25, 8, 8, 8, 8, 25, 8,
  8, 25, 8, 8, 43, 8, 25, 8, 8, 25, 25, 8, 43, 25, 25, 8, 8, 25, 8, 8, 8, 43, 25, 8, 8,
  25, 25, 25, 8, 43, 25, 8, 8, 25, 25, 8, 8, 8, 43, 8, 8, 25, 8, 8, 25, 8, 43, 8, 8, 25,
  8, 43, 8, 25, 43, 8, 8, 25, 43, 25, 25, 25, 43, 8, 8, 25, 8, 43, 43, 25, 43, 8, 8, 25,
  8, 8, 8, 8, 8, 25, 8, 25, 8, 43, 8, 8, 8, 25, 8, 25, 8, 8, 43, 8, 8, 25, 8, 25, 8, 8,
  8, 43, 8, 25, 8, 25, 25, 43, 25, 43, 8, 25, 8, 25, 43, 8, 25, 8, 25, 25, 8, 25, 8, 25,
  43, 8, 25, 25, 8, 25, 8, 8, 8, 8, 43, 25, 8, 25, 25, 8, 8, 8, 8, 43, 8, 25, 8, 25, 8,
  8, 8, 43, 8, 25, 8, 8, 25, 8, 8, 43, 8, 25, 8, 8, 8, 25, 8, 43, 8, 25, 25, 25, 8, 25,
  8, 43, 8, 25, 8, 8, 8, 8, 25, 43, 8, 25, 8, 43, 25, 25, 25, 43, 8, 25, 25, 8, 43, 25,
  25, 43, 8, 25, 43, 8, 8, 43, 25, 43, 8, 25, 25, 25, 8, 25, 43, 43, 8, 25, 8, 8, 25,
  43, 43, 43, 8, 25, 8, 8, 8, 8, 8, 8, 25, 25, 8, 43, 8, 8, 8, 8, 25, 25, 25, 8, 25, 8,
  8, 8, 25, 25, 25, 43, 25, 8, 8, 8, 25, 25, 8, 8, 43, 8, 8, 8, 25, 25, 8, 8, 8, 43, 8,
  8, 25, 25, 8, 43, 8, 43, 8, 8, 25, 25, 8, 25, 8, 8, 25, 8, 25, 25, 43, 8, 8, 25, 25,
  8, 25, 25, 8, 25, 43, 43, 25, 8, 25, 25, 25, 8, 25, 43, 43, 8, 25, 25, 8, 8, 25, 43,
  8, 25, 25, 25, 43, 8, 25, 43, 8, 25, 25, 25, 43, 43, 8, 8, 25, 25, 25, 25, 25, 8, 8,
  8, 43, 25, 25, 25, 8, 25, 25, 25, 43, 25, 25, 25, 8, 8, 8, 8, 8, 43, 25, 25, 25, 8,
  25, 8, 8, 43, 25, 25, 25, 43, 25, 8, 8, 43, 25, 25, 8, 25, 43, 25, 8, 43, 25, 25, 8,
  8, 8, 25, 25, 43, 25, 25, 8, 43, 8, 8, 43, 43, 25, 25, 8, 25, 8, 8, 8, 8, 43, 25, 8,
  8, 25, 8, 8, 8, 43, 25, 8, 8, 8, 25, 8, 8, 43, 25, 8, 43, 43, 25, 8, 8, 43, 25, 8, 8,
  8, 8, 25, 8, 43, 25, 25, 25, 25, 25, 25, 8, 43, 25, 8, 43, 25, 8, 43, 8, 43, 25, 8, 8,
  43, 25, 43, 8, 43, 25, 8, 8, 8, 8, 8, 25, 43, 25, 25, 25, 8, 8, 8, 25, 43, 25, 8, 8,
  25, 8, 25, 25, 43, 25, 43, 8, 25, 8, 25, 25, 43, 25, 8, 25, 8, 43, 25, 25, 43, 25, 43,
  8, 8, 25, 8, 43, 43, 25, 8, 8, 8, 8, 8, 8, 8, 43, 43, 8, 8, 8, 8, 8, 8, 43, 43, 43, 8,
  8, 8, 8, 8, 43, 25, 8, 8, 25, 8, 8, 8, 43, 43, 8, 8, 43, 8, 8, 8, 43, 8, 25, 8, 8, 25,
  8, 8, 43, 8, 43, 25, 8, 25, 8, 8, 43, 8, 8, 8, 25, 25, 8, 8, 43, 25, 8, 25, 8, 43, 8,
  8, 43, 25, 8, 8, 8, 8, 25, 8, 43, 8, 25, 8, 8, 8, 25, 8, 43, 8, 8, 25, 8, 8, 25, 8,
  43, 25, 25, 25, 8, 8, 25, 8, 43, 8, 8, 8, 25, 8, 25, 8, 43, 8, 8, 43, 25, 8, 25, 8,
  43, 8, 8, 8, 8, 25, 25, 8, 43, 43, 25, 8, 25, 25, 25, 8, 43, 8, 25, 25, 43, 25, 25, 8,
  43, 25, 43, 8, 8, 43, 25, 8, 43, 8, 8, 8, 25, 43, 25, 8, 43, 8, 8, 43, 25, 43, 25, 8,
  43, 43, 8, 8, 8, 8, 43, 8, 43, 8, 25, 8, 8, 25, 43, 8, 43, 25, 8, 25, 8, 43, 43, 8,
  43, 8, 25, 8, 8, 8, 8, 25, 43, 8, 8, 25, 8, 8, 8, 25, 43, 8, 25, 43, 8, 8, 8, 25, 43,
  8, 8, 8, 25, 8, 8, 25, 43, 25, 8, 43, 43, 8, 8, 25, 43, 43, 25, 25, 8, 25, 8, 25, 43,
  8, 8, 8, 43, 25, 8, 25, 43, 25, 25, 8, 25, 43, 8, 25, 43, 8, 8, 8, 8, 8, 25, 25, 43,
  43, 8, 43, 8, 8, 25, 25, 43, 8, 25, 8, 25, 8, 25, 25, 43, 25, 8, 25, 25, 25, 25, 25,
  43, 25, 8, 8, 43, 8, 43, 25, 43, 8, 8, 43, 8, 25, 43, 25, 43, 43, 8, 8, 8, 8, 8, 43,
  43, 8, 8, 25, 25, 8, 8, 43, 43, 25, 25, 8, 43, 8, 8, 43, 43, 25, 43, 8, 8, 25, 8, 43,
  43, 8, 8, 8, 8, 43, 8, 43, 43, 8, 43, 25, 8, 8, 25, 43, 43, 8, 8, 25, 25, 8, 43, 43,
  43, 8, 25, 8, 8, 25, 43, 43, 43,
]);

/**
 * `block_iq3_xxs { ggml_half d; uint8_t qs[96]; }` — 98 bytes, 256 elements.
 *
 * The first **grid** codebook here, and structurally unlike IQ4. Where IQ4 looks
 * a 4-bit code up in a 16-entry value table, this reads a *byte* index into a
 * 256-entry grid of packed quadruplets, flips signs from a second table, and
 * takes its scale from four bits shared across 32 weights. Three tables and a
 * bit-packed scale, which is why it has its own golden.
 *
 * The 96 payload bytes are two regions, and the split is not at a power of two:
 * the first `QK_K/4` = 64 bytes are grid indices, and the remaining 32 are eight
 * `uint32`s, one per sub-block of 32 weights. Each of those packs four 7-bit
 * sign codes in its low 28 bits and the scale in its top 4.
 *
 * Chosen first among the undecoded formats by byte mass: IQ3_XXS carries 6.99
 * GiB in the coverage corpus, more than any other scheme the tool could not
 * read.
 */
const decodeIQ3_XXS: DecodeBlock = (view, at, out, base) => {
  const d = half(view, at);
  const qsAt = at + 2;
  // Where the eight scale/sign words begin. `QK_K/4`, not `QK_K/8`: the index
  // region holds one byte per *pair* of output values, and there are 256 of
  // those in eight groups of eight.
  const signsAt = qsAt + QK_K / 4;

  for (let ib32 = 0; ib32 < QK_K / 32; ib32++) {
    const aux32 = view.getUint32(signsAt + 4 * ib32, true);
    /*
     * `db` is a C `float` in ggml, and both multiplies round there. `f32` on
     * each is load-bearing for the same reason as `decodeIQ4_XS`'s `dl`, and
     * here the fixture does catch it: the scale reaches the output through two
     * roundings before it ever meets a grid value.
     *
     * `aux32 >>> 28` rather than `>>`: the top bit of a `uint32` makes the
     * signed shift negative, which would turn the largest scale into the
     * smallest.
     */
    const db = f32(f32(d * (0.5 + (aux32 >>> 28))) * 0.5);
    for (let l = 0; l < 4; l++) {
      const signs = KSIGNS_IQ2XS[(aux32 >>> (7 * l)) & 127] as number;
      // Two grid entries per iteration, four bytes each, giving the eight
      // values this step writes.
      const g1 = (view.getUint8(qsAt + 8 * ib32 + 2 * l) as number) * 4;
      const g2 = (view.getUint8(qsAt + 8 * ib32 + 2 * l + 1) as number) * 4;
      const dst = base + ib32 * 32 + l * 8;
      for (let j = 0; j < 4; j++) {
        const s1 = signs & (KMASK_IQ2XS[j] as number) ? -1 : 1;
        const s2 = signs & (KMASK_IQ2XS[j + 4] as number) ? -1 : 1;
        // No `f32` on the store: the `Float32Array` rounds, and ggml's
        // `db * grid * sign` associates left-to-right into one float result.
        out[dst + j] = f32(db * (IQ3XXS_GRID[g1 + j] as number)) * s1;
        out[dst + j + 4] = f32(db * (IQ3XXS_GRID[g2 + j] as number)) * s2;
      }
    }
  }
};

/**
 * `iq2s_grid` — 1024 entries of eight bytes, extracted by
 * `scripts/gen-ggml-tables.c`.
 *
 * The same three magnitudes as `IQ2XXS_GRID` — 8, 25, 43 — over four times as
 * many patterns. IQ2_S is not a different alphabet from IQ2_XXS but a longer
 * index into the same one, which is where its extra half-bit goes.
 */
const IQ2S_GRID = new Uint8Array([
  8, 8, 8, 8, 8, 8, 8, 8, 43, 8, 8, 8, 8, 8, 8, 8, 25, 25, 8, 8, 8, 8, 8, 8, 8, 43, 8,
  8, 8, 8, 8, 8, 43, 43, 8, 8, 8, 8, 8, 8, 25, 8, 25, 8, 8, 8, 8, 8, 8, 25, 25, 8, 8, 8,
  8, 8, 43, 25, 25, 8, 8, 8, 8, 8, 25, 43, 25, 8, 8, 8, 8, 8, 8, 8, 43, 8, 8, 8, 8, 8,
  43, 8, 43, 8, 8, 8, 8, 8, 25, 25, 43, 8, 8, 8, 8, 8, 8, 43, 43, 8, 8, 8, 8, 8, 25, 8,
  8, 25, 8, 8, 8, 8, 8, 25, 8, 25, 8, 8, 8, 8, 43, 25, 8, 25, 8, 8, 8, 8, 25, 43, 8, 25,
  8, 8, 8, 8, 8, 8, 25, 25, 8, 8, 8, 8, 43, 8, 25, 25, 8, 8, 8, 8, 25, 25, 25, 25, 8, 8,
  8, 8, 8, 43, 25, 25, 8, 8, 8, 8, 25, 8, 43, 25, 8, 8, 8, 8, 8, 25, 43, 25, 8, 8, 8, 8,
  43, 25, 43, 25, 8, 8, 8, 8, 25, 43, 43, 25, 8, 8, 8, 8, 8, 8, 8, 43, 8, 8, 8, 8, 43,
  8, 8, 43, 8, 8, 8, 8, 25, 25, 8, 43, 8, 8, 8, 8, 8, 43, 8, 43, 8, 8, 8, 8, 25, 8, 25,
  43, 8, 8, 8, 8, 8, 25, 25, 43, 8, 8, 8, 8, 8, 8, 43, 43, 8, 8, 8, 8, 25, 25, 43, 43,
  8, 8, 8, 8, 43, 43, 43, 43, 8, 8, 8, 8, 25, 8, 8, 8, 25, 8, 8, 8, 8, 25, 8, 8, 25, 8,
  8, 8, 43, 25, 8, 8, 25, 8, 8, 8, 25, 43, 8, 8, 25, 8, 8, 8, 8, 8, 25, 8, 25, 8, 8, 8,
  43, 8, 25, 8, 25, 8, 8, 8, 25, 25, 25, 8, 25, 8, 8, 8, 8, 43, 25, 8, 25, 8, 8, 8, 25,
  8, 43, 8, 25, 8, 8, 8, 8, 25, 43, 8, 25, 8, 8, 8, 8, 8, 8, 25, 25, 8, 8, 8, 43, 8, 8,
  25, 25, 8, 8, 8, 25, 25, 8, 25, 25, 8, 8, 8, 8, 43, 8, 25, 25, 8, 8, 8, 25, 8, 25, 25,
  25, 8, 8, 8, 8, 25, 25, 25, 25, 8, 8, 8, 43, 25, 25, 25, 25, 8, 8, 8, 25, 43, 25, 25,
  25, 8, 8, 8, 8, 8, 43, 25, 25, 8, 8, 8, 25, 25, 43, 25, 25, 8, 8, 8, 8, 43, 43, 25,
  25, 8, 8, 8, 25, 8, 8, 43, 25, 8, 8, 8, 8, 25, 8, 43, 25, 8, 8, 8, 8, 8, 25, 43, 25,
  8, 8, 8, 43, 8, 25, 43, 25, 8, 8, 8, 25, 25, 25, 43, 25, 8, 8, 8, 25, 8, 43, 43, 25,
  8, 8, 8, 8, 25, 43, 43, 25, 8, 8, 8, 8, 8, 8, 8, 43, 8, 8, 8, 43, 8, 8, 8, 43, 8, 8,
  8, 25, 25, 8, 8, 43, 8, 8, 8, 8, 43, 8, 8, 43, 8, 8, 8, 25, 8, 25, 8, 43, 8, 8, 8, 8,
  25, 25, 8, 43, 8, 8, 8, 8, 8, 43, 8, 43, 8, 8, 8, 43, 43, 43, 8, 43, 8, 8, 8, 25, 8,
  8, 25, 43, 8, 8, 8, 8, 25, 8, 25, 43, 8, 8, 8, 43, 25, 8, 25, 43, 8, 8, 8, 25, 43, 8,
  25, 43, 8, 8, 8, 8, 8, 25, 25, 43, 8, 8, 8, 25, 25, 25, 25, 43, 8, 8, 8, 8, 8, 8, 43,
  43, 8, 8, 8, 25, 25, 8, 43, 43, 8, 8, 8, 43, 43, 8, 43, 43, 8, 8, 8, 8, 25, 25, 43,
  43, 8, 8, 8, 43, 8, 43, 43, 43, 8, 8, 8, 25, 8, 8, 8, 8, 25, 8, 8, 8, 25, 8, 8, 8, 25,
  8, 8, 43, 25, 8, 8, 8, 25, 8, 8, 25, 43, 8, 8, 8, 25, 8, 8, 8, 8, 25, 8, 8, 25, 8, 8,
  43, 8, 25, 8, 8, 25, 8, 8, 25, 25, 25, 8, 8, 25, 8, 8, 8, 43, 25, 8, 8, 25, 8, 8, 25,
  8, 43, 8, 8, 25, 8, 8, 8, 25, 43, 8, 8, 25, 8, 8, 43, 25, 43, 8, 8, 25, 8, 8, 25, 43,
  43, 8, 8, 25, 8, 8, 8, 8, 8, 25, 8, 25, 8, 8, 43, 8, 8, 25, 8, 25, 8, 8, 25, 25, 8,
  25, 8, 25, 8, 8, 8, 43, 8, 25, 8, 25, 8, 8, 43, 43, 8, 25, 8, 25, 8, 8, 25, 8, 25, 25,
  8, 25, 8, 8, 8, 25, 25, 25, 8, 25, 8, 8, 43, 25, 25, 25, 8, 25, 8, 8, 25, 43, 25, 25,
  8, 25, 8, 8, 8, 8, 43, 25, 8, 25, 8, 8, 43, 8, 43, 25, 8, 25, 8, 8, 25, 25, 43, 25, 8,
  25, 8, 8, 25, 8, 8, 43, 8, 25, 8, 8, 8, 25, 8, 43, 8, 25, 8, 8, 43, 25, 8, 43, 8, 25,
  8, 8, 25, 43, 8, 43, 8, 25, 8, 8, 8, 8, 25, 43, 8, 25, 8, 8, 25, 25, 25, 43, 8, 25, 8,
  8, 8, 43, 25, 43, 8, 25, 8, 8, 25, 8, 43, 43, 8, 25, 8, 8, 8, 25, 43, 43, 8, 25, 8, 8,
  8, 8, 8, 8, 25, 25, 8, 8, 43, 8, 8, 8, 25, 25, 8, 8, 25, 25, 8, 8, 25, 25, 8, 8, 8,
  43, 8, 8, 25, 25, 8, 8, 43, 43, 8, 8, 25, 25, 8, 8, 25, 8, 25, 8, 25, 25, 8, 8, 8, 25,
  25, 8, 25, 25, 8, 8, 43, 25, 25, 8, 25, 25, 8, 8, 25, 43, 25, 8, 25, 25, 8, 8, 8, 8,
  43, 8, 25, 25, 8, 8, 25, 25, 43, 8, 25, 25, 8, 8, 8, 43, 43, 8, 25, 25, 8, 8, 25, 8,
  8, 25, 25, 25, 8, 8, 8, 25, 8, 25, 25, 25, 8, 8, 43, 25, 8, 25, 25, 25, 8, 8, 25, 43,
  8, 25, 25, 25, 8, 8, 8, 8, 25, 25, 25, 25, 8, 8, 43, 8, 25, 25, 25, 25, 8, 8, 25, 25,
  25, 25, 25, 25, 8, 8, 8, 43, 25, 25, 25, 25, 8, 8, 25, 8, 43, 25, 25, 25, 8, 8, 8, 25,
  43, 25, 25, 25, 8, 8, 8, 8, 8, 43, 25, 25, 8, 8, 43, 8, 8, 43, 25, 25, 8, 8, 25, 25,
  8, 43, 25, 25, 8, 8, 8, 43, 8, 43, 25, 25, 8, 8, 25, 8, 25, 43, 25, 25, 8, 8, 8, 25,
  25, 43, 25, 25, 8, 8, 8, 8, 43, 43, 25, 25, 8, 8, 25, 8, 8, 8, 43, 25, 8, 8, 8, 25, 8,
  8, 43, 25, 8, 8, 43, 25, 8, 8, 43, 25, 8, 8, 25, 43, 8, 8, 43, 25, 8, 8, 8, 8, 25, 8,
  43, 25, 8, 8, 25, 25, 25, 8, 43, 25, 8, 8, 8, 8, 8, 25, 43, 25, 8, 8, 25, 25, 8, 25,
  43, 25, 8, 8, 8, 43, 8, 25, 43, 25, 8, 8, 25, 8, 25, 25, 43, 25, 8, 8, 8, 25, 25, 25,
  43, 25, 8, 8, 8, 8, 43, 25, 43, 25, 8, 8, 25, 8, 8, 43, 43, 25, 8, 8, 8, 25, 8, 43,
  43, 25, 8, 8, 8, 8, 25, 43, 43, 25, 8, 8, 8, 8, 8, 8, 8, 43, 8, 8, 43, 8, 8, 8, 8, 43,
  8, 8, 25, 25, 8, 8, 8, 43, 8, 8, 8, 43, 8, 8, 8, 43, 8, 8, 25, 8, 25, 8, 8, 43, 8, 8,
  8, 25, 25, 8, 8, 43, 8, 8, 43, 25, 25, 8, 8, 43, 8, 8, 25, 43, 25, 8, 8, 43, 8, 8, 8,
  8, 43, 8, 8, 43, 8, 8, 25, 25, 43, 8, 8, 43, 8, 8, 43, 43, 43, 8, 8, 43, 8, 8, 25, 8,
  8, 25, 8, 43, 8, 8, 8, 25, 8, 25, 8, 43, 8, 8, 43, 25, 8, 25, 8, 43, 8, 8, 25, 43, 8,
  25, 8, 43, 8, 8, 8, 8, 25, 25, 8, 43, 8, 8, 43, 8, 25, 25, 8, 43, 8, 8, 25, 25, 25,
  25, 8, 43, 8, 8, 8, 43, 25, 25, 8, 43, 8, 8, 25, 8, 43, 25, 8, 43, 8, 8, 8, 25, 43,
  25, 8, 43, 8, 8, 8, 8, 8, 43, 8, 43, 8, 8, 25, 25, 8, 43, 8, 43, 8, 8, 8, 25, 25, 43,
  8, 43, 8, 8, 43, 43, 43, 43, 8, 43, 8, 8, 25, 8, 8, 8, 25, 43, 8, 8, 8, 25, 8, 8, 25,
  43, 8, 8, 8, 8, 25, 8, 25, 43, 8, 8, 43, 8, 25, 8, 25, 43, 8, 8, 25, 25, 25, 8, 25,
  43, 8, 8, 8, 43, 25, 8, 25, 43, 8, 8, 25, 8, 43, 8, 25, 43, 8, 8, 8, 8, 8, 25, 25, 43,
  8, 8, 25, 25, 8, 25, 25, 43, 8, 8, 8, 43, 8, 25, 25, 43, 8, 8, 25, 8, 25, 25, 25, 43,
  8, 8, 8, 25, 25, 25, 25, 43, 8, 8, 8, 8, 43, 25, 25, 43, 8, 8, 25, 8, 8, 43, 25, 43,
  8, 8, 8, 8, 25, 43, 25, 43, 8, 8, 8, 8, 8, 8, 43, 43, 8, 8, 25, 8, 25, 8, 43, 43, 8,
  8, 8, 25, 25, 8, 43, 43, 8, 8, 43, 8, 43, 8, 43, 43, 8, 8, 8, 43, 43, 8, 43, 43, 8, 8,
  43, 43, 43, 8, 43, 43, 8, 8, 8, 8, 25, 25, 43, 43, 8, 8, 25, 43, 25, 43, 43, 43, 8, 8,
  25, 8, 8, 8, 8, 8, 25, 8, 8, 25, 8, 8, 8, 8, 25, 8, 43, 25, 8, 8, 8, 8, 25, 8, 25, 43,
  8, 8, 8, 8, 25, 8, 8, 8, 25, 8, 8, 8, 25, 8, 43, 8, 25, 8, 8, 8, 25, 8, 25, 25, 25, 8,
  8, 8, 25, 8, 8, 43, 25, 8, 8, 8, 25, 8, 25, 8, 43, 8, 8, 8, 25, 8, 8, 25, 43, 8, 8, 8,
  25, 8, 43, 25, 43, 8, 8, 8, 25, 8, 8, 8, 8, 25, 8, 8, 25, 8, 43, 8, 8, 25, 8, 8, 25,
  8, 25, 25, 8, 25, 8, 8, 25, 8, 8, 43, 8, 25, 8, 8, 25, 8, 25, 8, 25, 25, 8, 8, 25, 8,
  8, 25, 25, 25, 8, 8, 25, 8, 43, 25, 25, 25, 8, 8, 25, 8, 25, 43, 25, 25, 8, 8, 25, 8,
  8, 8, 43, 25, 8, 8, 25, 8, 43, 8, 43, 25, 8, 8, 25, 8, 25, 25, 43, 25, 8, 8, 25, 8, 8,
  43, 43, 25, 8, 8, 25, 8, 25, 8, 8, 43, 8, 8, 25, 8, 8, 25, 8, 43, 8, 8, 25, 8, 43, 25,
  8, 43, 8, 8, 25, 8, 8, 8, 25, 43, 8, 8, 25, 8, 25, 25, 25, 43, 8, 8, 25, 8, 8, 43, 25,
  43, 8, 8, 25, 8, 25, 8, 43, 43, 8, 8, 25, 8, 8, 25, 43, 43, 8, 8, 25, 8, 8, 8, 8, 8,
  25, 8, 25, 8, 43, 8, 8, 8, 25, 8, 25, 8, 25, 25, 8, 8, 25, 8, 25, 8, 8, 43, 8, 8, 25,
  8, 25, 8, 43, 43, 8, 8, 25, 8, 25, 8, 25, 8, 25, 8, 25, 8, 25, 8, 8, 25, 25, 8, 25, 8,
  25, 8, 43, 25, 25, 8, 25, 8, 25, 8, 25, 43, 25, 8, 25, 8, 25, 8, 8, 8, 43, 8, 25, 8,
  25, 8, 43, 8, 43, 8, 25, 8, 25, 8, 25, 25, 43, 8, 25, 8, 25, 8, 8, 43, 43, 8, 25, 8,
  25, 8, 25, 8, 8, 25, 25, 8, 25, 8, 8, 25, 8, 25, 25, 8, 25, 8, 43, 25, 8, 25, 25, 8,
  25, 8, 25, 43, 8, 25, 25, 8, 25, 8, 8, 8, 25, 25, 25, 8, 25, 8, 43, 8, 25, 25, 25, 8,
  25, 8, 25, 25, 25, 25, 25, 8, 25, 8, 8, 43, 25, 25, 25, 8, 25, 8, 25, 8, 43, 25, 25,
  8, 25, 8, 8, 25, 43, 25, 25, 8, 25, 8, 8, 8, 8, 43, 25, 8, 25, 8, 43, 8, 8, 43, 25, 8,
  25, 8, 25, 25, 8, 43, 25, 8, 25, 8, 8, 43, 8, 43, 25, 8, 25, 8, 25, 8, 25, 43, 25, 8,
  25, 8, 8, 25, 25, 43, 25, 8, 25, 8, 25, 8, 8, 8, 43, 8, 25, 8, 8, 25, 8, 8, 43, 8, 25,
  8, 25, 43, 8, 8, 43, 8, 25, 8, 8, 8, 25, 8, 43, 8, 25, 8, 25, 25, 25, 8, 43, 8, 25, 8,
  25, 8, 43, 8, 43, 8, 25, 8, 8, 25, 43, 8, 43, 8, 25, 8, 8, 8, 8, 25, 43, 8, 25, 8, 25,
  25, 8, 25, 43, 8, 25, 8, 25, 8, 25, 25, 43, 8, 25, 8, 8, 25, 25, 25, 43, 8, 25, 8, 25,
  8, 8, 43, 43, 8, 25, 8, 8, 25, 8, 43, 43, 8, 25, 8, 8, 8, 25, 43, 43, 8, 25, 8, 8, 8,
  8, 8, 8, 25, 25, 8, 43, 8, 8, 8, 8, 25, 25, 8, 25, 25, 8, 8, 8, 25, 25, 8, 8, 43, 8,
  8, 8, 25, 25, 8, 25, 8, 25, 8, 8, 25, 25, 8, 8, 25, 25, 8, 8, 25, 25, 8, 43, 25, 25,
  8, 8, 25, 25, 8, 25, 43, 25, 8, 8, 25, 25, 8, 8, 8, 43, 8, 8, 25, 25, 8, 25, 25, 43,
  8, 8, 25, 25, 8, 8, 43, 43, 8, 8, 25, 25, 8, 25, 8, 8, 25, 8, 25, 25, 8, 8, 25, 8, 25,
  8, 25, 25, 8, 43, 25, 8, 25, 8, 25, 25, 8, 25, 43, 8, 25, 8, 25, 25, 8, 8, 8, 25, 25,
  8, 25, 25, 8, 43, 8, 25, 25, 8, 25, 25, 8, 25, 25, 25, 25, 8, 25, 25, 8, 8, 43, 25,
  25, 8, 25, 25, 8, 25, 8, 43, 25, 8, 25, 25, 8, 8, 25, 43, 25, 8, 25, 25, 8, 8, 8, 8,
  43, 8, 25, 25, 8, 43, 8, 8, 43, 8, 25, 25, 8, 25, 25, 8, 43, 8, 25, 25, 8, 8, 43, 8,
  43, 8, 25, 25, 8, 25, 8, 25, 43, 8, 25, 25, 8, 8, 25, 25, 43, 8, 25, 25, 8, 8, 8, 43,
  43, 8, 25, 25, 8, 25, 8, 8, 8, 25, 25, 25, 8, 8, 25, 8, 8, 25, 25, 25, 8, 43, 25, 8,
  8, 25, 25, 25, 8, 25, 43, 8, 8, 25, 25, 25, 8, 8, 8, 25, 8, 25, 25, 25, 8, 43, 8, 25,
  8, 25, 25, 25, 8, 25, 25, 25, 8, 25, 25, 25, 8, 8, 43, 25, 8, 25, 25, 25, 8, 25, 8,
  43, 8, 25, 25, 25, 8, 8, 25, 43, 8, 25, 25, 25, 8, 8, 8, 8, 25, 25, 25, 25, 8, 43, 8,
  8, 25, 25, 25, 25, 8, 25, 25, 8, 25, 25, 25, 25, 8, 8, 43, 8, 25, 25, 25, 25, 8, 25,
  8, 25, 25, 25, 25, 25, 8, 8, 25, 25, 25, 25, 25, 25, 8, 8, 8, 43, 25, 25, 25, 25, 8,
  25, 8, 8, 43, 25, 25, 25, 8, 8, 25, 8, 43, 25, 25, 25, 8, 8, 8, 25, 43, 25, 25, 25, 8,
  8, 8, 8, 8, 43, 25, 25, 8, 25, 25, 8, 8, 43, 25, 25, 8, 8, 43, 8, 8, 43, 25, 25, 8,
  25, 8, 25, 8, 43, 25, 25, 8, 8, 25, 25, 8, 43, 25, 25, 8, 8, 8, 43, 8, 43, 25, 25, 8,
  25, 8, 8, 25, 43, 25, 25, 8, 8, 25, 8, 25, 43, 25, 25, 8, 8, 8, 25, 25, 43, 25, 25, 8,
  8, 8, 8, 43, 43, 25, 25, 8, 43, 43, 43, 43, 43, 25, 25, 8, 25, 8, 8, 8, 8, 43, 25, 8,
  8, 25, 8, 8, 8, 43, 25, 8, 43, 25, 8, 8, 8, 43, 25, 8, 25, 43, 8, 8, 8, 43, 25, 8, 8,
  8, 25, 8, 8, 43, 25, 8, 25, 25, 25, 8, 8, 43, 25, 8, 8, 43, 25, 8, 8, 43, 25, 8, 25,
  8, 43, 8, 8, 43, 25, 8, 8, 8, 8, 25, 8, 43, 25, 8, 43, 8, 8, 25, 8, 43, 25, 8, 25, 25,
  8, 25, 8, 43, 25, 8, 8, 43, 8, 25, 8, 43, 25, 8, 25, 8, 25, 25, 8, 43, 25, 8, 8, 25,
  25, 25, 8, 43, 25, 8, 8, 8, 43, 25, 8, 43, 25, 8, 25, 8, 8, 43, 8, 43, 25, 8, 8, 25,
  8, 43, 8, 43, 25, 8, 8, 8, 8, 8, 25, 43, 25, 8, 43, 8, 8, 8, 25, 43, 25, 8, 25, 25, 8,
  8, 25, 43, 25, 8, 8, 43, 8, 8, 25, 43, 25, 8, 25, 8, 25, 8, 25, 43, 25, 8, 8, 25, 25,
  8, 25, 43, 25, 8, 8, 8, 43, 8, 25, 43, 25, 8, 25, 8, 8, 25, 25, 43, 25, 8, 8, 25, 8,
  25, 25, 43, 25, 8, 8, 8, 25, 25, 25, 43, 25, 8, 25, 43, 43, 25, 25, 43, 25, 8, 43, 8,
  43, 43, 25, 43, 25, 8, 8, 25, 8, 8, 43, 43, 25, 8, 8, 8, 25, 8, 43, 43, 25, 8, 8, 8,
  8, 25, 43, 43, 25, 8, 43, 25, 25, 25, 43, 43, 25, 8, 8, 8, 8, 8, 8, 8, 43, 8, 43, 8,
  8, 8, 8, 8, 43, 8, 25, 25, 8, 8, 8, 8, 43, 8, 8, 43, 8, 8, 8, 8, 43, 8, 25, 8, 25, 8,
  8, 8, 43, 8, 8, 25, 25, 8, 8, 8, 43, 8, 43, 25, 25, 8, 8, 8, 43, 8, 25, 43, 25, 8, 8,
  8, 43, 8, 8, 8, 43, 8, 8, 8, 43, 8, 25, 25, 43, 8, 8, 8, 43, 8, 43, 43, 43, 8, 8, 8,
  43, 8, 25, 8, 8, 25, 8, 8, 43, 8, 8, 25, 8, 25, 8, 8, 43, 8, 8, 8, 25, 25, 8, 8, 43,
  8, 43, 8, 25, 25, 8, 8, 43, 8, 25, 25, 25, 25, 8, 8, 43, 8, 8, 25, 43, 25, 8, 8, 43,
  8, 8, 8, 8, 43, 8, 8, 43, 8, 43, 43, 8, 43, 8, 8, 43, 8, 8, 25, 25, 43, 8, 8, 43, 8,
  43, 43, 43, 43, 8, 8, 43, 8, 25, 8, 8, 8, 25, 8, 43, 8, 8, 25, 8, 8, 25, 8, 43, 8, 8,
  8, 25, 8, 25, 8, 43, 8, 43, 8, 25, 8, 25, 8, 43, 8, 25, 25, 25, 8, 25, 8, 43, 8, 25,
  8, 43, 8, 25, 8, 43, 8, 8, 8, 8, 25, 25, 8, 43, 8, 43, 8, 8, 25, 25, 8, 43, 8, 25, 25,
  8, 25, 25, 8, 43, 8, 25, 8, 25, 25, 25, 8, 43, 8, 8, 25, 25, 25, 25, 8, 43, 8, 8, 8,
  43, 25, 25, 8, 43, 8, 25, 8, 8, 43, 25, 8, 43, 8, 8, 25, 8, 43, 25, 8, 43, 8, 8, 8,
  25, 43, 25, 8, 43, 8, 8, 8, 8, 8, 43, 8, 43, 8, 43, 43, 8, 8, 43, 8, 43, 8, 43, 8, 43,
  8, 43, 8, 43, 8, 8, 43, 43, 8, 43, 8, 43, 8, 43, 43, 43, 8, 43, 8, 43, 8, 8, 25, 8,
  25, 43, 8, 43, 8, 8, 8, 25, 25, 43, 8, 43, 8, 8, 43, 8, 43, 43, 8, 43, 8, 43, 43, 8,
  43, 43, 8, 43, 8, 8, 43, 43, 43, 43, 8, 43, 8, 25, 8, 8, 8, 8, 25, 43, 8, 8, 25, 8, 8,
  8, 25, 43, 8, 43, 25, 8, 8, 8, 25, 43, 8, 25, 43, 8, 8, 8, 25, 43, 8, 8, 8, 25, 8, 8,
  25, 43, 8, 25, 25, 25, 8, 8, 25, 43, 8, 8, 43, 25, 8, 8, 25, 43, 8, 25, 8, 43, 8, 8,
  25, 43, 8, 8, 25, 43, 8, 8, 25, 43, 8, 8, 8, 8, 25, 8, 25, 43, 8, 43, 8, 8, 25, 8, 25,
  43, 8, 25, 25, 8, 25, 8, 25, 43, 8, 8, 43, 8, 25, 8, 25, 43, 8, 25, 8, 25, 25, 8, 25,
  43, 8, 8, 25, 25, 25, 8, 25, 43, 8, 8, 8, 43, 25, 8, 25, 43, 8, 25, 8, 8, 43, 8, 25,
  43, 8, 8, 25, 8, 43, 8, 25, 43, 8, 8, 8, 25, 43, 8, 25, 43, 8, 8, 8, 8, 8, 25, 25, 43,
  8, 25, 25, 8, 8, 25, 25, 43, 8, 8, 43, 8, 8, 25, 25, 43, 8, 25, 8, 25, 8, 25, 25, 43,
  8, 8, 25, 25, 8, 25, 25, 43, 8, 8, 8, 43, 8, 25, 25, 43, 8, 25, 8, 8, 25, 25, 25, 43,
  8, 8, 25, 8, 25, 25, 25, 43, 8, 8, 8, 25, 25, 25, 25, 43, 8, 43, 25, 43, 25, 25, 25,
  43, 8, 8, 8, 8, 43, 25, 25, 43, 8, 25, 8, 8, 8, 43, 25, 43, 8, 8, 25, 8, 8, 43, 25,
  43, 8, 8, 8, 25, 8, 43, 25, 43, 8, 8, 8, 8, 25, 43, 25, 43, 8, 25, 43, 25, 25, 43, 25,
  43, 8, 8, 8, 8, 8, 8, 43, 43, 8, 25, 25, 8, 8, 8, 43, 43, 8, 25, 8, 25, 8, 8, 43, 43,
  8, 8, 25, 25, 8, 8, 43, 43, 8, 25, 8, 8, 25, 8, 43, 43, 8, 8, 25, 8, 25, 8, 43, 43, 8,
  8, 8, 25, 25, 8, 43, 43, 8, 43, 43, 8, 43, 8, 43, 43, 8, 43, 43, 43, 43, 8, 43, 43, 8,
  25, 8, 8, 8, 25, 43, 43, 8, 8, 25, 8, 8, 25, 43, 43, 8, 8, 8, 25, 8, 25, 43, 43, 8,
  25, 25, 25, 43, 25, 43, 43, 8, 43, 43, 8, 8, 43, 43, 43, 8, 43, 8, 43, 8, 43, 43, 43,
  8, 8, 25, 43, 25, 43, 43, 43, 8, 8, 43, 8, 43, 43, 43, 43, 8, 43, 43, 8, 43, 43, 43,
  43, 8, 25, 8, 8, 8, 8, 8, 8, 25, 8, 25, 8, 8, 8, 8, 8, 25, 43, 25, 8, 8, 8, 8, 8, 25,
  25, 43, 8, 8, 8, 8, 8, 25, 8, 8, 25, 8, 8, 8, 8, 25, 43, 8, 25, 8, 8, 8, 8, 25, 25,
  25, 25, 8, 8, 8, 8, 25, 8, 43, 25, 8, 8, 8, 8, 25, 43, 43, 25, 8, 8, 8, 8, 25, 25, 8,
  43, 8, 8, 8, 8, 25, 8, 25, 43, 8, 8, 8, 8, 25, 43, 25, 43, 8, 8, 8, 8, 25, 8, 8, 8,
  25, 8, 8, 8, 25, 43, 8, 8, 25, 8, 8, 8, 25, 25, 25, 8, 25, 8, 8, 8, 25, 8, 43, 8, 25,
  8, 8, 8, 25, 43, 43, 8, 25, 8, 8, 8, 25, 25, 8, 25, 25, 8, 8, 8, 25, 8, 25, 25, 25, 8,
  8, 8, 25, 43, 25, 25, 25, 8, 8, 8, 25, 25, 43, 25, 25, 8, 8, 8, 25, 8, 8, 43, 25, 8,
  8, 8, 25, 43, 8, 43, 25, 8, 8, 8, 25, 25, 25, 43, 25, 8, 8, 8, 25, 25, 8, 8, 43, 8, 8,
  8, 25, 8, 25, 8, 43, 8, 8, 8, 25, 8, 8, 25, 43, 8, 8, 8, 25, 25, 25, 25, 43, 8, 8, 8,
  25, 8, 43, 25, 43, 8, 8, 8, 25, 25, 8, 43, 43, 8, 8, 8, 25, 8, 25, 43, 43, 8, 8, 8,
  25, 8, 8, 8, 8, 25, 8, 8, 25, 43, 8, 8, 8, 25, 8, 8, 25, 25, 25, 8, 8, 25, 8, 8, 25,
  8, 43, 8, 8, 25, 8, 8, 25, 25, 8, 25, 8, 25, 8, 8, 25, 8, 25, 25, 8, 25, 8, 8, 25, 43,
  25, 25, 8, 25, 8, 8, 25, 25, 43, 25, 8, 25, 8, 8, 25, 8, 8, 43, 8, 25, 8, 8, 25, 43,
  8, 43, 8, 25, 8, 8, 25, 25, 25, 43, 8, 25, 8, 8, 25, 25, 8, 8, 25, 25, 8, 8, 25, 8,
  25, 8, 25, 25, 8, 8, 25, 43, 25, 8, 25, 25, 8, 8, 25, 25, 43, 8, 25, 25, 8, 8, 25, 8,
  8, 25, 25, 25, 8, 8, 25, 43, 8, 25, 25, 25, 8, 8, 25, 25, 25, 25, 25, 25, 8, 8, 25, 8,
  43, 25, 25, 25, 8, 8, 25, 25, 8, 43, 25, 25, 8, 8, 25, 8, 25, 43, 25, 25, 8, 8, 25, 8,
  8, 8, 43, 25, 8, 8, 25, 43, 8, 8, 43, 25, 8, 8, 25, 25, 25, 8, 43, 25, 8, 8, 25, 8,
  43, 8, 43, 25, 8, 8, 25, 25, 8, 25, 43, 25, 8, 8, 25, 8, 25, 25, 43, 25, 8, 8, 25, 8,
  8, 43, 43, 25, 8, 8, 25, 25, 8, 8, 8, 43, 8, 8, 25, 8, 25, 8, 8, 43, 8, 8, 25, 8, 8,
  25, 8, 43, 8, 8, 25, 43, 8, 25, 8, 43, 8, 8, 25, 25, 25, 25, 8, 43, 8, 8, 25, 8, 43,
  25, 8, 43, 8, 8, 25, 8, 25, 43, 8, 43, 8, 8, 25, 8, 8, 8, 25, 43, 8, 8, 25, 25, 25, 8,
  25, 43, 8, 8, 25, 8, 43, 8, 25, 43, 8, 8, 25, 25, 8, 25, 25, 43, 8, 8, 25, 8, 25, 25,
  25, 43, 8, 8, 25, 8, 8, 43, 25, 43, 8, 8, 25, 25, 8, 8, 43, 43, 8, 8, 25, 8, 25, 8,
  43, 43, 8, 8, 25, 8, 8, 8, 8, 8, 25, 8, 25, 43, 8, 8, 8, 8, 25, 8, 25, 25, 25, 8, 8,
  8, 25, 8, 25, 8, 43, 8, 8, 8, 25, 8, 25, 43, 43, 8, 8, 8, 25, 8, 25, 25, 8, 25, 8, 8,
  25, 8, 25, 8, 25, 25, 8, 8, 25, 8, 25, 43, 25, 25, 8, 8, 25, 8, 25, 25, 43, 25, 8, 8,
  25, 8, 25, 8, 8, 43, 8, 8, 25, 8, 25, 43, 8, 43, 8, 8, 25, 8, 25, 25, 25, 43, 8, 8,
  25, 8, 25, 8, 43, 43, 8, 8, 25, 8, 25, 25, 8, 8, 25, 8, 25, 8, 25, 8, 25, 8, 25, 8,
  25, 8, 25, 43, 25, 8, 25, 8, 25, 8, 25, 25, 43, 8, 25, 8, 25, 8, 25, 8, 8, 25, 25, 8,
  25, 8, 25, 43, 8, 25, 25, 8, 25, 8, 25, 25, 25, 25, 25, 8, 25, 8, 25, 8, 43, 25, 25,
  8, 25, 8, 25, 25, 8, 43, 25, 8, 25, 8, 25, 8, 25, 43, 25, 8, 25, 8, 25, 8, 8, 8, 43,
  8, 25, 8, 25, 43, 8, 8, 43, 8, 25, 8, 25, 25, 25, 8, 43, 8, 25, 8, 25, 8, 43, 8, 43,
  8, 25, 8, 25, 25, 8, 25, 43, 8, 25, 8, 25, 8, 25, 25, 43, 8, 25, 8, 25, 8, 8, 43, 43,
  8, 25, 8, 25, 25, 8, 8, 8, 25, 25, 8, 25, 8, 25, 8, 8, 25, 25, 8, 25, 43, 25, 8, 8,
  25, 25, 8, 25, 25, 43, 8, 8, 25, 25, 8, 25, 8, 8, 25, 8, 25, 25, 8, 25, 43, 8, 25, 8,
  25, 25, 8, 25, 25, 25, 25, 8, 25, 25, 8, 25, 8, 43, 25, 8, 25, 25, 8, 25, 25, 8, 43,
  8, 25, 25, 8, 25, 8, 25, 43, 8, 25, 25, 8, 25, 8, 8, 8, 25, 25, 25, 8, 25, 43, 8, 8,
  25, 25, 25, 8, 25, 25, 25, 8, 25, 25, 25, 8, 25, 8, 43, 8, 25, 25, 25, 8, 25, 25, 8,
  25, 25, 25, 25, 8, 25, 8, 25, 25, 25, 25, 25, 8, 25, 8, 8, 43, 25, 25, 25, 8, 25, 43,
  43, 43, 25, 25, 25, 8, 25, 25, 8, 8, 43, 25, 25, 8, 25, 8, 25, 8, 43, 25, 25, 8, 25,
  8, 8, 25, 43, 25, 25, 8, 25, 8, 8, 8, 8, 43, 25, 8, 25, 43, 8, 8, 8, 43, 25, 8, 25,
  25, 25, 8, 8, 43, 25, 8, 25, 8, 43, 8, 8, 43, 25, 8, 25, 25, 8, 25, 8, 43, 25, 8, 25,
  8, 25, 25, 8, 43, 25, 8, 25, 8, 8, 43, 8, 43, 25, 8, 25, 25, 8, 8, 25, 43, 25, 8, 25,
  8, 25, 8, 25, 43, 25, 8, 25, 8, 8, 25, 25, 43, 25, 8, 25, 8, 8, 8, 43, 43, 25, 8, 25,
  25, 25, 43, 43, 43, 25, 8, 25, 25, 8, 8, 8, 8, 43, 8, 25, 8, 25, 8, 8, 8, 43, 8, 25,
  25, 43, 8, 8, 8, 43, 8, 25, 8, 8, 25, 8, 8, 43, 8, 25, 43, 8, 25, 8, 8, 43, 8, 25, 25,
  25, 25, 8, 8, 43, 8, 25, 8, 43, 25, 8, 8, 43, 8, 25, 25, 8, 43, 8, 8, 43, 8, 25, 8,
  25, 43, 8, 8, 43, 8, 25, 8, 8, 8, 25, 8, 43, 8, 25, 43, 8, 8, 25, 8, 43, 8, 25, 25,
  25, 8, 25, 8, 43, 8, 25, 8, 43, 8, 25, 8, 43, 8, 25, 25, 8, 25, 25, 8, 43, 8, 25, 8,
  25, 25, 25, 8, 43, 8, 25, 8, 8, 43, 25, 8, 43, 8, 25, 8, 25, 8, 43, 8, 43, 8, 25, 8,
  8, 25, 43, 8, 43, 8, 25, 8, 8, 8, 8, 25, 43, 8, 25, 43, 8, 8, 8, 25, 43, 8, 25, 25,
  25, 8, 8, 25, 43, 8, 25, 8, 43, 8, 8, 25, 43, 8, 25, 25, 8, 25, 8, 25, 43, 8, 25, 8,
  25, 25, 8, 25, 43, 8, 25, 8, 8, 43, 8, 25, 43, 8, 25, 25, 8, 8, 25, 25, 43, 8, 25, 8,
  25, 8, 25, 25, 43, 8, 25, 8, 8, 25, 25, 25, 43, 8, 25, 8, 8, 8, 43, 25, 43, 8, 25, 43,
  25, 25, 43, 25, 43, 8, 25, 25, 8, 8, 8, 43, 43, 8, 25, 8, 25, 8, 8, 43, 43, 8, 25, 8,
  8, 25, 8, 43, 43, 8, 25, 8, 8, 8, 25, 43, 43, 8, 25, 8, 8, 8, 8, 8, 8, 25, 25, 43, 8,
  8, 8, 8, 8, 25, 25, 25, 25, 8, 8, 8, 8, 25, 25, 8, 43, 8, 8, 8, 8, 25, 25, 25, 8, 25,
  8, 8, 8, 25, 25, 8, 25, 25, 8, 8, 8, 25, 25, 43, 25, 25, 8, 8, 8, 25, 25, 25, 43, 25,
  8, 8, 8, 25, 25, 8, 8, 43, 8, 8, 8, 25, 25, 43, 8, 43, 8, 8, 8, 25, 25, 25, 25, 43, 8,
  8, 8, 25, 25, 8, 43, 43, 8, 8, 8, 25, 25, 25, 8, 8, 25, 8, 8, 25, 25, 8, 25, 8, 25, 8,
  8, 25, 25, 43, 25, 8, 25, 8, 8, 25, 25, 25, 43, 8, 25, 8, 8, 25, 25, 8, 8, 25, 25, 8,
  8, 25, 25, 43, 8, 25, 25, 8, 8, 25, 25, 25, 25, 25, 25, 8, 8, 25, 25, 8, 43, 25, 25,
  8, 8, 25, 25, 25, 8, 43, 25, 8, 8, 25, 25, 8, 25, 43, 25, 8, 8, 25, 25, 8, 8, 8, 43,
  8, 8, 25, 25, 43, 8, 8, 43, 8, 8, 25, 25, 25, 25, 8, 43, 8, 8, 25, 25, 8, 43, 8, 43,
  8, 8, 25, 25, 25, 8, 25, 43, 8, 8, 25, 25, 8, 25, 25, 43, 8, 8, 25, 25, 25, 8, 8, 8,
  25, 8, 25, 25, 8, 25, 8, 8, 25, 8, 25, 25, 43, 25, 8, 8, 25, 8, 25, 25, 25, 43, 8, 8,
  25, 8, 25, 25, 8, 8, 25, 8, 25, 8, 25, 25, 43, 8, 25, 8, 25, 8, 25, 25, 25, 25, 25, 8,
  25, 8, 25, 25, 8, 43, 25, 8, 25, 8, 25, 25, 25, 8, 43, 8, 25, 8, 25, 25, 8, 25, 43, 8,
  25, 8, 25, 25, 8, 8, 8, 25, 25, 8, 25, 25, 43, 8, 8, 25, 25, 8, 25, 25, 25, 25, 8, 25,
  25, 8, 25, 25, 8, 43, 8, 25, 25, 8, 25, 25, 25, 8, 25, 25, 25, 8, 25, 25, 8, 25, 25,
  25, 25, 8, 25, 25, 8, 8, 43, 25, 25, 8, 25, 25, 25, 8, 8, 43, 25, 8, 25, 25, 8, 25, 8,
  43, 25, 8, 25, 25, 8, 8, 25, 43, 25, 8, 25, 25, 8, 8, 8, 8, 43, 8, 25, 25, 25, 25, 8,
  8, 43, 8, 25, 25, 8, 43, 8, 8, 43, 8, 25, 25, 25, 8, 25, 8, 43, 8, 25, 25, 8, 25, 25,
  8, 43, 8, 25, 25, 8, 8, 43, 8, 43, 8, 25, 25, 25, 8, 8, 25, 43, 8, 25, 25, 8, 25, 8,
  25, 43, 8, 25, 25, 8, 8, 25, 25, 43, 8, 25, 25, 25, 43, 43, 25, 43, 8, 25, 25, 8, 8,
  8, 43, 43, 8, 25, 25, 25, 8, 8, 8, 8, 25, 25, 25, 8, 25, 8, 8, 8, 25, 25, 25, 43, 25,
  8, 8, 8, 25, 25, 25, 25, 43, 8, 8, 8, 25, 25, 25, 8, 8, 25, 8, 8, 25, 25, 25, 43, 8,
  25, 8, 8, 25, 25, 25, 25, 25, 25, 8, 8, 25, 25, 25, 8, 43, 25, 8, 8, 25, 25, 25, 25,
  8, 43, 8, 8, 25, 25, 25, 8, 25, 43, 8, 8, 25, 25, 25, 8, 8, 8, 25, 8, 25, 25, 25, 43,
  8, 8, 25, 8, 25, 25, 25, 25, 25, 8, 25, 8, 25, 25, 25, 8, 43, 8, 25, 8, 25, 25, 25,
  25, 8, 25, 25, 8, 25, 25, 25, 8, 25, 25, 25, 8, 25, 25, 25, 8, 8, 43, 25, 8, 25, 25,
  25, 25, 8, 8, 43, 8, 25, 25, 25, 8, 25, 8, 43, 8, 25, 25, 25, 8, 8, 25, 43, 8, 25, 25,
  25, 8, 8, 8, 8, 25, 25, 25, 25, 43, 8, 8, 8, 25, 25, 25, 25, 25, 25, 8, 8, 25, 25, 25,
  25, 8, 43, 8, 8, 25, 25, 25, 25, 25, 8, 25, 8, 25, 25, 25, 25, 8, 25, 25, 8, 25, 25,
  25, 25, 8, 8, 43, 8, 25, 25, 25, 25, 25, 8, 8, 25, 25, 25, 25, 25, 8, 25, 8, 25, 25,
  25, 25, 25, 8, 8, 25, 25, 25, 25, 25, 25, 8, 8, 8, 43, 25, 25, 25, 25, 25, 8, 8, 8,
  43, 25, 25, 25, 8, 25, 8, 8, 43, 25, 25, 25, 8, 8, 25, 8, 43, 25, 25, 25, 43, 25, 43,
  8, 43, 25, 25, 25, 8, 8, 8, 25, 43, 25, 25, 25, 8, 8, 8, 8, 8, 43, 25, 25, 43, 8, 8,
  8, 8, 43, 25, 25, 25, 25, 8, 8, 8, 43, 25, 25, 8, 43, 8, 8, 8, 43, 25, 25, 25, 8, 25,
  8, 8, 43, 25, 25, 8, 25, 25, 8, 8, 43, 25, 25, 8, 8, 43, 8, 8, 43, 25, 25, 25, 8, 8,
  25, 8, 43, 25, 25, 8, 25, 8, 25, 8, 43, 25, 25, 8, 8, 25, 25, 8, 43, 25, 25, 43, 43,
  25, 25, 8, 43, 25, 25, 8, 8, 8, 43, 8, 43, 25, 25, 25, 8, 8, 8, 25, 43, 25, 25, 8, 25,
  8, 8, 25, 43, 25, 25, 8, 8, 25, 8, 25, 43, 25, 25, 8, 8, 8, 25, 25, 43, 25, 25, 8, 8,
  8, 8, 43, 43, 25, 25, 25, 43, 25, 8, 43, 43, 25, 25, 25, 25, 8, 43, 43, 43, 25, 25, 8,
  43, 43, 43, 43, 43, 25, 25, 25, 8, 8, 8, 8, 8, 43, 25, 8, 25, 8, 8, 8, 8, 43, 25, 43,
  25, 8, 8, 8, 8, 43, 25, 8, 8, 25, 8, 8, 8, 43, 25, 43, 8, 25, 8, 8, 8, 43, 25, 25, 25,
  25, 8, 8, 8, 43, 25, 8, 43, 25, 8, 8, 8, 43, 25, 25, 8, 43, 8, 8, 8, 43, 25, 8, 25,
  43, 8, 8, 8, 43, 25, 8, 8, 8, 25, 8, 8, 43, 25, 25, 25, 8, 25, 8, 8, 43, 25, 8, 43, 8,
  25, 8, 8, 43, 25, 25, 8, 25, 25, 8, 8, 43, 25, 8, 25, 25, 25, 8, 8, 43, 25, 8, 8, 43,
  25, 8, 8, 43, 25, 8, 25, 8, 43, 8, 8, 43, 25, 8, 8, 25, 43, 8, 8, 43, 25, 8, 8, 8, 8,
  25, 8, 43, 25, 43, 8, 8, 8, 25, 8, 43, 25, 25, 25, 8, 8, 25, 8, 43, 25, 8, 43, 8, 8,
  25, 8, 43, 25, 25, 8, 25, 8, 25, 8, 43, 25, 8, 25, 25, 8, 25, 8, 43, 25, 8, 8, 43, 8,
  25, 8, 43, 25, 25, 8, 8, 25, 25, 8, 43, 25, 8, 25, 8, 25, 25, 8, 43, 25, 8, 8, 25, 25,
  25, 8, 43, 25, 8, 8, 8, 43, 25, 8, 43, 25, 25, 43, 25, 43, 25, 8, 43, 25, 8, 25, 8, 8,
  43, 8, 43, 25, 8, 8, 25, 8, 43, 8, 43, 25, 8, 8, 8, 25, 43, 8, 43, 25, 43, 25, 25, 25,
  43, 8, 43, 25, 25, 8, 43, 43, 43, 8, 43, 25, 8, 8, 8, 8, 8, 25, 43, 25, 25, 25, 8, 8,
  8, 25, 43, 25, 8, 43, 8, 8, 8, 25, 43, 25, 25, 8, 25, 8, 8, 25, 43, 25, 8, 25, 25, 8,
  8, 25, 43, 25, 8, 8, 43, 8, 8, 25, 43, 25, 25, 8, 8, 25, 8, 25, 43, 25, 8, 25, 8, 25,
  8, 25, 43, 25, 8, 8, 25, 25, 8, 25, 43, 25, 8, 8, 8, 43, 8, 25, 43, 25, 25, 8, 8, 8,
  25, 25, 43, 25, 8, 25, 8, 8, 25, 25, 43, 25, 8, 8, 25, 8, 25, 25, 43, 25, 8, 8, 8, 25,
  25, 25, 43, 25, 43, 43, 8, 25, 25, 25, 43, 25, 8, 43, 43, 25, 25, 25, 43, 25, 43, 8,
  25, 43, 25, 25, 43, 25, 8, 8, 8, 8, 43, 25, 43, 25, 8, 25, 25, 43, 43, 25, 43, 25, 25,
  8, 8, 8, 8, 43, 43, 25, 8, 25, 8, 8, 8, 43, 43, 25, 8, 8, 25, 8, 8, 43, 43, 25, 25,
  25, 43, 25, 8, 43, 43, 25, 8, 43, 25, 43, 8, 43, 43, 25, 8, 8, 8, 8, 25, 43, 43, 25,
  43, 43, 43, 8, 25, 43, 43, 25, 43, 8, 8, 25, 43, 43, 43, 25, 25, 8, 43, 43, 43, 43,
  43, 25, 8, 8, 8, 8, 8, 8, 8, 43, 43, 8, 8, 8, 8, 8, 8, 43, 25, 25, 8, 8, 8, 8, 8, 43,
  8, 43, 8, 8, 8, 8, 8, 43, 25, 8, 25, 8, 8, 8, 8, 43, 8, 25, 25, 8, 8, 8, 8, 43, 25,
  43, 25, 8, 8, 8, 8, 43, 8, 8, 43, 8, 8, 8, 8, 43, 25, 25, 43, 8, 8, 8, 8, 43, 25, 8,
  8, 25, 8, 8, 8, 43, 8, 25, 8, 25, 8, 8, 8, 43, 8, 8, 25, 25, 8, 8, 8, 43, 43, 8, 25,
  25, 8, 8, 8, 43, 25, 25, 25, 25, 8, 8, 8, 43, 8, 43, 25, 25, 8, 8, 8, 43, 25, 8, 43,
  25, 8, 8, 8, 43, 8, 8, 8, 43, 8, 8, 8, 43, 25, 25, 8, 43, 8, 8, 8, 43, 25, 8, 25, 43,
  8, 8, 8, 43, 8, 25, 25, 43, 8, 8, 8, 43, 25, 8, 8, 8, 25, 8, 8, 43, 8, 25, 8, 8, 25,
  8, 8, 43, 25, 43, 8, 8, 25, 8, 8, 43, 8, 8, 25, 8, 25, 8, 8, 43, 43, 8, 25, 8, 25, 8,
  8, 43, 25, 25, 25, 8, 25, 8, 8, 43, 8, 43, 25, 8, 25, 8, 8, 43, 25, 8, 43, 8, 25, 8,
  8, 43, 8, 25, 43, 8, 25, 8, 8, 43, 8, 8, 8, 25, 25, 8, 8, 43, 43, 8, 8, 25, 25, 8, 8,
  43, 25, 25, 8, 25, 25, 8, 8, 43, 8, 43, 8, 25, 25, 8, 8, 43, 25, 8, 25, 25, 25, 8, 8,
  43, 8, 25, 25, 25, 25, 8, 8, 43, 25, 8, 8, 43, 25, 8, 8, 43, 8, 25, 8, 43, 25, 8, 8,
  43, 8, 8, 25, 43, 25, 8, 8, 43, 25, 43, 43, 43, 25, 8, 8, 43, 8, 8, 8, 8, 43, 8, 8,
  43, 25, 25, 8, 8, 43, 8, 8, 43, 43, 43, 8, 8, 43, 8, 8, 43, 25, 8, 25, 8, 43, 8, 8,
  43, 8, 25, 25, 8, 43, 8, 8, 43, 25, 8, 8, 25, 43, 8, 8, 43, 8, 25, 8, 25, 43, 8, 8,
  43, 8, 8, 25, 25, 43, 8, 8, 43, 25, 8, 8, 8, 8, 25, 8, 43, 8, 25, 8, 8, 8, 25, 8, 43,
  43, 25, 8, 8, 8, 25, 8, 43, 25, 43, 8, 8, 8, 25, 8, 43, 8, 8, 25, 8, 8, 25, 8, 43, 43,
  8, 25, 8, 8, 25, 8, 43, 25, 25, 25, 8, 8, 25, 8, 43, 8, 43, 25, 8, 8, 25, 8, 43, 25,
  8, 43, 8, 8, 25, 8, 43, 8, 8, 8, 25, 8, 25, 8, 43, 43, 8, 8, 25, 8, 25, 8, 43, 25, 25,
  8, 25, 8, 25, 8, 43, 8, 43, 8, 25, 8, 25, 8, 43, 25, 8, 25, 25, 8, 25, 8, 43, 8, 25,
  25, 25, 8, 25, 8, 43, 8, 8, 43, 25, 8, 25, 8, 43, 25, 8, 8, 43, 8, 25, 8, 43, 8, 25,
  8, 43, 8, 25, 8, 43, 8, 8, 25, 43, 8, 25, 8, 43, 8, 8, 8, 8, 25, 25, 8, 43, 43, 8, 8,
  8, 25, 25, 8, 43, 25, 25, 8, 8, 25, 25, 8, 43, 8, 43, 8, 8, 25, 25, 8, 43, 25, 8, 25,
  8, 25, 25, 8, 43, 8, 25, 25, 8, 25, 25, 8, 43, 8, 8, 43, 8, 25, 25, 8, 43, 25, 8, 8,
  25, 25, 25, 8, 43, 8, 25, 8, 25, 25, 25, 8, 43, 8, 8, 25, 25, 25, 25, 8, 43, 8, 8, 8,
  43, 25, 25, 8, 43, 43, 43, 8, 43, 25, 25, 8, 43, 25, 8, 8, 8, 43, 25, 8, 43, 8, 25, 8,
  8, 43, 25, 8, 43, 8, 8, 25, 8, 43, 25, 8, 43, 25, 43, 43, 8, 43, 25, 8, 43, 8, 8, 8,
  25, 43, 25, 8, 43, 8, 8, 8, 8, 8, 43, 8, 43, 25, 25, 8, 8, 8, 43, 8, 43, 25, 8, 25, 8,
  8, 43, 8, 43, 8, 25, 25, 8, 8, 43, 8, 43, 25, 8, 8, 25, 8, 43, 8, 43, 8, 25, 8, 25, 8,
  43, 8, 43, 8, 8, 25, 25, 8, 43, 8, 43, 43, 8, 43, 43, 8, 43, 8, 43, 25, 8, 8, 8, 25,
  43, 8, 43, 8, 25, 8, 8, 25, 43, 8, 43, 8, 8, 8, 25, 25, 43, 8, 43, 25, 25, 43, 25, 25,
  43, 8, 43, 43, 8, 43, 8, 43, 43, 8, 43, 8, 43, 25, 25, 43, 43, 8, 43, 43, 43, 25, 25,
  43, 43, 8, 43, 43, 8, 8, 43, 43, 43, 8, 43, 43, 8, 43, 43, 43, 43, 8, 43, 25, 8, 8, 8,
  8, 8, 25, 43, 8, 25, 8, 8, 8, 8, 25, 43, 25, 43, 8, 8, 8, 8, 25, 43, 8, 8, 25, 8, 8,
  8, 25, 43, 43, 8, 25, 8, 8, 8, 25, 43, 25, 25, 25, 8, 8, 8, 25, 43, 8, 43, 25, 8, 8,
  8, 25, 43, 8, 25, 43, 8, 8, 8, 25, 43, 8, 8, 8, 25, 8, 8, 25, 43, 43, 8, 8, 25, 8, 8,
  25, 43, 25, 25, 8, 25, 8, 8, 25, 43, 8, 43, 8, 25, 8, 8, 25, 43, 25, 8, 25, 25, 8, 8,
  25, 43, 8, 25, 25, 25, 8, 8, 25, 43, 8, 8, 43, 25, 8, 8, 25, 43, 25, 8, 8, 43, 8, 8,
  25, 43, 8, 25, 8, 43, 8, 8, 25, 43, 8, 8, 25, 43, 8, 8, 25, 43, 8, 8, 8, 8, 25, 8, 25,
  43, 25, 25, 8, 8, 25, 8, 25, 43, 25, 8, 25, 8, 25, 8, 25, 43, 8, 25, 25, 8, 25, 8, 25,
  43, 25, 8, 8, 25, 25, 8, 25, 43, 8, 25, 8, 25, 25, 8, 25, 43, 8, 8, 25, 25, 25, 8, 25,
  43, 43, 43, 25, 25, 25, 8, 25, 43, 25, 8, 8, 8, 43, 8, 25, 43, 8, 25, 8, 8, 43, 8, 25,
  43, 8, 8, 25, 8, 43, 8, 25, 43, 8, 8, 8, 25, 43, 8, 25, 43, 43, 25, 43, 43, 43, 8, 25,
  43, 8, 8, 8, 8, 8, 25, 25, 43, 43, 8, 8, 8, 8, 25, 25, 43, 25, 25, 8, 8, 8, 25, 25,
  43, 8, 43, 8, 8, 8, 25, 25, 43, 25, 8, 25, 8, 8, 25, 25, 43, 8, 25, 25, 8, 8, 25, 25,
  43, 8, 8, 43, 8, 8, 25, 25, 43, 25, 8, 8, 25, 8, 25, 25, 43, 8, 25, 8, 25, 8, 25, 25,
  43, 8, 8, 25, 25, 8, 25, 25, 43, 8, 8, 8, 43, 8, 25, 25, 43, 43, 25, 25, 43, 8, 25,
  25, 43, 25, 8, 8, 8, 25, 25, 25, 43, 8, 25, 8, 8, 25, 25, 25, 43, 8, 8, 25, 8, 25, 25,
  25, 43, 8, 8, 8, 25, 25, 25, 25, 43, 8, 43, 25, 43, 25, 25, 25, 43, 25, 8, 43, 43, 25,
  25, 25, 43, 8, 8, 8, 8, 43, 25, 25, 43, 43, 25, 8, 25, 43, 25, 25, 43, 8, 25, 43, 25,
  43, 25, 25, 43, 25, 8, 8, 8, 8, 43, 25, 43, 8, 25, 8, 8, 8, 43, 25, 43, 8, 8, 25, 8,
  8, 43, 25, 43, 43, 25, 43, 8, 8, 43, 25, 43, 8, 8, 8, 25, 8, 43, 25, 43, 25, 43, 43,
  43, 8, 43, 25, 43, 8, 8, 8, 8, 25, 43, 25, 43, 25, 43, 8, 25, 25, 43, 25, 43, 43, 8,
  25, 25, 25, 43, 25, 43, 8, 8, 25, 43, 43, 43, 25, 43, 8, 8, 8, 8, 8, 8, 43, 43, 25,
  25, 8, 8, 8, 8, 43, 43, 43, 43, 8, 8, 8, 8, 43, 43, 8, 25, 25, 8, 8, 8, 43, 43, 43, 8,
  43, 8, 8, 8, 43, 43, 43, 43, 43, 8, 8, 8, 43, 43, 25, 8, 8, 25, 8, 8, 43, 43, 8, 25,
  8, 25, 8, 8, 43, 43, 8, 8, 25, 25, 8, 8, 43, 43, 43, 8, 43, 43, 8, 8, 43, 43, 43, 43,
  43, 43, 8, 8, 43, 43, 8, 8, 8, 25, 25, 8, 43, 43, 25, 25, 43, 25, 25, 8, 43, 43, 43,
  8, 8, 8, 43, 8, 43, 43, 43, 43, 8, 8, 43, 8, 43, 43, 43, 8, 43, 8, 43, 8, 43, 43, 8,
  43, 43, 8, 43, 8, 43, 43, 43, 43, 43, 8, 43, 8, 43, 43, 43, 8, 8, 43, 43, 8, 43, 43,
  8, 43, 8, 43, 43, 8, 43, 43, 43, 43, 8, 43, 43, 8, 43, 43, 8, 43, 43, 43, 43, 8, 43,
  43, 25, 8, 8, 8, 8, 25, 43, 43, 8, 25, 8, 8, 8, 25, 43, 43, 8, 8, 25, 8, 8, 25, 43,
  43, 8, 8, 8, 25, 8, 25, 43, 43, 25, 43, 8, 43, 8, 25, 43, 43, 8, 25, 43, 43, 8, 25,
  43, 43, 8, 8, 8, 8, 25, 25, 43, 43, 25, 43, 25, 8, 25, 25, 43, 43, 25, 8, 25, 25, 43,
  25, 43, 43, 43, 43, 8, 8, 8, 43, 43, 43, 8, 43, 43, 8, 8, 43, 43, 43, 43, 8, 43, 43,
  8, 43, 43, 43, 8, 25, 25, 25, 25, 43, 43, 43, 43, 25, 8, 43, 25, 43, 43, 43, 8, 43, 8,
  8, 43, 43, 43, 43, 43, 43, 8, 8, 43, 43, 43, 43, 8, 8, 43, 8, 43, 43, 43, 43, 43, 8,
  43, 8, 43, 43, 43, 43, 8, 43, 43, 8, 43, 43, 43, 43, 8, 43, 8, 43, 43, 43, 43, 43, 43,
  43, 43, 43, 43, 43, 43, 43,
]);

/**
 * `iq3s_grid` — 512 entries of four bytes.
 *
 * Every value is **odd**: 1, 3, 5, 7, 9, 11, 13, 15. A codebook of odd
 * magnitudes has no zero and no repeated value under scaling, which is why an
 * IQ3_S tensor also reads 0.00% sparsity — the fourth grid format in a row to
 * do so, and the third distinct reason.
 */
const IQ3S_GRID = new Uint8Array([
  1, 1, 1, 1, 3, 1, 1, 1, 5, 1, 1, 1, 11, 1, 1, 1, 15, 1, 1, 1, 1, 3, 1, 1, 3, 3, 1, 1,
  5, 3, 1, 1, 9, 3, 1, 1, 13, 3, 1, 1, 1, 5, 1, 1, 3, 5, 1, 1, 11, 5, 1, 1, 7, 7, 1, 1,
  1, 9, 1, 1, 5, 9, 1, 1, 11, 9, 1, 1, 15, 9, 1, 1, 3, 11, 1, 1, 7, 11, 1, 1, 1, 13, 1,
  1, 5, 13, 1, 1, 3, 15, 1, 1, 9, 15, 1, 1, 15, 15, 1, 1, 1, 1, 3, 1, 3, 1, 3, 1, 5, 1,
  3, 1, 9, 1, 3, 1, 1, 3, 3, 1, 3, 3, 3, 1, 11, 3, 3, 1, 1, 5, 3, 1, 7, 5, 3, 1, 15, 5,
  3, 1, 3, 7, 3, 1, 11, 7, 3, 1, 9, 9, 3, 1, 3, 13, 3, 1, 11, 13, 3, 1, 5, 15, 3, 1, 1,
  1, 5, 1, 3, 1, 5, 1, 11, 1, 5, 1, 15, 1, 5, 1, 1, 3, 5, 1, 7, 3, 5, 1, 13, 3, 5, 1, 3,
  5, 5, 1, 11, 5, 5, 1, 1, 7, 5, 1, 9, 7, 5, 1, 5, 9, 5, 1, 11, 9, 5, 1, 15, 9, 5, 1, 3,
  11, 5, 1, 7, 11, 5, 1, 1, 15, 5, 1, 7, 15, 5, 1, 7, 1, 7, 1, 3, 3, 7, 1, 11, 3, 7, 1,
  1, 5, 7, 1, 5, 5, 7, 1, 3, 7, 7, 1, 7, 7, 7, 1, 13, 7, 7, 1, 9, 9, 7, 1, 1, 11, 7, 1,
  5, 11, 7, 1, 15, 13, 7, 1, 3, 15, 7, 1, 11, 15, 7, 1, 1, 1, 9, 1, 7, 3, 9, 1, 15, 3,
  9, 1, 3, 5, 9, 1, 9, 5, 9, 1, 5, 7, 9, 1, 1, 9, 9, 1, 7, 9, 9, 1, 3, 11, 9, 1, 1, 15,
  9, 1, 5, 1, 11, 1, 9, 1, 11, 1, 1, 5, 11, 1, 5, 5, 11, 1, 13, 5, 11, 1, 7, 7, 11, 1,
  3, 9, 11, 1, 11, 9, 11, 1, 15, 9, 11, 1, 13, 13, 11, 1, 7, 15, 11, 1, 13, 1, 13, 1, 3,
  3, 13, 1, 7, 3, 13, 1, 3, 7, 13, 1, 5, 11, 13, 1, 3, 15, 13, 1, 1, 1, 15, 1, 5, 1, 15,
  1, 9, 1, 15, 1, 1, 5, 15, 1, 5, 5, 15, 1, 13, 5, 15, 1, 7, 7, 15, 1, 1, 11, 15, 1, 9,
  11, 15, 1, 1, 1, 1, 3, 3, 1, 1, 3, 5, 1, 1, 3, 9, 1, 1, 3, 1, 3, 1, 3, 3, 3, 1, 3, 7,
  3, 1, 3, 11, 3, 1, 3, 15, 3, 1, 3, 1, 5, 1, 3, 5, 5, 1, 3, 3, 7, 1, 3, 9, 7, 1, 3, 13,
  7, 1, 3, 9, 11, 1, 3, 13, 11, 1, 3, 3, 13, 1, 3, 5, 15, 1, 3, 1, 1, 3, 3, 3, 1, 3, 3,
  7, 1, 3, 3, 13, 1, 3, 3, 1, 3, 3, 3, 9, 3, 3, 3, 3, 5, 3, 3, 1, 7, 3, 3, 7, 7, 3, 3,
  3, 9, 3, 3, 1, 11, 3, 3, 5, 11, 3, 3, 1, 15, 3, 3, 13, 15, 3, 3, 1, 1, 5, 3, 5, 3, 5,
  3, 11, 3, 5, 3, 15, 3, 5, 3, 1, 5, 5, 3, 9, 5, 5, 3, 5, 7, 5, 3, 1, 9, 5, 3, 7, 9, 5,
  3, 11, 11, 5, 3, 1, 13, 5, 3, 5, 15, 5, 3, 3, 1, 7, 3, 9, 1, 7, 3, 15, 1, 7, 3, 1, 3,
  7, 3, 7, 3, 7, 3, 3, 5, 7, 3, 15, 5, 7, 3, 1, 7, 7, 3, 9, 7, 7, 3, 3, 9, 7, 3, 5, 13,
  7, 3, 1, 15, 7, 3, 7, 1, 9, 3, 11, 1, 9, 3, 5, 3, 9, 3, 9, 3, 9, 3, 3, 7, 9, 3, 7, 7,
  9, 3, 5, 9, 9, 3, 13, 9, 9, 3, 1, 11, 9, 3, 9, 11, 9, 3, 3, 1, 11, 3, 1, 3, 11, 3, 7,
  3, 11, 3, 3, 5, 11, 3, 1, 7, 11, 3, 5, 7, 11, 3, 3, 11, 11, 3, 1, 5, 13, 3, 9, 5, 13,
  3, 15, 5, 13, 3, 9, 9, 13, 3, 13, 9, 13, 3, 3, 1, 15, 3, 7, 1, 15, 3, 1, 3, 15, 3, 5,
  3, 15, 3, 3, 5, 15, 3, 11, 7, 15, 3, 3, 9, 15, 3, 5, 13, 15, 3, 1, 15, 15, 3, 1, 1, 1,
  5, 3, 1, 1, 5, 7, 1, 1, 5, 11, 1, 1, 5, 15, 1, 1, 5, 1, 3, 1, 5, 5, 3, 1, 5, 9, 3, 1,
  5, 13, 3, 1, 5, 3, 5, 1, 5, 7, 5, 1, 5, 15, 5, 1, 5, 1, 7, 1, 5, 5, 7, 1, 5, 3, 9, 1,
  5, 7, 9, 1, 5, 11, 9, 1, 5, 1, 11, 1, 5, 5, 11, 1, 5, 15, 13, 1, 5, 1, 15, 1, 5, 7,
  15, 1, 5, 11, 15, 1, 5, 1, 1, 3, 5, 5, 1, 3, 5, 1, 3, 3, 5, 7, 3, 3, 5, 15, 3, 3, 5,
  5, 5, 3, 5, 11, 5, 3, 5, 3, 7, 3, 5, 9, 7, 3, 5, 5, 9, 3, 5, 3, 11, 3, 5, 3, 1, 5, 5,
  9, 1, 5, 5, 15, 1, 5, 5, 3, 5, 5, 5, 7, 5, 5, 5, 1, 7, 5, 5, 15, 7, 5, 5, 3, 9, 5, 5,
  7, 11, 5, 5, 15, 11, 5, 5, 3, 15, 5, 5, 9, 15, 5, 5, 1, 1, 7, 5, 5, 1, 7, 5, 11, 1, 7,
  5, 3, 3, 7, 5, 5, 5, 7, 5, 9, 5, 7, 5, 3, 7, 7, 5, 7, 7, 7, 5, 5, 9, 7, 5, 1, 11, 7,
  5, 13, 13, 7, 5, 3, 1, 9, 5, 15, 1, 9, 5, 1, 5, 9, 5, 7, 5, 9, 5, 5, 7, 9, 5, 11, 7,
  9, 5, 3, 9, 9, 5, 5, 15, 9, 5, 11, 15, 9, 5, 9, 1, 11, 5, 3, 3, 11, 5, 5, 5, 11, 5,
  15, 7, 11, 5, 1, 9, 11, 5, 7, 11, 11, 5, 1, 15, 11, 5, 1, 1, 13, 5, 5, 1, 13, 5, 15,
  1, 13, 5, 3, 5, 13, 5, 11, 11, 13, 5, 3, 13, 13, 5, 11, 1, 15, 5, 3, 3, 15, 5, 13, 5,
  15, 5, 1, 7, 15, 5, 7, 9, 15, 5, 1, 11, 15, 5, 5, 1, 1, 7, 3, 3, 1, 7, 7, 3, 1, 7, 11,
  3, 1, 7, 15, 3, 1, 7, 5, 5, 1, 7, 3, 7, 1, 7, 7, 7, 1, 7, 11, 7, 1, 7, 5, 9, 1, 7, 9,
  9, 1, 7, 15, 9, 1, 7, 3, 11, 1, 7, 7, 13, 1, 7, 3, 15, 1, 7, 3, 1, 3, 7, 7, 1, 3, 7,
  11, 1, 3, 7, 9, 3, 3, 7, 3, 5, 3, 7, 7, 5, 3, 7, 1, 9, 3, 7, 1, 13, 3, 7, 5, 15, 3, 7,
  13, 15, 3, 7, 1, 1, 5, 7, 5, 3, 5, 7, 1, 5, 5, 7, 5, 7, 5, 7, 9, 7, 5, 7, 1, 11, 5, 7,
  3, 1, 7, 7, 1, 3, 7, 7, 9, 3, 7, 7, 3, 5, 7, 7, 7, 5, 7, 7, 15, 5, 7, 7, 1, 7, 7, 7,
  3, 9, 7, 7, 7, 9, 7, 7, 15, 9, 7, 7, 11, 11, 7, 7, 7, 15, 7, 7, 7, 1, 9, 7, 3, 3, 9,
  7, 13, 3, 9, 7, 5, 5, 9, 7, 3, 7, 9, 7, 5, 11, 9, 7, 1, 13, 9, 7, 9, 13, 9, 7, 3, 1,
  11, 7, 1, 3, 11, 7, 5, 3, 11, 7, 11, 5, 11, 7, 5, 7, 11, 7, 9, 9, 11, 7, 13, 11, 11,
  7, 7, 15, 11, 7, 13, 3, 13, 7, 3, 9, 13, 7, 3, 1, 15, 7, 7, 1, 15, 7, 1, 5, 15, 7, 5,
  5, 15, 7, 11, 7, 15, 7, 1, 1, 1, 9, 9, 1, 1, 9, 5, 3, 1, 9, 1, 5, 1, 9, 9, 5, 1, 9,
  15, 5, 1, 9, 5, 7, 1, 9, 3, 9, 1, 9, 1, 11, 1, 9, 1, 15, 1, 9, 5, 1, 3, 9, 15, 1, 3,
  9, 3, 3, 3, 9, 7, 3, 3, 9, 5, 5, 3, 9, 1, 7, 3, 9, 11, 7, 3, 9, 7, 9, 3, 9, 3, 11, 3,
  9, 11, 11, 3, 9, 3, 1, 5, 9, 7, 1, 5, 9, 1, 3, 5, 9, 11, 3, 5, 9, 3, 5, 5, 9, 7, 7, 5,
  9, 1, 9, 5, 9, 15, 11, 5, 9, 5, 13, 5, 9, 1, 15, 5, 9, 9, 1, 7, 9, 3, 3, 7, 9, 7, 3,
  7, 9, 1, 5, 7, 9, 5, 5, 7, 9, 3, 7, 7, 9, 11, 7, 7, 9, 1, 1, 9, 9, 5, 1, 9, 9, 9, 5,
  9, 9, 15, 7, 9, 9, 1, 9, 9, 9, 3, 15, 9, 9, 11, 1, 11, 9, 15, 1, 11, 9, 3, 5, 11, 9,
  5, 13, 11, 9, 7, 3, 13, 9, 9, 7, 13, 9, 1, 13, 13, 9, 1, 3, 15, 9, 11, 3, 15, 9, 1, 7,
  15, 9, 7, 9, 15, 9, 3, 11, 15, 9, 5, 1, 1, 11, 1, 3, 1, 11, 9, 3, 1, 11, 5, 5, 1, 11,
  1, 9, 1, 11, 9, 9, 1, 11, 15, 9, 1, 11, 5, 11, 1, 11, 13, 13, 1, 11, 9, 15, 1, 11, 3,
  1, 3, 11, 7, 1, 3, 11, 11, 1, 3, 11, 5, 3, 3, 11, 3, 5, 3, 11, 5, 7, 3, 11, 5, 15, 3,
  11, 1, 1, 5, 11, 3, 3, 5, 11, 7, 5, 5, 11, 1, 7, 5, 11, 13, 7, 5, 11, 7, 11, 5, 11, 5,
  1, 7, 11, 15, 1, 7, 11, 1, 3, 7, 11, 15, 5, 7, 11, 9, 9, 7, 11, 3, 11, 7, 11, 11, 13,
  7, 11, 7, 15, 7, 11, 3, 1, 9, 11, 9, 1, 9, 11, 1, 5, 9, 11, 5, 7, 9, 11, 13, 9, 9, 11,
  5, 3, 11, 11, 13, 5, 11, 11, 3, 11, 11, 11, 7, 11, 11, 11, 5, 9, 13, 11, 5, 1, 15, 11,
  9, 1, 15, 11, 5, 5, 15, 11, 3, 3, 1, 13, 7, 3, 1, 13, 11, 3, 1, 13, 3, 7, 1, 13, 7, 7,
  1, 13, 1, 13, 1, 13, 1, 1, 3, 13, 1, 5, 3, 13, 15, 5, 3, 13, 9, 13, 3, 13, 5, 3, 5,
  13, 9, 7, 5, 13, 5, 9, 5, 13, 11, 11, 5, 13, 5, 13, 5, 13, 1, 15, 5, 13, 1, 1, 7, 13,
  9, 3, 7, 13, 3, 5, 7, 13, 1, 9, 7, 13, 11, 5, 9, 13, 7, 9, 9, 13, 5, 13, 9, 13, 1, 1,
  11, 13, 7, 1, 11, 13, 9, 7, 11, 13, 1, 13, 11, 13, 11, 1, 13, 13, 1, 9, 13, 13, 3, 3,
  15, 13, 7, 3, 15, 13, 1, 1, 1, 15, 9, 1, 1, 15, 15, 1, 1, 15, 1, 5, 1, 15, 5, 5, 1,
  15, 13, 7, 1, 15, 1, 9, 1, 15, 9, 11, 1, 15, 5, 13, 1, 15, 5, 1, 3, 15, 3, 3, 3, 15,
  9, 5, 3, 15, 7, 9, 3, 15, 11, 9, 3, 15, 3, 1, 5, 15, 9, 1, 5, 15, 1, 3, 5, 15, 13, 3,
  5, 15, 3, 5, 5, 15, 1, 7, 5, 15, 3, 11, 5, 15, 5, 1, 7, 15, 5, 7, 7, 15, 11, 7, 7, 15,
  7, 11, 7, 15, 3, 1, 9, 15, 11, 1, 9, 15, 7, 3, 9, 15, 1, 5, 9, 15, 1, 11, 9, 15, 5, 5,
  11, 15, 5, 9, 11, 15, 5, 1, 13, 15, 3, 7, 13, 15, 1, 1, 15, 15,
]);

/**
 * `iq1s_grid` — 2048 entries of eight **signed** bytes, extracted by
 * `scripts/gen-ggml-tables.c` like the other two.
 *
 * `Int8Array`, not `Uint8Array`, and that is the whole of the difference from
 * its neighbours: ggml reads this one through an `int8_t *`. Emitted signed at
 * the source so the sign lives in the data rather than in a reinterpretation
 * the decode loop has to remember.
 *
 * **Every value is -1, 0 or +1.** A true ternary codebook, where IQ2_XXS holds
 * three magnitudes and IQ3_XXS seven. What separates the eight positions is not
 * their magnitudes but which of 2048 sign-and-zero patterns the 11-bit index
 * selects.
 *
 * Note this grid *does* contain zero, unlike IQ2_XXS's — and an IQ1_S tensor
 * still decodes no zero weight, because `IQ1S_DELTA` is added before scaling.
 * Same observed sparsity, entirely different reason.
 */
const IQ1S_GRID = new Int8Array([
  -1, -1, -1, -1, -1, -1, -1, -1, 1, -1, -1, -1, -1, -1, -1, -1, 0, 0, -1, -1, -1, -1,
  -1, -1, -1, 1, -1, -1, -1, -1, -1, -1, 1, 1, -1, -1, -1, -1, -1, -1, 0, -1, 0, -1, -1,
  -1, -1, -1, 0, 0, 0, -1, -1, -1, -1, -1, -1, -1, 1, -1, -1, -1, -1, -1, 1, -1, 1, -1,
  -1, -1, -1, -1, -1, 1, 1, -1, -1, -1, -1, -1, 1, 1, 1, -1, -1, -1, -1, -1, 0, 0, -1,
  0, -1, -1, -1, -1, 0, -1, 0, 0, -1, -1, -1, -1, -1, 0, 0, 0, -1, -1, -1, -1, 1, 0, 0,
  0, -1, -1, -1, -1, 0, 0, 1, 0, -1, -1, -1, -1, -1, -1, -1, 1, -1, -1, -1, -1, 1, -1,
  -1, 1, -1, -1, -1, -1, -1, 1, -1, 1, -1, -1, -1, -1, 1, 1, -1, 1, -1, -1, -1, -1, 0,
  0, 0, 1, -1, -1, -1, -1, -1, -1, 1, 1, -1, -1, -1, -1, 1, -1, 1, 1, -1, -1, -1, -1,
  -1, 1, 1, 1, -1, -1, -1, -1, 1, 1, 1, 1, -1, -1, -1, -1, -1, 0, -1, -1, 0, -1, -1, -1,
  0, 0, -1, -1, 0, -1, -1, -1, 0, -1, 0, -1, 0, -1, -1, -1, -1, 0, 0, -1, 0, -1, -1, -1,
  1, 0, 0, -1, 0, -1, -1, -1, 0, 1, 0, -1, 0, -1, -1, -1, 1, 1, 0, -1, 0, -1, -1, -1, 0,
  0, 1, -1, 0, -1, -1, -1, 0, -1, -1, 0, 0, -1, -1, -1, 1, 0, -1, 0, 0, -1, -1, -1, 0,
  1, -1, 0, 0, -1, -1, -1, 1, -1, 0, 0, 0, -1, -1, -1, 0, 0, 0, 0, 0, -1, -1, -1, 1, 1,
  0, 0, 0, -1, -1, -1, 0, -1, 1, 0, 0, -1, -1, -1, -1, 0, 1, 0, 0, -1, -1, -1, 1, 0, 1,
  0, 0, -1, -1, -1, -1, 1, 1, 0, 0, -1, -1, -1, 0, 0, -1, 1, 0, -1, -1, -1, 0, -1, 0, 1,
  0, -1, -1, -1, -1, 0, 0, 1, 0, -1, -1, -1, 1, 0, 0, 1, 0, -1, -1, -1, 0, 0, 1, 1, 0,
  -1, -1, -1, -1, -1, -1, -1, 1, -1, -1, -1, 1, -1, -1, -1, 1, -1, -1, -1, -1, 1, -1,
  -1, 1, -1, -1, -1, 1, 1, -1, -1, 1, -1, -1, -1, 0, 0, 0, -1, 1, -1, -1, -1, -1, -1, 1,
  -1, 1, -1, -1, -1, 1, -1, 1, -1, 1, -1, -1, -1, -1, 1, 1, -1, 1, -1, -1, -1, 1, 1, 1,
  -1, 1, -1, -1, -1, 0, 0, -1, 0, 1, -1, -1, -1, 0, -1, 0, 0, 1, -1, -1, -1, 0, 1, 0, 0,
  1, -1, -1, -1, -1, 0, 1, 0, 1, -1, -1, -1, 0, 1, 1, 0, 1, -1, -1, -1, -1, -1, -1, 1,
  1, -1, -1, -1, 1, -1, -1, 1, 1, -1, -1, -1, -1, 1, -1, 1, 1, -1, -1, -1, 1, 1, -1, 1,
  1, -1, -1, -1, 0, -1, 0, 1, 1, -1, -1, -1, 0, 0, 0, 1, 1, -1, -1, -1, 0, 1, 0, 1, 1,
  -1, -1, -1, -1, -1, 1, 1, 1, -1, -1, -1, 1, -1, 1, 1, 1, -1, -1, -1, -1, 1, 1, 1, 1,
  -1, -1, -1, 1, 1, 1, 1, 1, -1, -1, -1, 0, -1, 0, -1, -1, 0, -1, -1, -1, 0, 0, -1, -1,
  0, -1, -1, 1, 0, 0, -1, -1, 0, -1, -1, 0, 0, 1, -1, -1, 0, -1, -1, 0, -1, -1, 0, -1,
  0, -1, -1, 0, 1, -1, 0, -1, 0, -1, -1, 0, 0, 0, 0, -1, 0, -1, -1, 1, 1, 0, 0, -1, 0,
  -1, -1, -1, 0, 1, 0, -1, 0, -1, -1, 0, 0, 1, 0, -1, 0, -1, -1, 0, -1, 0, 1, -1, 0, -1,
  -1, 0, 1, 0, 1, -1, 0, -1, -1, 0, 0, 1, 1, -1, 0, -1, -1, 0, -1, -1, -1, 0, 0, -1, -1,
  -1, 0, -1, -1, 0, 0, -1, -1, 0, 0, -1, -1, 0, 0, -1, -1, 1, 0, -1, -1, 0, 0, -1, -1,
  0, 0, 0, -1, 0, 0, -1, -1, -1, 1, 0, -1, 0, 0, -1, -1, 1, 1, 0, -1, 0, 0, -1, -1, 0,
  1, 1, -1, 0, 0, -1, -1, -1, -1, -1, 0, 0, 0, -1, -1, 0, 0, -1, 0, 0, 0, -1, -1, 1, 1,
  -1, 0, 0, 0, -1, -1, -1, -1, 0, 0, 0, 0, -1, -1, 0, -1, 0, 0, 0, 0, -1, -1, -1, 0, 0,
  0, 0, 0, -1, -1, 0, 0, 0, 0, 0, 0, -1, -1, 1, 0, 0, 0, 0, 0, -1, -1, 0, 1, 0, 0, 0, 0,
  -1, -1, -1, -1, 1, 0, 0, 0, -1, -1, 1, -1, 1, 0, 0, 0, -1, -1, 0, 0, 1, 0, 0, 0, -1,
  -1, -1, 1, 1, 0, 0, 0, -1, -1, 1, 1, 1, 0, 0, 0, -1, -1, 0, -1, -1, 1, 0, 0, -1, -1,
  0, -1, 0, 1, 0, 0, -1, -1, 0, 0, 0, 1, 0, 0, -1, -1, -1, 1, 0, 1, 0, 0, -1, -1, 1, 1,
  0, 1, 0, 0, -1, -1, 0, -1, 1, 1, 0, 0, -1, -1, -1, 0, 1, 1, 0, 0, -1, -1, 0, 0, 1, 1,
  0, 0, -1, -1, 1, 0, 1, 1, 0, 0, -1, -1, 0, 1, 1, 1, 0, 0, -1, -1, -1, 0, 0, -1, 1, 0,
  -1, -1, 0, 1, 0, -1, 1, 0, -1, -1, 0, -1, -1, 0, 1, 0, -1, -1, -1, 0, -1, 0, 1, 0, -1,
  -1, -1, -1, 0, 0, 1, 0, -1, -1, 1, -1, 0, 0, 1, 0, -1, -1, 0, 0, 0, 0, 1, 0, -1, -1,
  -1, 1, 0, 0, 1, 0, -1, -1, -1, -1, 1, 0, 1, 0, -1, -1, 0, -1, 1, 0, 1, 0, -1, -1, 1,
  0, 1, 0, 1, 0, -1, -1, 0, 1, 1, 0, 1, 0, -1, -1, 0, 0, -1, 1, 1, 0, -1, -1, 0, -1, 0,
  1, 1, 0, -1, -1, -1, 0, 0, 1, 1, 0, -1, -1, 0, 1, 0, 1, 1, 0, -1, -1, -1, -1, -1, -1,
  -1, 1, -1, -1, 1, -1, -1, -1, -1, 1, -1, -1, -1, 1, -1, -1, -1, 1, -1, -1, 1, 1, -1,
  -1, -1, 1, -1, -1, 0, 0, 0, -1, -1, 1, -1, -1, -1, -1, 1, -1, -1, 1, -1, -1, 1, -1, 1,
  -1, -1, 1, -1, -1, -1, 1, 1, -1, -1, 1, -1, -1, 1, 1, 1, -1, -1, 1, -1, -1, 0, 0, -1,
  0, -1, 1, -1, -1, 0, -1, 0, 0, -1, 1, -1, -1, 1, 0, 0, 0, -1, 1, -1, -1, 0, 0, 1, 0,
  -1, 1, -1, -1, -1, -1, -1, 1, -1, 1, -1, -1, 1, -1, -1, 1, -1, 1, -1, -1, -1, 1, -1,
  1, -1, 1, -1, -1, 1, 1, -1, 1, -1, 1, -1, -1, 0, 0, 0, 1, -1, 1, -1, -1, -1, -1, 1, 1,
  -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, -1, -1, -1, 1, 1, 1, -1, 1, -1, -1, 1, 1, 1, 1, -1,
  1, -1, -1, 0, 0, -1, -1, 0, 1, -1, -1, 0, -1, 0, -1, 0, 1, -1, -1, -1, 0, 0, -1, 0, 1,
  -1, -1, 0, 1, 0, -1, 0, 1, -1, -1, -1, 0, 1, -1, 0, 1, -1, -1, 0, 0, 1, -1, 0, 1, -1,
  -1, 0, -1, -1, 0, 0, 1, -1, -1, -1, -1, 0, 0, 0, 1, -1, -1, 0, -1, 0, 0, 0, 1, -1, -1,
  0, 0, 0, 0, 0, 1, -1, -1, 0, -1, 1, 0, 0, 1, -1, -1, -1, 0, 1, 0, 0, 1, -1, -1, 0, 1,
  1, 0, 0, 1, -1, -1, 0, -1, 0, 1, 0, 1, -1, -1, -1, 0, 0, 1, 0, 1, -1, -1, 1, 0, 0, 1,
  0, 1, -1, -1, 0, 1, 0, 1, 0, 1, -1, -1, 0, 0, 1, 1, 0, 1, -1, -1, -1, -1, -1, -1, 1,
  1, -1, -1, 1, -1, -1, -1, 1, 1, -1, -1, -1, 1, -1, -1, 1, 1, -1, -1, 1, 1, -1, -1, 1,
  1, -1, -1, 0, 0, 0, -1, 1, 1, -1, -1, -1, -1, 1, -1, 1, 1, -1, -1, 1, -1, 1, -1, 1, 1,
  -1, -1, -1, 1, 1, -1, 1, 1, -1, -1, 1, 1, 1, -1, 1, 1, -1, -1, 0, 0, -1, 0, 1, 1, -1,
  -1, 0, -1, 0, 0, 1, 1, -1, -1, 0, 1, 0, 0, 1, 1, -1, -1, 0, -1, 1, 0, 1, 1, -1, -1, 0,
  0, 1, 0, 1, 1, -1, -1, -1, -1, -1, 1, 1, 1, -1, -1, 1, -1, -1, 1, 1, 1, -1, -1, 0, 0,
  -1, 1, 1, 1, -1, -1, -1, 1, -1, 1, 1, 1, -1, -1, 1, 1, -1, 1, 1, 1, -1, -1, 0, 0, 0,
  1, 1, 1, -1, -1, -1, -1, 1, 1, 1, 1, -1, -1, 1, -1, 1, 1, 1, 1, -1, -1, -1, 1, 1, 1,
  1, 1, -1, -1, 1, 1, 1, 1, 1, 1, -1, -1, -1, -1, 0, -1, -1, -1, 0, -1, 0, -1, 0, -1,
  -1, -1, 0, -1, -1, 0, 0, -1, -1, -1, 0, -1, 0, 1, 0, -1, -1, -1, 0, -1, -1, 0, 1, -1,
  -1, -1, 0, -1, 0, 0, 1, -1, -1, -1, 0, -1, 0, -1, -1, 0, -1, -1, 0, -1, -1, 0, -1, 0,
  -1, -1, 0, -1, -1, -1, 0, 0, -1, -1, 0, -1, 0, 0, 0, 0, -1, -1, 0, -1, -1, 1, 0, 0,
  -1, -1, 0, -1, 0, -1, 1, 0, -1, -1, 0, -1, -1, 0, 1, 0, -1, -1, 0, -1, 0, 0, 1, 0, -1,
  -1, 0, -1, 0, 1, 1, 0, -1, -1, 0, -1, 0, -1, 0, 1, -1, -1, 0, -1, -1, 0, 0, 1, -1, -1,
  0, -1, 1, 0, 0, 1, -1, -1, 0, -1, 0, -1, 1, 1, -1, -1, 0, -1, 0, 0, 1, 1, -1, -1, 0,
  -1, 0, -1, -1, -1, 0, -1, 0, -1, -1, 0, -1, -1, 0, -1, 0, -1, 1, 0, -1, -1, 0, -1, 0,
  -1, 0, 1, -1, -1, 0, -1, 0, -1, -1, -1, 0, -1, 0, -1, 0, -1, 1, -1, 0, -1, 0, -1, 0,
  -1, 0, 0, 0, -1, 0, -1, 0, -1, -1, 1, 0, -1, 0, -1, 0, -1, 0, -1, 1, -1, 0, -1, 0, -1,
  -1, 0, 1, -1, 0, -1, 0, -1, 0, 1, 1, -1, 0, -1, 0, -1, 0, 0, -1, 0, 0, -1, 0, -1, 1,
  1, -1, 0, 0, -1, 0, -1, -1, -1, 0, 0, 0, -1, 0, -1, 0, -1, 0, 0, 0, -1, 0, -1, 1, -1,
  0, 0, 0, -1, 0, -1, -1, 0, 0, 0, 0, -1, 0, -1, 0, 0, 0, 0, 0, -1, 0, -1, 1, 0, 0, 0,
  0, -1, 0, -1, 0, 1, 0, 0, 0, -1, 0, -1, -1, -1, 1, 0, 0, -1, 0, -1, 0, 0, 1, 0, 0, -1,
  0, -1, -1, 0, -1, 1, 0, -1, 0, -1, 1, -1, 0, 1, 0, -1, 0, -1, 0, 0, 0, 1, 0, -1, 0,
  -1, 0, -1, 1, 1, 0, -1, 0, -1, -1, 0, 1, 1, 0, -1, 0, -1, 0, -1, 0, -1, 1, -1, 0, -1,
  -1, 0, 0, -1, 1, -1, 0, -1, 1, 0, 0, -1, 1, -1, 0, -1, 0, 0, 1, -1, 1, -1, 0, -1, -1,
  -1, -1, 0, 1, -1, 0, -1, 1, 0, -1, 0, 1, -1, 0, -1, 0, 1, -1, 0, 1, -1, 0, -1, 1, -1,
  0, 0, 1, -1, 0, -1, 0, 0, 0, 0, 1, -1, 0, -1, -1, 1, 0, 0, 1, -1, 0, -1, 1, 1, 0, 0,
  1, -1, 0, -1, -1, 0, 1, 0, 1, -1, 0, -1, 1, 0, 1, 0, 1, -1, 0, -1, 0, 0, -1, 1, 1, -1,
  0, -1, 0, -1, 0, 1, 1, -1, 0, -1, -1, 0, 0, 1, 1, -1, 0, -1, 1, 0, 0, 1, 1, -1, 0, -1,
  0, 0, 1, 1, 1, -1, 0, -1, 0, -1, -1, -1, -1, 0, 0, -1, 1, 0, -1, -1, -1, 0, 0, -1, 0,
  1, -1, -1, -1, 0, 0, -1, -1, 0, 0, -1, -1, 0, 0, -1, 0, 0, 0, -1, -1, 0, 0, -1, -1, 1,
  0, -1, -1, 0, 0, -1, 0, 1, 0, -1, -1, 0, 0, -1, 0, -1, 1, -1, -1, 0, 0, -1, 1, 0, 1,
  -1, -1, 0, 0, -1, 0, -1, -1, 0, -1, 0, 0, -1, 0, 0, -1, 0, -1, 0, 0, -1, 1, 0, -1, 0,
  -1, 0, 0, -1, -1, 1, -1, 0, -1, 0, 0, -1, 1, 1, -1, 0, -1, 0, 0, -1, 0, -1, 0, 0, -1,
  0, 0, -1, -1, 0, 0, 0, -1, 0, 0, -1, 0, 0, 0, 0, -1, 0, 0, -1, 1, 0, 0, 0, -1, 0, 0,
  -1, 0, 1, 0, 0, -1, 0, 0, -1, 1, -1, 1, 0, -1, 0, 0, -1, 0, 0, 1, 0, -1, 0, 0, -1, -1,
  1, 1, 0, -1, 0, 0, -1, -1, 0, -1, 1, -1, 0, 0, -1, 0, 1, -1, 1, -1, 0, 0, -1, -1, -1,
  0, 1, -1, 0, 0, -1, -1, 0, 0, 1, -1, 0, 0, -1, 0, 0, 0, 1, -1, 0, 0, -1, -1, 1, 0, 1,
  -1, 0, 0, -1, 0, 1, 0, 1, -1, 0, 0, -1, 1, 1, 0, 1, -1, 0, 0, -1, 0, -1, 1, 1, -1, 0,
  0, -1, -1, 0, 1, 1, -1, 0, 0, -1, 0, 0, 1, 1, -1, 0, 0, -1, 0, 1, 1, 1, -1, 0, 0, -1,
  1, -1, -1, -1, 0, 0, 0, -1, 0, 0, -1, -1, 0, 0, 0, -1, 1, 1, -1, -1, 0, 0, 0, -1, 0,
  -1, 0, -1, 0, 0, 0, -1, -1, 0, 0, -1, 0, 0, 0, -1, 0, 0, 0, -1, 0, 0, 0, -1, 1, 0, 0,
  -1, 0, 0, 0, -1, 0, 1, 0, -1, 0, 0, 0, -1, -1, -1, 1, -1, 0, 0, 0, -1, 1, -1, 1, -1,
  0, 0, 0, -1, 0, 0, 1, -1, 0, 0, 0, -1, -1, 1, 1, -1, 0, 0, 0, -1, 1, 1, 1, -1, 0, 0,
  0, -1, 0, -1, -1, 0, 0, 0, 0, -1, -1, 0, -1, 0, 0, 0, 0, -1, 0, 0, -1, 0, 0, 0, 0, -1,
  1, 0, -1, 0, 0, 0, 0, -1, 0, -1, 0, 0, 0, 0, 0, -1, 1, -1, 0, 0, 0, 0, 0, -1, -1, 0,
  0, 0, 0, 0, 0, -1, 0, 0, 0, 0, 0, 0, 0, -1, 1, 0, 0, 0, 0, 0, 0, -1, 0, 1, 0, 0, 0, 0,
  0, -1, 1, 1, 0, 0, 0, 0, 0, -1, 0, -1, 1, 0, 0, 0, 0, -1, -1, 0, 1, 0, 0, 0, 0, -1, 0,
  0, 1, 0, 0, 0, 0, -1, 1, 0, 1, 0, 0, 0, 0, -1, 0, 1, 1, 0, 0, 0, 0, -1, -1, -1, -1, 1,
  0, 0, 0, -1, 1, -1, -1, 1, 0, 0, 0, -1, -1, 0, -1, 1, 0, 0, 0, -1, 0, 0, -1, 1, 0, 0,
  0, -1, -1, 1, -1, 1, 0, 0, 0, -1, 1, 1, -1, 1, 0, 0, 0, -1, -1, -1, 0, 1, 0, 0, 0, -1,
  0, -1, 0, 1, 0, 0, 0, -1, -1, 0, 0, 1, 0, 0, 0, -1, 0, 0, 0, 1, 0, 0, 0, -1, 1, 0, 0,
  1, 0, 0, 0, -1, 0, 1, 0, 1, 0, 0, 0, -1, 1, 1, 0, 1, 0, 0, 0, -1, -1, -1, 1, 1, 0, 0,
  0, -1, 1, -1, 1, 1, 0, 0, 0, -1, 0, 0, 1, 1, 0, 0, 0, -1, 0, -1, -1, -1, 1, 0, 0, -1,
  -1, 0, -1, -1, 1, 0, 0, -1, 0, 0, -1, -1, 1, 0, 0, -1, 1, 0, -1, -1, 1, 0, 0, -1, 0,
  0, 0, -1, 1, 0, 0, -1, 1, 0, 0, -1, 1, 0, 0, -1, -1, 1, 0, -1, 1, 0, 0, -1, 1, 1, 0,
  -1, 1, 0, 0, -1, 0, -1, 1, -1, 1, 0, 0, -1, 1, 0, 1, -1, 1, 0, 0, -1, -1, -1, -1, 0,
  1, 0, 0, -1, 1, -1, -1, 0, 1, 0, 0, -1, -1, 0, -1, 0, 1, 0, 0, -1, 0, 0, -1, 0, 1, 0,
  0, -1, -1, 1, -1, 0, 1, 0, 0, -1, 1, 1, -1, 0, 1, 0, 0, -1, 0, -1, 0, 0, 1, 0, 0, -1,
  0, 0, 0, 0, 1, 0, 0, -1, 1, 0, 0, 0, 1, 0, 0, -1, -1, 1, 0, 0, 1, 0, 0, -1, 0, 1, 0,
  0, 1, 0, 0, -1, 0, -1, 1, 0, 1, 0, 0, -1, -1, 0, 1, 0, 1, 0, 0, -1, 0, 0, 1, 0, 1, 0,
  0, -1, -1, 1, 1, 0, 1, 0, 0, -1, 0, 1, 1, 0, 1, 0, 0, -1, 1, 1, 1, 0, 1, 0, 0, -1, 1,
  0, -1, 1, 1, 0, 0, -1, 1, 1, -1, 1, 1, 0, 0, -1, 1, -1, 0, 1, 1, 0, 0, -1, 0, 0, 0, 1,
  1, 0, 0, -1, -1, 0, 1, 1, 1, 0, 0, -1, 0, 1, 1, 1, 1, 0, 0, -1, 0, -1, 0, -1, -1, 1,
  0, -1, 1, 0, 0, -1, -1, 1, 0, -1, 0, 0, 1, -1, -1, 1, 0, -1, 0, -1, -1, 0, -1, 1, 0,
  -1, -1, 0, -1, 0, -1, 1, 0, -1, 1, 0, -1, 0, -1, 1, 0, -1, 0, 1, -1, 0, -1, 1, 0, -1,
  -1, -1, 0, 0, -1, 1, 0, -1, 0, 0, 0, 0, -1, 1, 0, -1, -1, 1, 0, 0, -1, 1, 0, -1, 1, 1,
  0, 0, -1, 1, 0, -1, -1, -1, 1, 0, -1, 1, 0, -1, 0, -1, 1, 0, -1, 1, 0, -1, -1, 0, 1,
  0, -1, 1, 0, -1, 1, 0, 1, 0, -1, 1, 0, -1, 0, 1, 1, 0, -1, 1, 0, -1, 0, 0, -1, 1, -1,
  1, 0, -1, 0, -1, 0, 1, -1, 1, 0, -1, -1, 0, 0, 1, -1, 1, 0, -1, 0, 0, 1, 1, -1, 1, 0,
  -1, -1, -1, 0, -1, 0, 1, 0, -1, 1, -1, 0, -1, 0, 1, 0, -1, 0, 0, 0, -1, 0, 1, 0, -1,
  1, 1, 0, -1, 0, 1, 0, -1, 0, -1, 1, -1, 0, 1, 0, -1, 0, 0, 1, -1, 0, 1, 0, -1, 1, -1,
  -1, 0, 0, 1, 0, -1, -1, 0, -1, 0, 0, 1, 0, -1, 0, 0, -1, 0, 0, 1, 0, -1, -1, 1, -1, 0,
  0, 1, 0, -1, 0, -1, 0, 0, 0, 1, 0, -1, -1, 0, 0, 0, 0, 1, 0, -1, 0, 0, 0, 0, 0, 1, 0,
  -1, 1, 0, 0, 0, 0, 1, 0, -1, 0, 1, 0, 0, 0, 1, 0, -1, 1, 1, 0, 0, 0, 1, 0, -1, -1, -1,
  1, 0, 0, 1, 0, -1, 0, 0, 1, 0, 0, 1, 0, -1, 1, 1, 1, 0, 0, 1, 0, -1, 0, 1, -1, 1, 0,
  1, 0, -1, 0, -1, 0, 1, 0, 1, 0, -1, 1, -1, 0, 1, 0, 1, 0, -1, 0, 0, 0, 1, 0, 1, 0, -1,
  -1, 1, 0, 1, 0, 1, 0, -1, 0, -1, 1, 1, 0, 1, 0, -1, 1, 0, 1, 1, 0, 1, 0, -1, 0, 1, 1,
  1, 0, 1, 0, -1, 0, 1, -1, -1, 1, 1, 0, -1, 1, 0, 0, -1, 1, 1, 0, -1, -1, 0, 1, -1, 1,
  1, 0, -1, 1, 0, 1, -1, 1, 1, 0, -1, -1, 0, -1, 0, 1, 1, 0, -1, 1, 0, -1, 0, 1, 1, 0,
  -1, 0, 1, -1, 0, 1, 1, 0, -1, -1, -1, 0, 0, 1, 1, 0, -1, 1, -1, 0, 0, 1, 1, 0, -1, 0,
  0, 0, 0, 1, 1, 0, -1, -1, 1, 0, 0, 1, 1, 0, -1, 0, -1, 1, 0, 1, 1, 0, -1, 1, 0, 1, 0,
  1, 1, 0, -1, 0, 1, 1, 0, 1, 1, 0, -1, 0, 0, -1, 1, 1, 1, 0, -1, 0, -1, 0, 1, 1, 1, 0,
  -1, 1, 0, 0, 1, 1, 1, 0, -1, 1, 1, 0, 1, 1, 1, 0, -1, -1, -1, -1, -1, -1, -1, 1, -1,
  1, -1, -1, -1, -1, -1, 1, -1, -1, 1, -1, -1, -1, -1, 1, -1, 1, 1, -1, -1, -1, -1, 1,
  -1, 0, 0, 0, -1, -1, -1, 1, -1, -1, -1, 1, -1, -1, -1, 1, -1, 1, -1, 1, -1, -1, -1, 1,
  -1, 0, 0, 1, -1, -1, -1, 1, -1, -1, 1, 1, -1, -1, -1, 1, -1, 1, 1, 1, -1, -1, -1, 1,
  -1, 0, 0, -1, 0, -1, -1, 1, -1, 0, -1, 0, 0, -1, -1, 1, -1, 0, 1, 0, 0, -1, -1, 1, -1,
  0, -1, 1, 0, -1, -1, 1, -1, 0, 0, 1, 0, -1, -1, 1, -1, -1, -1, -1, 1, -1, -1, 1, -1,
  1, -1, -1, 1, -1, -1, 1, -1, -1, 1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, -1, -1, 1, -1,
  0, 0, 0, 1, -1, -1, 1, -1, -1, -1, 1, 1, -1, -1, 1, -1, 1, -1, 1, 1, -1, -1, 1, -1, 0,
  0, 1, 1, -1, -1, 1, -1, -1, 1, 1, 1, -1, -1, 1, -1, 1, 1, 1, 1, -1, -1, 1, -1, 0, 0,
  -1, -1, 0, -1, 1, -1, 0, -1, 0, -1, 0, -1, 1, -1, -1, 0, 0, -1, 0, -1, 1, -1, 0, 1, 0,
  -1, 0, -1, 1, -1, 0, 0, 1, -1, 0, -1, 1, -1, 1, -1, -1, 0, 0, -1, 1, -1, -1, 0, -1, 0,
  0, -1, 1, -1, 0, 1, -1, 0, 0, -1, 1, -1, 0, 0, 0, 0, 0, -1, 1, -1, -1, 1, 0, 0, 0, -1,
  1, -1, 1, 1, 0, 0, 0, -1, 1, -1, 0, -1, 1, 0, 0, -1, 1, -1, -1, 0, 1, 0, 0, -1, 1, -1,
  0, 0, 1, 0, 0, -1, 1, -1, 1, 0, 1, 0, 0, -1, 1, -1, 0, 0, -1, 1, 0, -1, 1, -1, -1, -1,
  0, 1, 0, -1, 1, -1, 1, 0, 0, 1, 0, -1, 1, -1, 0, 1, 0, 1, 0, -1, 1, -1, 0, 0, 1, 1, 0,
  -1, 1, -1, 0, -1, -1, -1, 1, -1, 1, -1, -1, 1, -1, -1, 1, -1, 1, -1, 1, 1, -1, -1, 1,
  -1, 1, -1, 0, -1, 0, -1, 1, -1, 1, -1, 0, 0, 0, -1, 1, -1, 1, -1, -1, -1, 1, -1, 1,
  -1, 1, -1, 1, -1, 1, -1, 1, -1, 1, -1, -1, 1, 1, -1, 1, -1, 1, -1, 1, 1, 1, -1, 1, -1,
  1, -1, 0, 0, -1, 0, 1, -1, 1, -1, 0, -1, 0, 0, 1, -1, 1, -1, 1, 0, 0, 0, 1, -1, 1, -1,
  0, 1, 0, 0, 1, -1, 1, -1, 0, 0, 1, 0, 1, -1, 1, -1, 0, -1, -1, 1, 1, -1, 1, -1, -1, 1,
  -1, 1, 1, -1, 1, -1, 1, 1, -1, 1, 1, -1, 1, -1, 0, -1, 0, 1, 1, -1, 1, -1, 0, 0, 0, 1,
  1, -1, 1, -1, -1, -1, 1, 1, 1, -1, 1, -1, 1, -1, 1, 1, 1, -1, 1, -1, -1, 1, 1, 1, 1,
  -1, 1, -1, 1, 1, 1, 1, 1, -1, 1, -1, 0, 0, -1, -1, -1, 0, 1, -1, -1, 0, 0, -1, -1, 0,
  1, -1, 1, 0, 0, -1, -1, 0, 1, -1, 0, 1, 0, -1, -1, 0, 1, -1, 0, 0, 1, -1, -1, 0, 1,
  -1, -1, 0, -1, 0, -1, 0, 1, -1, 0, 0, -1, 0, -1, 0, 1, -1, 1, 0, -1, 0, -1, 0, 1, -1,
  0, 1, -1, 0, -1, 0, 1, -1, 1, -1, 0, 0, -1, 0, 1, -1, 0, 0, 0, 0, -1, 0, 1, -1, -1, 1,
  0, 0, -1, 0, 1, -1, 1, 1, 0, 0, -1, 0, 1, -1, 1, 0, 1, 0, -1, 0, 1, -1, 0, 0, -1, 1,
  -1, 0, 1, -1, 0, -1, 0, 1, -1, 0, 1, -1, -1, 0, 0, 1, -1, 0, 1, -1, 0, 1, 0, 1, -1, 0,
  1, -1, 0, -1, 1, 1, -1, 0, 1, -1, 0, 0, 1, 1, -1, 0, 1, -1, 0, 1, -1, -1, 0, 0, 1, -1,
  0, 0, 0, -1, 0, 0, 1, -1, 0, -1, 1, -1, 0, 0, 1, -1, 0, 1, 1, -1, 0, 0, 1, -1, -1, -1,
  -1, 0, 0, 0, 1, -1, 0, 0, -1, 0, 0, 0, 1, -1, -1, 1, -1, 0, 0, 0, 1, -1, 0, -1, 0, 0,
  0, 0, 1, -1, -1, 0, 0, 0, 0, 0, 1, -1, 0, 0, 0, 0, 0, 0, 1, -1, 0, 1, 0, 0, 0, 0, 1,
  -1, 1, -1, 1, 0, 0, 0, 1, -1, 0, 0, 1, 0, 0, 0, 1, -1, -1, 1, 1, 0, 0, 0, 1, -1, 0, 1,
  -1, 1, 0, 0, 1, -1, -1, -1, 0, 1, 0, 0, 1, -1, -1, 0, 0, 1, 0, 0, 1, -1, 0, 0, 0, 1,
  0, 0, 1, -1, -1, 1, 0, 1, 0, 0, 1, -1, 1, 1, 0, 1, 0, 0, 1, -1, 0, -1, 1, 1, 0, 0, 1,
  -1, -1, 0, 1, 1, 0, 0, 1, -1, 1, 0, 1, 1, 0, 0, 1, -1, 0, 1, 1, 1, 0, 0, 1, -1, 0, 0,
  -1, -1, 1, 0, 1, -1, -1, -1, 0, -1, 1, 0, 1, -1, 1, -1, 0, -1, 1, 0, 1, -1, 0, 1, 0,
  -1, 1, 0, 1, -1, 0, 0, 1, -1, 1, 0, 1, -1, 0, -1, -1, 0, 1, 0, 1, -1, 0, 1, -1, 0, 1,
  0, 1, -1, 0, 0, 0, 0, 1, 0, 1, -1, -1, -1, 1, 0, 1, 0, 1, -1, 0, -1, 1, 0, 1, 0, 1,
  -1, 0, 1, 1, 0, 1, 0, 1, -1, -1, 0, -1, 1, 1, 0, 1, -1, 1, 0, -1, 1, 1, 0, 1, -1, -1,
  -1, 0, 1, 1, 0, 1, -1, 1, 1, 0, 1, 1, 0, 1, -1, -1, -1, -1, -1, -1, 1, 1, -1, 1, -1,
  -1, -1, -1, 1, 1, -1, -1, 1, -1, -1, -1, 1, 1, -1, 1, 1, -1, -1, -1, 1, 1, -1, 0, 0,
  0, -1, -1, 1, 1, -1, -1, -1, 1, -1, -1, 1, 1, -1, 1, -1, 1, -1, -1, 1, 1, -1, -1, 1,
  1, -1, -1, 1, 1, -1, 1, 1, 1, -1, -1, 1, 1, -1, 0, 0, -1, 0, -1, 1, 1, -1, 0, -1, 0,
  0, -1, 1, 1, -1, -1, 0, 0, 0, -1, 1, 1, -1, 0, 0, 1, 0, -1, 1, 1, -1, -1, -1, -1, 1,
  -1, 1, 1, -1, 1, -1, -1, 1, -1, 1, 1, -1, -1, 1, -1, 1, -1, 1, 1, -1, 1, 1, -1, 1, -1,
  1, 1, -1, -1, -1, 1, 1, -1, 1, 1, -1, 1, -1, 1, 1, -1, 1, 1, -1, -1, 1, 1, 1, -1, 1,
  1, -1, 1, 1, 1, 1, -1, 1, 1, -1, 0, 1, -1, -1, 0, 1, 1, -1, 0, -1, 0, -1, 0, 1, 1, -1,
  -1, 0, 0, -1, 0, 1, 1, -1, 0, 1, 0, -1, 0, 1, 1, -1, 0, 0, 1, -1, 0, 1, 1, -1, 1, 0,
  -1, 0, 0, 1, 1, -1, 0, 1, -1, 0, 0, 1, 1, -1, 1, -1, 0, 0, 0, 1, 1, -1, 0, 0, 0, 0, 0,
  1, 1, -1, 0, -1, 1, 0, 0, 1, 1, -1, -1, 0, 1, 0, 0, 1, 1, -1, 1, 0, 1, 0, 0, 1, 1, -1,
  0, 1, 1, 0, 0, 1, 1, -1, 0, 0, -1, 1, 0, 1, 1, -1, -1, -1, 0, 1, 0, 1, 1, -1, 1, 0, 0,
  1, 0, 1, 1, -1, 0, 1, 0, 1, 0, 1, 1, -1, -1, 0, 1, 1, 0, 1, 1, -1, 0, 0, 1, 1, 0, 1,
  1, -1, -1, -1, -1, -1, 1, 1, 1, -1, 1, -1, -1, -1, 1, 1, 1, -1, -1, 1, -1, -1, 1, 1,
  1, -1, 1, 1, -1, -1, 1, 1, 1, -1, -1, -1, 1, -1, 1, 1, 1, -1, 1, -1, 1, -1, 1, 1, 1,
  -1, -1, 1, 1, -1, 1, 1, 1, -1, 1, 1, 1, -1, 1, 1, 1, -1, 0, 0, -1, 0, 1, 1, 1, -1, 0,
  -1, 0, 0, 1, 1, 1, -1, 1, 0, 0, 0, 1, 1, 1, -1, 0, 1, 0, 0, 1, 1, 1, -1, 0, 0, 1, 0,
  1, 1, 1, -1, -1, -1, -1, 1, 1, 1, 1, -1, 1, -1, -1, 1, 1, 1, 1, -1, -1, 1, -1, 1, 1,
  1, 1, -1, 1, 1, -1, 1, 1, 1, 1, -1, 0, 0, 0, 1, 1, 1, 1, -1, -1, -1, 1, 1, 1, 1, 1,
  -1, 1, -1, 1, 1, 1, 1, 1, -1, -1, 1, 1, 1, 1, 1, 1, -1, 1, 1, 1, 1, 1, 1, 1, -1, 0, 0,
  -1, -1, -1, -1, -1, 0, 0, -1, 0, -1, -1, -1, -1, 0, 1, 0, 0, -1, -1, -1, -1, 0, 0, 0,
  1, -1, -1, -1, -1, 0, 0, 1, -1, 0, -1, -1, -1, 0, 1, -1, 0, 0, -1, -1, -1, 0, 0, 0, 0,
  0, -1, -1, -1, 0, -1, 1, 0, 0, -1, -1, -1, 0, 1, 1, 0, 0, -1, -1, -1, 0, 0, -1, 1, 0,
  -1, -1, -1, 0, -1, 0, 1, 0, -1, -1, -1, 0, 1, 0, 1, 0, -1, -1, -1, 0, -1, 0, 0, 1, -1,
  -1, -1, 0, 0, 1, 0, 1, -1, -1, -1, 0, 0, -1, 1, 1, -1, -1, -1, 0, 1, 0, 1, 1, -1, -1,
  -1, 0, -1, -1, -1, -1, 0, -1, -1, 0, 0, -1, -1, -1, 0, -1, -1, 0, -1, 0, -1, -1, 0,
  -1, -1, 0, 1, 0, -1, -1, 0, -1, -1, 0, 0, 1, -1, -1, 0, -1, -1, 0, 1, -1, 0, -1, 0,
  -1, -1, 0, 0, 0, 0, -1, 0, -1, -1, 0, 1, 0, 0, -1, 0, -1, -1, 0, -1, 1, 0, -1, 0, -1,
  -1, 0, 1, 1, 0, -1, 0, -1, -1, 0, 0, -1, 1, -1, 0, -1, -1, 0, 1, 0, 1, -1, 0, -1, -1,
  0, 0, 1, 1, -1, 0, -1, -1, 0, 0, 0, -1, 0, 0, -1, -1, 0, -1, 1, -1, 0, 0, -1, -1, 0,
  1, 1, -1, 0, 0, -1, -1, 0, 0, -1, 0, 0, 0, -1, -1, 0, -1, 0, 0, 0, 0, -1, -1, 0, 0, 0,
  0, 0, 0, -1, -1, 0, 1, 0, 0, 0, 0, -1, -1, 0, 0, 1, 0, 0, 0, -1, -1, 0, 1, 1, 0, 0, 0,
  -1, -1, 0, 0, 0, 1, 0, 0, -1, -1, 0, -1, 1, 1, 0, 0, -1, -1, 0, 1, 1, 1, 0, 0, -1, -1,
  0, 0, -1, -1, 1, 0, -1, -1, 0, -1, 0, -1, 1, 0, -1, -1, 0, 1, 0, -1, 1, 0, -1, -1, 0,
  -1, -1, 0, 1, 0, -1, -1, 0, 1, -1, 0, 1, 0, -1, -1, 0, 0, 0, 0, 1, 0, -1, -1, 0, -1,
  -1, 1, 1, 0, -1, -1, 0, 0, -1, 1, 1, 0, -1, -1, 0, 1, -1, 1, 1, 0, -1, -1, 0, 0, 0,
  -1, -1, 1, -1, -1, 0, 0, -1, 0, -1, 1, -1, -1, 0, -1, 0, 0, -1, 1, -1, -1, 0, 1, 0, 0,
  -1, 1, -1, -1, 0, 0, 0, 1, -1, 1, -1, -1, 0, 0, -1, -1, 0, 1, -1, -1, 0, 1, -1, 0, 0,
  1, -1, -1, 0, 0, 0, 0, 0, 1, -1, -1, 0, 1, 1, 0, 0, 1, -1, -1, 0, -1, 0, 1, 0, 1, -1,
  -1, 0, 0, 1, 1, 0, 1, -1, -1, 0, 0, 1, -1, 1, 1, -1, -1, 0, -1, 0, 0, 1, 1, -1, -1, 0,
  0, 0, 1, 1, 1, -1, -1, 0, 0, -1, -1, -1, -1, 0, -1, 0, 0, 0, 0, -1, -1, 0, -1, 0, 0,
  1, 0, -1, -1, 0, -1, 0, 0, 1, 1, -1, -1, 0, -1, 0, 0, 0, -1, 0, -1, 0, -1, 0, -1, 1,
  -1, 0, -1, 0, -1, 0, 1, 1, -1, 0, -1, 0, -1, 0, 0, -1, 0, 0, -1, 0, -1, 0, -1, 0, 0,
  0, -1, 0, -1, 0, 0, 0, 0, 0, -1, 0, -1, 0, 1, 0, 0, 0, -1, 0, -1, 0, 0, -1, 1, 0, -1,
  0, -1, 0, 1, -1, 1, 0, -1, 0, -1, 0, 0, 0, 1, 0, -1, 0, -1, 0, -1, 1, 1, 0, -1, 0, -1,
  0, 1, 1, 1, 0, -1, 0, -1, 0, 0, -1, -1, 1, -1, 0, -1, 0, 1, 0, -1, 1, -1, 0, -1, 0, 0,
  1, -1, 1, -1, 0, -1, 0, -1, -1, 0, 1, -1, 0, -1, 0, 1, -1, 0, 1, -1, 0, -1, 0, 0, 0,
  0, 1, -1, 0, -1, 0, -1, -1, 1, 1, -1, 0, -1, 0, 0, -1, 1, 1, -1, 0, -1, 0, 0, 1, 1, 1,
  -1, 0, -1, 0, 0, -1, -1, -1, 0, 0, -1, 0, 1, -1, -1, -1, 0, 0, -1, 0, 0, 0, -1, -1, 0,
  0, -1, 0, 1, 1, -1, -1, 0, 0, -1, 0, 0, -1, 0, -1, 0, 0, -1, 0, -1, 0, 0, -1, 0, 0,
  -1, 0, 0, 0, 0, -1, 0, 0, -1, 0, 1, 0, 0, -1, 0, 0, -1, 0, 0, 1, 0, -1, 0, 0, -1, 0,
  -1, -1, 1, -1, 0, 0, -1, 0, 0, 0, 1, -1, 0, 0, -1, 0, 1, 1, 1, -1, 0, 0, -1, 0, 0, -1,
  -1, 0, 0, 0, -1, 0, -1, 0, -1, 0, 0, 0, -1, 0, 0, 0, -1, 0, 0, 0, -1, 0, 1, 0, -1, 0,
  0, 0, -1, 0, 0, 1, -1, 0, 0, 0, -1, 0, -1, -1, 0, 0, 0, 0, -1, 0, 0, -1, 0, 0, 0, 0,
  -1, 0, -1, 0, 0, 0, 0, 0, -1, 0, 0, 0, 0, 0, 0, 0, -1, 0, 1, 0, 0, 0, 0, 0, -1, 0, -1,
  1, 0, 0, 0, 0, -1, 0, 0, 1, 0, 0, 0, 0, -1, 0, 0, -1, 1, 0, 0, 0, -1, 0, -1, 0, 1, 0,
  0, 0, -1, 0, 0, 0, 1, 0, 0, 0, -1, 0, 1, 0, 1, 0, 0, 0, -1, 0, 0, 1, 1, 0, 0, 0, -1,
  0, 1, -1, -1, 1, 0, 0, -1, 0, -1, 0, -1, 1, 0, 0, -1, 0, 0, 0, -1, 1, 0, 0, -1, 0, -1,
  1, -1, 1, 0, 0, -1, 0, 0, -1, 0, 1, 0, 0, -1, 0, -1, 0, 0, 1, 0, 0, -1, 0, 0, 0, 0, 1,
  0, 0, -1, 0, 1, 0, 0, 1, 0, 0, -1, 0, 0, 1, 0, 1, 0, 0, -1, 0, 1, 1, 0, 1, 0, 0, -1,
  0, 0, 0, 1, 1, 0, 0, -1, 0, -1, 1, 1, 1, 0, 0, -1, 0, 1, 1, 1, 1, 0, 0, -1, 0, 0, -1,
  -1, -1, 1, 0, -1, 0, 0, 0, -1, -1, 1, 0, -1, 0, 0, 1, -1, -1, 1, 0, -1, 0, -1, 0, 0,
  -1, 1, 0, -1, 0, 0, 0, 0, -1, 1, 0, -1, 0, -1, 1, 0, -1, 1, 0, -1, 0, 1, 1, 0, -1, 1,
  0, -1, 0, 0, -1, 1, -1, 1, 0, -1, 0, -1, 0, 1, -1, 1, 0, -1, 0, 0, 1, 1, -1, 1, 0, -1,
  0, -1, -1, -1, 0, 1, 0, -1, 0, 1, -1, -1, 0, 1, 0, -1, 0, 0, 0, -1, 0, 1, 0, -1, 0,
  -1, 1, -1, 0, 1, 0, -1, 0, -1, -1, 0, 0, 1, 0, -1, 0, 0, -1, 0, 0, 1, 0, -1, 0, 1, -1,
  0, 0, 1, 0, -1, 0, 0, 0, 0, 0, 1, 0, -1, 0, 1, 0, 0, 0, 1, 0, -1, 0, 0, 1, 0, 0, 1, 0,
  -1, 0, 1, -1, 1, 0, 1, 0, -1, 0, 0, 0, 1, 0, 1, 0, -1, 0, -1, 1, 1, 0, 1, 0, -1, 0, 0,
  -1, -1, 1, 1, 0, -1, 0, 0, 0, -1, 1, 1, 0, -1, 0, 1, 1, -1, 1, 1, 0, -1, 0, -1, 0, 0,
  1, 1, 0, -1, 0, 0, 0, 0, 1, 1, 0, -1, 0, 0, -1, 1, 1, 1, 0, -1, 0, -1, 0, 1, 1, 1, 0,
  -1, 0, 1, 0, 1, 1, 1, 0, -1, 0, 0, 0, -1, -1, -1, 1, -1, 0, 0, -1, 0, -1, -1, 1, -1,
  0, 0, 0, 0, -1, -1, 1, -1, 0, 1, 1, 0, -1, -1, 1, -1, 0, 0, 0, 1, -1, -1, 1, -1, 0, 1,
  -1, -1, 0, -1, 1, -1, 0, 0, 1, -1, 0, -1, 1, -1, 0, -1, -1, 0, 0, -1, 1, -1, 0, 0, 0,
  0, 0, -1, 1, -1, 0, -1, 1, 0, 0, -1, 1, -1, 0, 0, -1, 1, 0, -1, 1, -1, 0, -1, 0, 1, 0,
  -1, 1, -1, 0, 1, 0, 1, 0, -1, 1, -1, 0, 0, 1, 1, 0, -1, 1, -1, 0, 0, 0, -1, 1, -1, 1,
  -1, 0, 0, -1, 0, 1, -1, 1, -1, 0, -1, 0, 0, 1, -1, 1, -1, 0, 1, 0, 0, 1, -1, 1, -1, 0,
  0, 1, 0, 1, -1, 1, -1, 0, 0, 0, 1, 1, -1, 1, -1, 0, 0, -1, -1, -1, 0, 1, -1, 0, 0, 0,
  -1, -1, 0, 1, -1, 0, 1, 0, -1, -1, 0, 1, -1, 0, 1, 1, -1, -1, 0, 1, -1, 0, -1, -1, 0,
  -1, 0, 1, -1, 0, -1, 0, 0, -1, 0, 1, -1, 0, 0, 0, 0, -1, 0, 1, -1, 0, -1, 1, 0, -1, 0,
  1, -1, 0, 0, -1, 1, -1, 0, 1, -1, 0, -1, 0, 1, -1, 0, 1, -1, 0, 1, 0, 1, -1, 0, 1, -1,
  0, -1, -1, -1, 0, 0, 1, -1, 0, 0, 0, -1, 0, 0, 1, -1, 0, 1, 1, -1, 0, 0, 1, -1, 0, 0,
  -1, 0, 0, 0, 1, -1, 0, 1, -1, 0, 0, 0, 1, -1, 0, -1, 0, 0, 0, 0, 1, -1, 0, 0, 0, 0, 0,
  0, 1, -1, 0, 1, 0, 0, 0, 0, 1, -1, 0, 0, 1, 0, 0, 0, 1, -1, 0, -1, -1, 1, 0, 0, 1, -1,
  0, 1, -1, 1, 0, 0, 1, -1, 0, 0, 0, 1, 0, 0, 1, -1, 0, 1, 0, 1, 0, 0, 1, -1, 0, 1, 1,
  1, 0, 0, 1, -1, 0, 1, 0, -1, 1, 0, 1, -1, 0, 0, 1, -1, 1, 0, 1, -1, 0, 1, -1, 0, 1, 0,
  1, -1, 0, 0, 0, 0, 1, 0, 1, -1, 0, 1, 0, 0, 1, 0, 1, -1, 0, -1, 1, 0, 1, 0, 1, -1, 0,
  0, -1, 1, 1, 0, 1, -1, 0, -1, 0, 1, 1, 0, 1, -1, 0, 1, 0, 1, 1, 0, 1, -1, 0, 0, 1, 1,
  1, 0, 1, -1, 0, 1, 0, 0, -1, 1, 1, -1, 0, -1, 0, -1, 0, 1, 1, -1, 0, 1, 0, -1, 0, 1,
  1, -1, 0, 0, 1, -1, 0, 1, 1, -1, 0, 0, 0, 0, 0, 1, 1, -1, 0, -1, 1, 0, 0, 1, 1, -1, 0,
  1, 1, 0, 0, 1, 1, -1, 0, -1, 0, 1, 0, 1, 1, -1, 0, 0, 1, 1, 0, 1, 1, -1, 0, -1, 0, 0,
  1, 1, 1, -1, 0, 0, 0, 1, 1, 1, 1, -1, 0, 0, -1, -1, -1, -1, -1, 0, 0, -1, 0, -1, -1,
  -1, -1, 0, 0, 0, 0, -1, -1, -1, -1, 0, 0, 1, 0, -1, -1, -1, -1, 0, 0, 0, 1, -1, -1,
  -1, -1, 0, 0, 1, -1, 0, -1, -1, -1, 0, 0, 0, 0, 0, -1, -1, -1, 0, 0, 1, 1, 0, -1, -1,
  -1, 0, 0, 0, -1, 1, -1, -1, -1, 0, 0, -1, 0, 1, -1, -1, -1, 0, 0, 0, 1, 1, -1, -1, -1,
  0, 0, -1, -1, -1, 0, -1, -1, 0, 0, 0, 0, -1, 0, -1, -1, 0, 0, -1, 1, -1, 0, -1, -1, 0,
  0, 0, -1, 0, 0, -1, -1, 0, 0, -1, 0, 0, 0, -1, -1, 0, 0, 0, 0, 0, 0, -1, -1, 0, 0, 1,
  0, 0, 0, -1, -1, 0, 0, 0, 1, 0, 0, -1, -1, 0, 0, 0, 0, 1, 0, -1, -1, 0, 0, -1, 1, 1,
  0, -1, -1, 0, 0, 1, 0, -1, 1, -1, -1, 0, 0, 0, 1, -1, 1, -1, -1, 0, 0, 0, 0, 0, 1, -1,
  -1, 0, 0, -1, 1, 0, 1, -1, -1, 0, 0, -1, -1, 1, 1, -1, -1, 0, 0, 0, -1, 1, 1, -1, -1,
  0, 0, 1, 0, 1, 1, -1, -1, 0, 0, 0, 1, 1, 1, -1, -1, 0, 0, 0, 0, -1, -1, 0, -1, 0, 0,
  -1, 1, -1, -1, 0, -1, 0, 0, 0, 1, -1, -1, 0, -1, 0, 0, 1, 1, -1, -1, 0, -1, 0, 0, 0,
  -1, 0, -1, 0, -1, 0, 0, -1, 0, 0, -1, 0, -1, 0, 0, 0, 0, 0, -1, 0, -1, 0, 0, 1, 0, 0,
  -1, 0, -1, 0, 0, -1, 1, 0, -1, 0, -1, 0, 0, 0, 1, 0, -1, 0, -1, 0, 0, -1, -1, 1, -1,
  0, -1, 0, 0, 0, 0, 1, -1, 0, -1, 0, 0, 1, 0, 1, -1, 0, -1, 0, 0, -1, 1, 1, -1, 0, -1,
  0, 0, 1, 1, 1, -1, 0, -1, 0, 0, 0, -1, -1, 0, 0, -1, 0, 0, -1, 0, -1, 0, 0, -1, 0, 0,
  0, 0, -1, 0, 0, -1, 0, 0, 1, 0, -1, 0, 0, -1, 0, 0, 0, 1, -1, 0, 0, -1, 0, 0, -1, -1,
  0, 0, 0, -1, 0, 0, 0, -1, 0, 0, 0, -1, 0, 0, 1, -1, 0, 0, 0, -1, 0, 0, -1, 0, 0, 0, 0,
  -1, 0, 0, 0, 0, 0, 0, 0, -1, 0, 0, 1, 0, 0, 0, 0, -1, 0, 0, -1, 1, 0, 0, 0, -1, 0, 0,
  0, 1, 0, 0, 0, -1, 0, 0, 1, 1, 0, 0, 0, -1, 0, 0, 0, -1, 1, 0, 0, -1, 0, 0, -1, 0, 1,
  0, 0, -1, 0, 0, 0, 0, 1, 0, 0, -1, 0, 0, 1, 0, 1, 0, 0, -1, 0, 0, 0, 1, 1, 0, 0, -1,
  0, 0, 1, -1, -1, 1, 0, -1, 0, 0, 0, 0, -1, 1, 0, -1, 0, 0, 0, -1, 0, 1, 0, -1, 0, 0,
  -1, 0, 0, 1, 0, -1, 0, 0, 0, 0, 0, 1, 0, -1, 0, 0, 1, 0, 0, 1, 0, -1, 0, 0, 0, 1, 0,
  1, 0, -1, 0, 0, -1, -1, 1, 1, 0, -1, 0, 0, 0, 0, 1, 1, 0, -1, 0, 0, 1, 1, 1, 1, 0, -1,
  0, 0, 0, -1, -1, -1, 1, -1, 0, 0, 1, 0, -1, -1, 1, -1, 0, 0, 1, -1, 0, -1, 1, -1, 0,
  0, 0, 0, 0, -1, 1, -1, 0, 0, 1, 1, 0, -1, 1, -1, 0, 0, 0, -1, 1, -1, 1, -1, 0, 0, -1,
  0, 1, -1, 1, -1, 0, 0, 1, -1, -1, 0, 1, -1, 0, 0, 0, 0, -1, 0, 1, -1, 0, 0, 1, 1, -1,
  0, 1, -1, 0, 0, 0, -1, 0, 0, 1, -1, 0, 0, -1, 0, 0, 0, 1, -1, 0, 0, 0, 0, 0, 0, 1, -1,
  0, 0, 1, 0, 0, 0, 1, -1, 0, 0, 0, 1, 0, 0, 1, -1, 0, 0, 1, -1, 1, 0, 1, -1, 0, 0, 0,
  0, 1, 0, 1, -1, 0, 0, 0, 0, -1, 1, 1, -1, 0, 0, -1, -1, 0, 1, 1, -1, 0, 0, 1, -1, 0,
  1, 1, -1, 0, 0, 0, 0, 0, 1, 1, -1, 0, 0, 0, 1, 0, 1, 1, -1, 0, 0, 1, 1, 0, 1, 1, -1,
  0, 0, -1, 0, 1, 1, 1, -1, 0, 0, -1, 0, -1, -1, -1, 0, 0, 0, 0, 0, -1, -1, -1, 0, 0, 0,
  0, -1, 0, -1, -1, 0, 0, 0, -1, 0, 0, -1, -1, 0, 0, 0, 0, 0, 0, -1, -1, 0, 0, 0, 1, 0,
  0, -1, -1, 0, 0, 0, -1, 1, 0, -1, -1, 0, 0, 0, 0, 1, 0, -1, -1, 0, 0, 0, 0, -1, 1, -1,
  -1, 0, 0, 0, 0, 0, 1, -1, -1, 0, 0, 0, -1, 1, 1, -1, -1, 0, 0, 0, 1, 1, 1, -1, -1, 0,
  0, 0, 0, -1, -1, 0, -1, 0, 0, 0, -1, 0, -1, 0, -1, 0, 0, 0, 0, 0, -1, 0, -1, 0, 0, 0,
  1, 0, -1, 0, -1, 0, 0, 0, 0, 1, -1, 0, -1, 0, 0, 0, 1, 1, -1, 0, -1, 0, 0, 0, -1, -1,
  0, 0, -1, 0, 0, 0, 0, -1, 0, 0, -1, 0, 0, 0, -1, 0, 0, 0, -1, 0, 0, 0, 0, 0, 0, 0, -1,
  0, 0, 0, 1, 0, 0, 0, -1, 0, 0, 0, -1, 1, 0, 0, -1, 0, 0, 0, 0, 1, 0, 0, -1, 0, 0, 0,
  1, 1, 0, 0, -1, 0, 0, 0, 0, -1, 1, 0, -1, 0, 0, 0, 1, -1, 1, 0, -1, 0, 0, 0, -1, 0, 1,
  0, -1, 0, 0, 0, 0, 0, 1, 0, -1, 0, 0, 0, 1, 0, 1, 0, -1, 0, 0, 0, 0, 1, 1, 0, -1, 0,
  0, 0, -1, -1, -1, 1, -1, 0, 0, 0, -1, 1, -1, 1, -1, 0, 0, 0, 1, 1, -1, 1, -1, 0, 0, 0,
  0, -1, 0, 1, -1, 0, 0, 0, -1, 0, 0, 1, -1, 0, 0, 0, 0, 0, 0, 1, -1, 0, 0, 0, 1, 0, 0,
  1, -1, 0, 0, 0, 0, 1, 0, 1, -1, 0, 0, 0, 0, -1, 1, 1, -1, 0, 0, 0, -1, 0, 1, 1, -1, 0,
  0, 0, 0, 0, 1, 1, -1, 0, 0, 0, 1, 1, 1, 1, -1, 0, 0, 0, 0, -1, -1, -1, 0, 0, 0, 0, 1,
  -1, -1, -1, 0, 0, 0, 0, -1, 0, -1, -1, 0, 0, 0, 0, 0, 0, -1, -1, 0, 0, 0, 0, 1, 0, -1,
  -1, 0, 0, 0, 0, 0, 1, -1, -1, 0, 0, 0, 0, -1, -1, 0, -1, 0, 0, 0, 0, 0, -1, 0, -1, 0,
  0, 0, 0, 1, -1, 0, -1, 0, 0, 0, 0, -1, 0, 0, -1, 0, 0, 0, 0, 0, 0, 0, -1, 0, 0, 0, 0,
  1, 0, 0, -1, 0, 0, 0, 0, 0, 1, 0, -1, 0, 0, 0, 0, 1, 1, 0, -1, 0, 0, 0, 0, 0, -1, 1,
  -1, 0, 0, 0, 0, -1, 0, 1, -1, 0, 0, 0, 0, 0, 0, 1, -1, 0, 0, 0, 0, 1, 0, 1, -1, 0, 0,
  0, 0, 0, 1, 1, -1, 0, 0, 0, 0, -1, -1, -1, 0, 0, 0, 0, 0, 0, -1, -1, 0, 0, 0, 0, 0, 1,
  -1, -1, 0, 0, 0, 0, 0, -1, 0, -1, 0, 0, 0, 0, 0, 0, 0, -1, 0, 0, 0, 0, 0, 1, 0, -1, 0,
  0, 0, 0, 0, -1, 1, -1, 0, 0, 0, 0, 0, 0, 1, -1, 0, 0, 0, 0, 0, -1, -1, 0, 0, 0, 0, 0,
  0, 0, -1, 0, 0, 0, 0, 0, 0, 1, -1, 0, 0, 0, 0, 0, 0, -1, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
  0, 0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0, 0, -1, 1, 0, 0, 0, 0, 0, 0, 0, 1, 0, 0, 0, 0, 0,
  0, 1, 1, 0, 0, 0, 0, 0, 0, -1, -1, 1, 0, 0, 0, 0, 0, 0, -1, 1, 0, 0, 0, 0, 0, -1, 0,
  1, 0, 0, 0, 0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 1, 0, 1, 0, 0, 0, 0, 0, -1, 1, 1, 0, 0, 0,
  0, 0, 0, 1, 1, 0, 0, 0, 0, 0, 1, 1, 1, 0, 0, 0, 0, 0, 0, -1, -1, 1, 0, 0, 0, 0, -1, 0,
  -1, 1, 0, 0, 0, 0, 0, 0, -1, 1, 0, 0, 0, 0, 0, 1, -1, 1, 0, 0, 0, 0, 1, 1, -1, 1, 0,
  0, 0, 0, -1, -1, 0, 1, 0, 0, 0, 0, 0, -1, 0, 1, 0, 0, 0, 0, -1, 0, 0, 1, 0, 0, 0, 0,
  0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 1, 0, 0, 0, 0, -1, 1, 0, 1, 0, 0, 0, 0, 0, 1, 0, 1,
  0, 0, 0, 0, 0, -1, 1, 1, 0, 0, 0, 0, -1, 0, 1, 1, 0, 0, 0, 0, 0, 0, 1, 1, 0, 0, 0, 0,
  1, 0, 1, 1, 0, 0, 0, 0, 0, 1, 1, 1, 0, 0, 0, 0, -1, -1, -1, -1, 1, 0, 0, 0, 0, -1, -1,
  -1, 1, 0, 0, 0, 1, -1, -1, -1, 1, 0, 0, 0, -1, 0, -1, -1, 1, 0, 0, 0, 1, 0, -1, -1, 1,
  0, 0, 0, -1, 1, -1, -1, 1, 0, 0, 0, 0, 1, -1, -1, 1, 0, 0, 0, 0, -1, 0, -1, 1, 0, 0,
  0, -1, 0, 0, -1, 1, 0, 0, 0, 0, 0, 0, -1, 1, 0, 0, 0, -1, 1, 0, -1, 1, 0, 0, 0, 0, 1,
  0, -1, 1, 0, 0, 0, -1, -1, 1, -1, 1, 0, 0, 0, 0, -1, 1, -1, 1, 0, 0, 0, 1, -1, 1, -1,
  1, 0, 0, 0, -1, 0, 1, -1, 1, 0, 0, 0, 0, 0, 1, -1, 1, 0, 0, 0, 1, 0, 1, -1, 1, 0, 0,
  0, -1, 1, 1, -1, 1, 0, 0, 0, 0, 1, 1, -1, 1, 0, 0, 0, 0, -1, -1, 0, 1, 0, 0, 0, 0, 0,
  -1, 0, 1, 0, 0, 0, 1, 0, -1, 0, 1, 0, 0, 0, -1, 1, -1, 0, 1, 0, 0, 0, 0, 1, -1, 0, 1,
  0, 0, 0, 1, 1, -1, 0, 1, 0, 0, 0, -1, -1, 0, 0, 1, 0, 0, 0, 0, -1, 0, 0, 1, 0, 0, 0,
  1, -1, 0, 0, 1, 0, 0, 0, -1, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0,
  1, 0, 0, 0, -1, 1, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 1, 0, 0, 0, 1, 1, 0, 0, 1, 0, 0, 0,
  0, -1, 1, 0, 1, 0, 0, 0, -1, 0, 1, 0, 1, 0, 0, 0, 0, 0, 1, 0, 1, 0, 0, 0, 0, 1, 1, 0,
  1, 0, 0, 0, 1, -1, -1, 1, 1, 0, 0, 0, 0, 0, -1, 1, 1, 0, 0, 0, 1, 0, -1, 1, 1, 0, 0,
  0, -1, 1, -1, 1, 1, 0, 0, 0, 0, 1, -1, 1, 1, 0, 0, 0, 1, 1, -1, 1, 1, 0, 0, 0, 0, -1,
  0, 1, 1, 0, 0, 0, 0, 0, 0, 1, 1, 0, 0, 0, 1, 1, 0, 1, 1, 0, 0, 0, 1, -1, 1, 1, 1, 0,
  0, 0, 0, 0, 1, 1, 1, 0, 0, 0, 1, 0, 1, 1, 1, 0, 0, 0, -1, 1, 1, 1, 1, 0, 0, 0, 0, 1,
  1, 1, 1, 0, 0, 0, -1, 0, -1, -1, -1, 1, 0, 0, 0, 0, -1, -1, -1, 1, 0, 0, 1, 0, -1, -1,
  -1, 1, 0, 0, 0, 1, -1, -1, -1, 1, 0, 0, -1, -1, 0, -1, -1, 1, 0, 0, 0, 0, 0, -1, -1,
  1, 0, 0, -1, 1, 0, -1, -1, 1, 0, 0, 0, -1, 1, -1, -1, 1, 0, 0, 1, 1, 1, -1, -1, 1, 0,
  0, 0, 0, -1, 0, -1, 1, 0, 0, -1, 1, -1, 0, -1, 1, 0, 0, 1, 1, -1, 0, -1, 1, 0, 0, 0,
  -1, 0, 0, -1, 1, 0, 0, -1, 0, 0, 0, -1, 1, 0, 0, 0, 0, 0, 0, -1, 1, 0, 0, 1, 0, 0, 0,
  -1, 1, 0, 0, -1, 1, 0, 0, -1, 1, 0, 0, 0, 1, 0, 0, -1, 1, 0, 0, -1, -1, 1, 0, -1, 1,
  0, 0, 1, -1, 1, 0, -1, 1, 0, 0, -1, 0, 1, 0, -1, 1, 0, 0, 0, 0, 1, 0, -1, 1, 0, 0, 1,
  -1, -1, 1, -1, 1, 0, 0, 0, 1, -1, 1, -1, 1, 0, 0, -1, -1, 0, 1, -1, 1, 0, 0, 1, -1, 0,
  1, -1, 1, 0, 0, 0, 0, 0, 1, -1, 1, 0, 0, -1, 1, 0, 1, -1, 1, 0, 0, 0, -1, 1, 1, -1, 1,
  0, 0, 0, 1, 1, 1, -1, 1, 0, 0, 0, -1, -1, -1, 0, 1, 0, 0, 1, -1, -1, -1, 0, 1, 0, 0,
  0, 0, -1, -1, 0, 1, 0, 0, 1, 1, -1, -1, 0, 1, 0, 0, 0, -1, 0, -1, 0, 1, 0, 0, -1, 0,
  0, -1, 0, 1, 0, 0, 0, 0, 0, -1, 0, 1, 0, 0, 1, 0, 0, -1, 0, 1, 0, 0, 0, 1, 0, -1, 0,
  1, 0, 0, 0, 0, 1, -1, 0, 1, 0, 0, 0, -1, -1, 0, 0, 1, 0, 0, -1, 0, -1, 0, 0, 1, 0, 0,
  0, 0, -1, 0, 0, 1, 0, 0, 1, 0, -1, 0, 0, 1, 0, 0, 0, 1, -1, 0, 0, 1, 0, 0, -1, -1, 0,
  0, 0, 1, 0, 0, 0, -1, 0, 0, 0, 1, 0, 0, 1, -1, 0, 0, 0, 1, 0, 0, -1, 0, 0, 0, 0, 1, 0,
  0, 0, 0, 0, 0, 0, 1, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, -1, 1, 0, 0, 0, 1, 0, 0, 0, 1, 0,
  0, 0, 1, 0, 0, 1, 1, 0, 0, 0, 1, 0, 0, 0, -1, 1, 0, 0, 1, 0, 0, -1, 0, 1, 0, 0, 1, 0,
  0, 0, 0, 1, 0, 0, 1, 0, 0, 1, 0, 1, 0, 0, 1, 0, 0, 0, 1, 1, 0, 0, 1, 0, 0, 0, -1, -1,
  1, 0, 1, 0, 0, 0, 0, -1, 1, 0, 1, 0, 0, 0, 1, -1, 1, 0, 1, 0, 0, 0, -1, 0, 1, 0, 1, 0,
  0, -1, 0, 0, 1, 0, 1, 0, 0, 0, 0, 0, 1, 0, 1, 0, 0, 1, 0, 0, 1, 0, 1, 0, 0, -1, 1, 0,
  1, 0, 1, 0, 0, 0, 1, 0, 1, 0, 1, 0, 0, 0, 0, 1, 1, 0, 1, 0, 0, -1, 0, -1, -1, 1, 1, 0,
  0, -1, 1, -1, -1, 1, 1, 0, 0, 0, 0, 0, -1, 1, 1, 0, 0, 1, 1, 0, -1, 1, 1, 0, 0, -1,
  -1, 1, -1, 1, 1, 0, 0, 0, 0, 1, -1, 1, 1, 0, 0, 1, 0, 1, -1, 1, 1, 0, 0, 0, 1, 1, -1,
  1, 1, 0, 0, 0, 0, -1, 0, 1, 1, 0, 0, -1, 1, -1, 0, 1, 1, 0, 0, 0, 1, -1, 0, 1, 1, 0,
  0, 0, -1, 0, 0, 1, 1, 0, 0, 0, 0, 0, 0, 1, 1, 0, 0, 1, 0, 0, 0, 1, 1, 0, 0, -1, 1, 0,
  0, 1, 1, 0, 0, 0, 1, 0, 0, 1, 1, 0, 0, 1, -1, 1, 0, 1, 1, 0, 0, 0, 0, 1, 0, 1, 1, 0,
  0, -1, 1, 1, 0, 1, 1, 0, 0, 1, 1, 1, 0, 1, 1, 0, 0, 0, -1, -1, 1, 1, 1, 0, 0, 1, 1,
  -1, 1, 1, 1, 0, 0, 1, -1, 0, 1, 1, 1, 0, 0, 0, 0, 0, 1, 1, 1, 0, 0, 1, 0, 0, 1, 1, 1,
  0, 0, -1, 1, 0, 1, 1, 1, 0, 0, 1, 1, 0, 1, 1, 1, 0, 0, 0, -1, 1, 1, 1, 1, 0, 0, 0, 0,
  -1, -1, -1, -1, 1, 0, -1, 0, 0, -1, -1, -1, 1, 0, 1, 0, 0, -1, -1, -1, 1, 0, 0, 1, 0,
  -1, -1, -1, 1, 0, 0, 0, 1, -1, -1, -1, 1, 0, -1, 0, -1, 0, -1, -1, 1, 0, -1, -1, 0, 0,
  -1, -1, 1, 0, 0, 0, 0, 0, -1, -1, 1, 0, 1, 0, 0, 0, -1, -1, 1, 0, -1, 1, 0, 0, -1, -1,
  1, 0, 1, 1, 0, 0, -1, -1, 1, 0, 0, -1, 1, 0, -1, -1, 1, 0, -1, 0, 1, 0, -1, -1, 1, 0,
  1, 0, 1, 0, -1, -1, 1, 0, 0, 1, 1, 0, -1, -1, 1, 0, 0, -1, -1, 1, -1, -1, 1, 0, 1, 0,
  0, 1, -1, -1, 1, 0, 0, 0, 1, 1, -1, -1, 1, 0, 0, -1, -1, -1, 0, -1, 1, 0, -1, 0, -1,
  -1, 0, -1, 1, 0, 1, 0, -1, -1, 0, -1, 1, 0, 0, 1, -1, -1, 0, -1, 1, 0, 1, -1, 0, -1,
  0, -1, 1, 0, 0, 0, 0, -1, 0, -1, 1, 0, 0, -1, 1, -1, 0, -1, 1, 0, 1, -1, 1, -1, 0, -1,
  1, 0, 1, 0, 1, -1, 0, -1, 1, 0, 0, 1, 1, -1, 0, -1, 1, 0, 0, 0, -1, 0, 0, -1, 1, 0, 0,
  1, -1, 0, 0, -1, 1, 0, 0, -1, 0, 0, 0, -1, 1, 0, 0, 0, 0, 0, 0, -1, 1, 0, 1, 0, 0, 0,
  0, -1, 1, 0, 0, 1, 0, 0, 0, -1, 1, 0, 0, 0, 1, 0, 0, -1, 1, 0, 1, 0, 1, 0, 0, -1, 1,
  0, 1, 1, 1, 0, 0, -1, 1, 0, -1, 0, -1, 1, 0, -1, 1, 0, 1, 1, -1, 1, 0, -1, 1, 0, 1,
  -1, 0, 1, 0, -1, 1, 0, 0, 0, 0, 1, 0, -1, 1, 0, 0, -1, 1, 1, 0, -1, 1, 0, 1, 0, 1, 1,
  0, -1, 1, 0, 0, 1, 1, 1, 0, -1, 1, 0, 0, -1, 0, -1, 1, -1, 1, 0, 1, 0, 0, -1, 1, -1,
  1, 0, 0, 1, 0, -1, 1, -1, 1, 0, -1, -1, -1, 0, 1, -1, 1, 0, 0, -1, -1, 0, 1, -1, 1, 0,
  1, 0, -1, 0, 1, -1, 1, 0, 0, 0, 0, 0, 1, -1, 1, 0, 1, 0, 0, 0, 1, -1, 1, 0, -1, 1, 0,
  0, 1, -1, 1, 0, -1, -1, 1, 0, 1, -1, 1, 0, 0, 0, -1, 1, 1, -1, 1, 0, 0, -1, 0, 1, 1,
  -1, 1, 0, 1, 0, 0, 1, 1, -1, 1, 0, 0, 0, 1, 1, 1, -1, 1, 0, 0, -1, 0, -1, -1, 0, 1, 0,
  1, -1, 0, -1, -1, 0, 1, 0, 0, 0, 0, -1, -1, 0, 1, 0, 1, 0, 0, -1, -1, 0, 1, 0, 1, 1,
  0, -1, -1, 0, 1, 0, 0, -1, 1, -1, -1, 0, 1, 0, 1, 0, 1, -1, -1, 0, 1, 0, 0, 1, 1, -1,
  -1, 0, 1, 0, -1, -1, -1, 0, -1, 0, 1, 0, 1, -1, -1, 0, -1, 0, 1, 0, 0, 0, -1, 0, -1,
  0, 1, 0, -1, 1, -1, 0, -1, 0, 1, 0, 1, 1, -1, 0, -1, 0, 1, 0, 0, -1, 0, 0, -1, 0, 1,
  0, -1, 0, 0, 0, -1, 0, 1, 0, 0, 0, 0, 0, -1, 0, 1, 0, 1, 0, 0, 0, -1, 0, 1, 0, 0, 1,
  0, 0, -1, 0, 1, 0, 1, 1, 0, 0, -1, 0, 1, 0, -1, -1, 1, 0, -1, 0, 1, 0, 1, -1, 1, 0,
  -1, 0, 1, 0, 0, 0, 1, 0, -1, 0, 1, 0, -1, 0, -1, 1, -1, 0, 1, 0, 0, 0, -1, 1, -1, 0,
  1, 0, 0, 1, -1, 1, -1, 0, 1, 0, -1, -1, 0, 1, -1, 0, 1, 0, 1, -1, 0, 1, -1, 0, 1, 0,
  -1, 0, 0, 1, -1, 0, 1, 0, 0, 0, 0, 1, -1, 0, 1, 0, 1, 0, 0, 1, -1, 0, 1, 0, -1, 1, 0,
  1, -1, 0, 1, 0, 1, 1, 0, 1, -1, 0, 1, 0, 0, -1, 1, 1, -1, 0, 1, 0, -1, 0, 1, 1, -1, 0,
  1, 0, 0, 1, 1, 1, -1, 0, 1, 0, 0, 0, -1, -1, 0, 0, 1, 0, -1, 1, -1, -1, 0, 0, 1, 0, 1,
  1, -1, -1, 0, 0, 1, 0, 0, -1, 0, -1, 0, 0, 1, 0, 0, 0, 0, -1, 0, 0, 1, 0, 1, 0, 0, -1,
  0, 0, 1, 0, 0, 1, 0, -1, 0, 0, 1, 0, -1, 0, -1, 0, 0, 0, 1, 0, 0, 0, -1, 0, 0, 0, 1,
  0, 1, 0, -1, 0, 0, 0, 1, 0, 0, 1, -1, 0, 0, 0, 1, 0, -1, -1, 0, 0, 0, 0, 1, 0, 0, -1,
  0, 0, 0, 0, 1, 0, -1, 0, 0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0, 0, 1, 0, 1, 0, 0, 0, 0, 0,
  1, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, -1, 1, 0, 0, 0, 1, 0, -1, 0, 1, 0, 0, 0, 1, 0, 0, 0,
  1, 0, 0, 0, 1, 0, 1, 0, 1, 0, 0, 0, 1, 0, 0, 1, 1, 0, 0, 0, 1, 0, 1, 0, -1, 1, 0, 0,
  1, 0, 0, 1, -1, 1, 0, 0, 1, 0, 1, 1, -1, 1, 0, 0, 1, 0, 0, -1, 0, 1, 0, 0, 1, 0, 0, 0,
  0, 1, 0, 0, 1, 0, 1, 0, 0, 1, 0, 0, 1, 0, 0, 1, 0, 1, 0, 0, 1, 0, 1, 1, 0, 1, 0, 0, 1,
  0, 1, -1, 1, 1, 0, 0, 1, 0, 0, 0, 1, 1, 0, 0, 1, 0, 1, 0, 1, 1, 0, 0, 1, 0, -1, 1, 1,
  1, 0, 0, 1, 0, 1, -1, -1, -1, 1, 0, 1, 0, 0, 1, -1, -1, 1, 0, 1, 0, 0, 0, 0, -1, 1, 0,
  1, 0, -1, -1, 1, -1, 1, 0, 1, 0, 1, 0, 1, -1, 1, 0, 1, 0, -1, 1, 1, -1, 1, 0, 1, 0, 0,
  1, 1, -1, 1, 0, 1, 0, -1, -1, -1, 0, 1, 0, 1, 0, 0, 0, -1, 0, 1, 0, 1, 0, -1, 1, -1,
  0, 1, 0, 1, 0, 1, 1, -1, 0, 1, 0, 1, 0, 0, -1, 0, 0, 1, 0, 1, 0, -1, 0, 0, 0, 1, 0, 1,
  0, 0, 0, 0, 0, 1, 0, 1, 0, 1, 0, 0, 0, 1, 0, 1, 0, -1, 1, 0, 0, 1, 0, 1, 0, 1, 1, 0,
  0, 1, 0, 1, 0, -1, -1, 1, 0, 1, 0, 1, 0, 0, 0, 1, 0, 1, 0, 1, 0, -1, 1, 1, 0, 1, 0, 1,
  0, -1, -1, -1, 1, 1, 0, 1, 0, 1, -1, -1, 1, 1, 0, 1, 0, 0, 0, -1, 1, 1, 0, 1, 0, 1, 1,
  -1, 1, 1, 0, 1, 0, -1, 0, 0, 1, 1, 0, 1, 0, 1, 0, 0, 1, 1, 0, 1, 0, -1, 1, 0, 1, 1, 0,
  1, 0, 0, 1, 0, 1, 1, 0, 1, 0, -1, -1, 1, 1, 1, 0, 1, 0, -1, 0, 1, 1, 1, 0, 1, 0, 1, 0,
  1, 1, 1, 0, 1, 0, 1, 1, 1, 1, 1, 0, 1, 0, 1, 0, 0, -1, -1, 1, 1, 0, 0, 1, 0, -1, -1,
  1, 1, 0, 0, 0, 1, -1, -1, 1, 1, 0, 0, -1, -1, 0, -1, 1, 1, 0, 1, -1, 0, 0, -1, 1, 1,
  0, 0, 0, 0, 0, -1, 1, 1, 0, 1, 1, 0, 0, -1, 1, 1, 0, 0, -1, 1, 0, -1, 1, 1, 0, 0, 1,
  1, 0, -1, 1, 1, 0, 0, 0, -1, 1, -1, 1, 1, 0, 0, -1, 0, 1, -1, 1, 1, 0, -1, 1, 0, 1,
  -1, 1, 1, 0, 1, 0, 1, 1, -1, 1, 1, 0, 0, -1, -1, -1, 0, 1, 1, 0, -1, 0, -1, -1, 0, 1,
  1, 0, -1, -1, 0, -1, 0, 1, 1, 0, 0, 0, 0, -1, 0, 1, 1, 0, 0, -1, 1, -1, 0, 1, 1, 0,
  -1, 0, 1, -1, 0, 1, 1, 0, 1, 0, 1, -1, 0, 1, 1, 0, 0, 1, 1, -1, 0, 1, 1, 0, -1, -1,
  -1, 0, 0, 1, 1, 0, 0, -1, -1, 0, 0, 1, 1, 0, 0, 0, -1, 0, 0, 1, 1, 0, 1, 0, -1, 0, 0,
  1, 1, 0, -1, 1, -1, 0, 0, 1, 1, 0, 0, -1, 0, 0, 0, 1, 1, 0, -1, 0, 0, 0, 0, 1, 1, 0,
  0, 0, 0, 0, 0, 1, 1, 0, 1, 0, 0, 0, 0, 1, 1, 0, 0, 1, 0, 0, 0, 1, 1, 0, -1, -1, 1, 0,
  0, 1, 1, 0, 0, 0, 1, 0, 0, 1, 1, 0, 1, 1, 1, 0, 0, 1, 1, 0, 1, -1, -1, 1, 0, 1, 1, 0,
  -1, 0, -1, 1, 0, 1, 1, 0, 1, 1, -1, 1, 0, 1, 1, 0, 0, 0, 0, 1, 0, 1, 1, 0, 0, -1, 1,
  1, 0, 1, 1, 0, -1, 0, 1, 1, 0, 1, 1, 0, 0, 0, 1, 1, 0, 1, 1, 0, 0, 1, 1, 1, 0, 1, 1,
  0, 0, -1, 0, -1, 1, 1, 1, 0, 1, 0, 0, -1, 1, 1, 1, 0, -1, 1, 0, -1, 1, 1, 1, 0, 0, -1,
  -1, 0, 1, 1, 1, 0, -1, 0, -1, 0, 1, 1, 1, 0, 0, 1, -1, 0, 1, 1, 1, 0, -1, -1, 0, 0, 1,
  1, 1, 0, 0, 0, 0, 0, 1, 1, 1, 0, -1, 1, 0, 0, 1, 1, 1, 0, 1, 1, 0, 0, 1, 1, 1, 0, -1,
  0, 1, 0, 1, 1, 1, 0, 0, 0, 1, 0, 1, 1, 1, 0, 0, 1, 1, 0, 1, 1, 1, 0, 1, 0, -1, 1, 1,
  1, 1, 0, -1, 0, 0, 1, 1, 1, 1, 0, -1, 1, 0, 1, 1, 1, 1, 0, 1, 1, 0, 1, 1, 1, 1, 0, 1,
  0, 1, 1, 1, 1, 1, 0, -1, -1, -1, -1, -1, -1, -1, 1, 1, -1, -1, -1, -1, -1, -1, 1, -1,
  1, -1, -1, -1, -1, -1, 1, 1, 1, -1, -1, -1, -1, -1, 1, -1, -1, 1, -1, -1, -1, -1, 1,
  1, -1, 1, -1, -1, -1, -1, 1, -1, 1, 1, -1, -1, -1, -1, 1, 1, 1, 1, -1, -1, -1, -1, 1,
  0, 0, -1, 0, -1, -1, -1, 1, -1, -1, 0, 0, -1, -1, -1, 1, 0, -1, 0, 0, -1, -1, -1, 1,
  -1, 0, 0, 0, -1, -1, -1, 1, 1, 0, 0, 0, -1, -1, -1, 1, 0, 1, 0, 0, -1, -1, -1, 1, 0,
  0, 1, 0, -1, -1, -1, 1, -1, -1, -1, 1, -1, -1, -1, 1, 1, -1, -1, 1, -1, -1, -1, 1, -1,
  1, -1, 1, -1, -1, -1, 1, 1, 1, -1, 1, -1, -1, -1, 1, 0, 0, 0, 1, -1, -1, -1, 1, -1,
  -1, 1, 1, -1, -1, -1, 1, 1, -1, 1, 1, -1, -1, -1, 1, -1, 1, 1, 1, -1, -1, -1, 1, 1, 1,
  1, 1, -1, -1, -1, 1, 0, 0, -1, -1, 0, -1, -1, 1, 0, -1, 0, -1, 0, -1, -1, 1, -1, 0, 0,
  -1, 0, -1, -1, 1, 1, 0, 0, -1, 0, -1, -1, 1, 0, 1, 0, -1, 0, -1, -1, 1, 0, 0, 1, -1,
  0, -1, -1, 1, 0, -1, -1, 0, 0, -1, -1, 1, -1, 0, -1, 0, 0, -1, -1, 1, 0, 1, -1, 0, 0,
  -1, -1, 1, -1, -1, 0, 0, 0, -1, -1, 1, 1, -1, 0, 0, 0, -1, -1, 1, 0, 0, 0, 0, 0, -1,
  -1, 1, 1, 0, 0, 0, 0, -1, -1, 1, -1, 1, 0, 0, 0, -1, -1, 1, 0, 1, 0, 0, 0, -1, -1, 1,
  -1, 0, 1, 0, 0, -1, -1, 1, 1, 0, 1, 0, 0, -1, -1, 1, 0, 1, 1, 0, 0, -1, -1, 1, 0, 0,
  -1, 1, 0, -1, -1, 1, 0, 1, -1, 1, 0, -1, -1, 1, -1, 0, 0, 1, 0, -1, -1, 1, 1, 0, 0, 1,
  0, -1, -1, 1, 0, 1, 0, 1, 0, -1, -1, 1, 0, 0, 1, 1, 0, -1, -1, 1, -1, -1, -1, -1, 1,
  -1, -1, 1, 1, -1, -1, -1, 1, -1, -1, 1, -1, 1, -1, -1, 1, -1, -1, 1, 1, 1, -1, -1, 1,
  -1, -1, 1, 0, 0, 0, -1, 1, -1, -1, 1, -1, -1, 1, -1, 1, -1, -1, 1, 1, -1, 1, -1, 1,
  -1, -1, 1, -1, 1, 1, -1, 1, -1, -1, 1, 1, 1, 1, -1, 1, -1, -1, 1, 0, -1, 0, 0, 1, -1,
  -1, 1, -1, 0, 0, 0, 1, -1, -1, 1, 0, 1, 0, 0, 1, -1, -1, 1, 0, 0, 1, 0, 1, -1, -1, 1,
  -1, -1, -1, 1, 1, -1, -1, 1, 1, -1, -1, 1, 1, -1, -1, 1, -1, 1, -1, 1, 1, -1, -1, 1,
  1, 1, -1, 1, 1, -1, -1, 1, 0, 0, 0, 1, 1, -1, -1, 1, -1, -1, 1, 1, 1, -1, -1, 1, 1,
  -1, 1, 1, 1, -1, -1, 1, -1, 1, 1, 1, 1, -1, -1, 1, 1, 1, 1, 1, 1, -1, -1, 1, -1, 0, 0,
  -1, -1, 0, -1, 1, 0, 1, 0, -1, -1, 0, -1, 1, 0, -1, -1, 0, -1, 0, -1, 1, -1, 0, -1, 0,
  -1, 0, -1, 1, 0, -1, 0, 0, -1, 0, -1, 1, 0, 0, 0, 0, -1, 0, -1, 1, 1, 1, 0, 0, -1, 0,
  -1, 1, 0, -1, 1, 0, -1, 0, -1, 1, -1, 0, 1, 0, -1, 0, -1, 1, 0, 1, 1, 0, -1, 0, -1, 1,
  -1, 0, 0, 1, -1, 0, -1, 1, 0, 1, 0, 1, -1, 0, -1, 1, 0, -1, -1, -1, 0, 0, -1, 1, 0, 1,
  -1, -1, 0, 0, -1, 1, 1, -1, 0, -1, 0, 0, -1, 1, 0, 0, 0, -1, 0, 0, -1, 1, 1, 1, 0, -1,
  0, 0, -1, 1, 1, 0, 1, -1, 0, 0, -1, 1, 0, 1, 1, -1, 0, 0, -1, 1, -1, -1, -1, 0, 0, 0,
  -1, 1, 0, -1, -1, 0, 0, 0, -1, 1, 0, 0, -1, 0, 0, 0, -1, 1, -1, 1, -1, 0, 0, 0, -1, 1,
  0, -1, 0, 0, 0, 0, -1, 1, -1, 0, 0, 0, 0, 0, -1, 1, 0, 0, 0, 0, 0, 0, -1, 1, 1, 0, 0,
  0, 0, 0, -1, 1, 0, 1, 0, 0, 0, 0, -1, 1, 1, 1, 0, 0, 0, 0, -1, 1, 0, 0, 1, 0, 0, 0,
  -1, 1, 1, 0, 1, 0, 0, 0, -1, 1, -1, 1, 1, 0, 0, 0, -1, 1, 1, 1, 1, 0, 0, 0, -1, 1, 0,
  -1, -1, 1, 0, 0, -1, 1, -1, 0, -1, 1, 0, 0, -1, 1, 1, 0, -1, 1, 0, 0, -1, 1, 0, 1, -1,
  1, 0, 0, -1, 1, -1, -1, 0, 1, 0, 0, -1, 1, 1, -1, 0, 1, 0, 0, -1, 1, 0, 0, 0, 1, 0, 0,
  -1, 1, -1, 1, 0, 1, 0, 0, -1, 1, 1, 0, 1, 1, 0, 0, -1, 1, 0, -1, 0, -1, 1, 0, -1, 1,
  1, 0, 0, -1, 1, 0, -1, 1, 0, 1, 0, -1, 1, 0, -1, 1, 0, 0, 1, -1, 1, 0, -1, 1, 0, -1,
  -1, 0, 1, 0, -1, 1, -1, 0, -1, 0, 1, 0, -1, 1, 0, 1, -1, 0, 1, 0, -1, 1, 1, 1, -1, 0,
  1, 0, -1, 1, -1, -1, 0, 0, 1, 0, -1, 1, 0, 0, 0, 0, 1, 0, -1, 1, 0, 1, 0, 0, 1, 0, -1,
  1, 1, 1, 0, 0, 1, 0, -1, 1, 0, -1, 1, 0, 1, 0, -1, 1, 1, 0, 1, 0, 1, 0, -1, 1, 1, 1,
  1, 0, 1, 0, -1, 1, 0, 0, -1, 1, 1, 0, -1, 1, 0, -1, 0, 1, 1, 0, -1, 1, 1, 1, 0, 1, 1,
  0, -1, 1, -1, 0, 1, 1, 1, 0, -1, 1, -1, -1, -1, -1, -1, 1, -1, 1, 1, -1, -1, -1, -1,
  1, -1, 1, -1, 1, -1, -1, -1, 1, -1, 1, 1, 1, -1, -1, -1, 1, -1, 1, 0, 0, 0, -1, -1, 1,
  -1, 1, -1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, -1, -1, 1, -1, 1, -1, 1, 1, -1, -1, 1,
  -1, 1, 1, 1, 1, -1, -1, 1, -1, 1, 0, -1, -1, 0, -1, 1, -1, 1, 0, 0, -1, 0, -1, 1, -1,
  1, 0, -1, 0, 0, -1, 1, -1, 1, -1, 0, 0, 0, -1, 1, -1, 1, 0, 1, 0, 0, -1, 1, -1, 1, 0,
  0, 1, 0, -1, 1, -1, 1, 0, 1, 1, 0, -1, 1, -1, 1, -1, -1, -1, 1, -1, 1, -1, 1, 1, -1,
  -1, 1, -1, 1, -1, 1, -1, 1, -1, 1, -1, 1, -1, 1, 1, 1, -1, 1, -1, 1, -1, 1, 0, 0, 0,
  1, -1, 1, -1, 1, -1, -1, 1, 1, -1, 1, -1, 1, 1, -1, 1, 1, -1, 1, -1, 1, -1, 1, 1, 1,
  -1, 1, -1, 1, 1, 1, 1, 1, -1, 1, -1, 1, 0, 0, -1, -1, 0, 1, -1, 1, 1, 0, -1, -1, 0, 1,
  -1, 1, 0, -1, 0, -1, 0, 1, -1, 1, -1, 0, 0, -1, 0, 1, -1, 1, 1, 0, 0, -1, 0, 1, -1, 1,
  0, 0, 1, -1, 0, 1, -1, 1, 0, -1, -1, 0, 0, 1, -1, 1, -1, 0, -1, 0, 0, 1, -1, 1, 1, 0,
  -1, 0, 0, 1, -1, 1, 0, 1, -1, 0, 0, 1, -1, 1, -1, -1, 0, 0, 0, 1, -1, 1, 1, -1, 0, 0,
  0, 1, -1, 1, 0, 0, 0, 0, 0, 1, -1, 1, 1, 1, 0, 0, 0, 1, -1, 1, 0, -1, 1, 0, 0, 1, -1,
  1, -1, 0, 1, 0, 0, 1, -1, 1, 0, 0, -1, 1, 0, 1, -1, 1, 1, 0, 0, 1, 0, 1, -1, 1, 0, 1,
  0, 1, 0, 1, -1, 1, 0, 0, 1, 1, 0, 1, -1, 1, -1, -1, -1, -1, 1, 1, -1, 1, 1, -1, -1,
  -1, 1, 1, -1, 1, -1, 1, -1, -1, 1, 1, -1, 1, 1, 1, -1, -1, 1, 1, -1, 1, 0, 0, 0, -1,
  1, 1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, 1, -1, 1, -1, 1, 1, -1, 1, -1, 1, 1, -1, 1,
  1, -1, 1, 1, 1, 1, -1, 1, 1, -1, 1, 0, 0, -1, 0, 1, 1, -1, 1, 0, -1, 0, 0, 1, 1, -1,
  1, -1, 0, 0, 0, 1, 1, -1, 1, 1, 0, 0, 0, 1, 1, -1, 1, -1, -1, -1, 1, 1, 1, -1, 1, 1,
  -1, -1, 1, 1, 1, -1, 1, -1, 1, -1, 1, 1, 1, -1, 1, 1, 1, -1, 1, 1, 1, -1, 1, 0, 0, 0,
  1, 1, 1, -1, 1, -1, -1, 1, 1, 1, 1, -1, 1, 1, -1, 1, 1, 1, 1, -1, 1, -1, 1, 1, 1, 1,
  1, -1, 1, 1, 1, 1, 1, 1, 1, -1, 1, 0, 0, -1, -1, -1, -1, 0, 1, 0, -1, 0, -1, -1, -1,
  0, 1, 1, 0, 0, -1, -1, -1, 0, 1, -1, 1, 0, -1, -1, -1, 0, 1, 0, 1, 0, -1, -1, -1, 0,
  1, 0, 0, 1, -1, -1, -1, 0, 1, 0, -1, -1, 0, -1, -1, 0, 1, 1, 0, -1, 0, -1, -1, 0, 1,
  0, 1, -1, 0, -1, -1, 0, 1, 0, 0, 0, 0, -1, -1, 0, 1, -1, 1, 0, 0, -1, -1, 0, 1, 1, 1,
  0, 0, -1, -1, 0, 1, 0, 1, 1, 0, -1, -1, 0, 1, 1, 1, 1, 0, -1, -1, 0, 1, 0, 0, -1, 1,
  -1, -1, 0, 1, 0, -1, 0, 1, -1, -1, 0, 1, -1, 0, 0, 1, -1, -1, 0, 1, 1, 0, 0, 1, -1,
  -1, 0, 1, 0, 1, 0, 1, -1, -1, 0, 1, 0, 0, 1, 1, -1, -1, 0, 1, 0, -1, -1, -1, 0, -1, 0,
  1, -1, 0, -1, -1, 0, -1, 0, 1, 1, 0, -1, -1, 0, -1, 0, 1, 0, 1, -1, -1, 0, -1, 0, 1,
  -1, -1, 0, -1, 0, -1, 0, 1, 0, 0, 0, -1, 0, -1, 0, 1, -1, 1, 0, -1, 0, -1, 0, 1, 1, 1,
  0, -1, 0, -1, 0, 1, 0, -1, 1, -1, 0, -1, 0, 1, -1, 0, 1, -1, 0, -1, 0, 1, 1, 0, 1, -1,
  0, -1, 0, 1, 0, 1, 1, -1, 0, -1, 0, 1, -1, -1, -1, 0, 0, -1, 0, 1, 0, 0, -1, 0, 0, -1,
  0, 1, -1, -1, 0, 0, 0, -1, 0, 1, 0, -1, 0, 0, 0, -1, 0, 1, -1, 0, 0, 0, 0, -1, 0, 1,
  0, 0, 0, 0, 0, -1, 0, 1, 1, 0, 0, 0, 0, -1, 0, 1, 0, 1, 0, 0, 0, -1, 0, 1, 1, -1, 1,
  0, 0, -1, 0, 1, 0, 0, 1, 0, 0, -1, 0, 1, -1, 0, -1, 1, 0, -1, 0, 1, 1, 0, -1, 1, 0,
  -1, 0, 1, 1, -1, 0, 1, 0, -1, 0, 1, 0, 0, 0, 1, 0, -1, 0, 1, -1, 1, 0, 1, 0, -1, 0, 1,
  0, -1, 1, 1, 0, -1, 0, 1, -1, 0, 1, 1, 0, -1, 0, 1, 1, 0, 1, 1, 0, -1, 0, 1, 0, 1, 1,
  1, 0, -1, 0, 1, 0, 0, -1, -1, 1, -1, 0, 1, 0, -1, 0, -1, 1, -1, 0, 1, -1, 0, 0, -1, 1,
  -1, 0, 1, 0, 1, 0, -1, 1, -1, 0, 1, 0, 0, 1, -1, 1, -1, 0, 1, -1, 0, -1, 0, 1, -1, 0,
  1, 1, 0, -1, 0, 1, -1, 0, 1, 0, 1, -1, 0, 1, -1, 0, 1, -1, -1, 0, 0, 1, -1, 0, 1, 1,
  -1, 0, 0, 1, -1, 0, 1, 0, 0, 0, 0, 1, -1, 0, 1, -1, 1, 0, 0, 1, -1, 0, 1, 1, 0, 1, 0,
  1, -1, 0, 1, 0, 1, 1, 0, 1, -1, 0, 1, 0, 0, -1, 1, 1, -1, 0, 1, -1, 0, 0, 1, 1, -1, 0,
  1, 1, 0, 0, 1, 1, -1, 0, 1, 0, 1, 1, 1, 1, -1, 0, 1, 0, -1, -1, -1, -1, 0, 0, 1, -1,
  0, -1, -1, -1, 0, 0, 1, 1, 0, -1, -1, -1, 0, 0, 1, -1, -1, 0, -1, -1, 0, 0, 1, 0, 0,
  0, -1, -1, 0, 0, 1, -1, 1, 0, -1, -1, 0, 0, 1, 1, 0, 1, -1, -1, 0, 0, 1, -1, -1, -1,
  0, -1, 0, 0, 1, 1, 1, -1, 0, -1, 0, 0, 1, 0, -1, 0, 0, -1, 0, 0, 1, -1, 0, 0, 0, -1,
  0, 0, 1, 0, 0, 0, 0, -1, 0, 0, 1, 1, 0, 0, 0, -1, 0, 0, 1, -1, 1, 0, 0, -1, 0, 0, 1,
  0, 1, 0, 0, -1, 0, 0, 1, -1, -1, 1, 0, -1, 0, 0, 1, 0, -1, 1, 0, -1, 0, 0, 1, 1, -1,
  1, 0, -1, 0, 0, 1, 0, 0, 1, 0, -1, 0, 0, 1, -1, 0, -1, 1, -1, 0, 0, 1, 1, 0, -1, 1,
  -1, 0, 0, 1, 1, -1, 0, 1, -1, 0, 0, 1, -1, 0, 0, 1, -1, 0, 0, 1, 0, 0, 0, 1, -1, 0, 0,
  1, -1, 1, 0, 1, -1, 0, 0, 1, 0, -1, 1, 1, -1, 0, 0, 1, 0, 1, 1, 1, -1, 0, 0, 1, -1,
  -1, -1, -1, 0, 0, 0, 1, 0, 0, -1, -1, 0, 0, 0, 1, -1, 1, -1, -1, 0, 0, 0, 1, 1, 1, -1,
  -1, 0, 0, 0, 1, -1, -1, 0, -1, 0, 0, 0, 1, 0, -1, 0, -1, 0, 0, 0, 1, -1, 0, 0, -1, 0,
  0, 0, 1, 0, 0, 0, -1, 0, 0, 0, 1, 1, 0, 0, -1, 0, 0, 0, 1, 0, 1, 0, -1, 0, 0, 0, 1, 0,
  -1, 1, -1, 0, 0, 0, 1, 0, 0, 1, -1, 0, 0, 0, 1, 0, 1, 1, -1, 0, 0, 0, 1, 1, 1, 1, -1,
  0, 0, 0, 1, 0, -1, -1, 0, 0, 0, 0, 1, -1, 0, -1, 0, 0, 0, 0, 1, 0, 0, -1, 0, 0, 0, 0,
  1, 1, 0, -1, 0, 0, 0, 0, 1, 0, 1, -1, 0, 0, 0, 0, 1, -1, -1, 0, 0, 0, 0, 0, 1, 0, -1,
  0, 0, 0, 0, 0, 1, 1, -1, 0, 0, 0, 0, 0, 1, -1, 0, 0, 0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0,
  0, 1, 1, 0, 0, 0, 0, 0, 0, 1, -1, 1, 0, 0, 0, 0, 0, 1, 0, 1, 0, 0, 0, 0, 0, 1, 1, 1,
  0, 0, 0, 0, 0, 1, 0, -1, 1, 0, 0, 0, 0, 1, -1, 0, 1, 0, 0, 0, 0, 1, 0, 0, 1, 0, 0, 0,
  0, 1, 1, 0, 1, 0, 0, 0, 0, 1, 0, 1, 1, 0, 0, 0, 0, 1, 0, -1, -1, 1, 0, 0, 0, 1, 0, 0,
  -1, 1, 0, 0, 0, 1, -1, 1, -1, 1, 0, 0, 0, 1, 0, -1, 0, 1, 0, 0, 0, 1, 1, -1, 0, 1, 0,
  0, 0, 1, -1, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 1, 1, 0, 0, 1, 0, 0, 0, 1, 0,
  1, 0, 1, 0, 0, 0, 1, 1, 1, 0, 1, 0, 0, 0, 1, -1, -1, 1, 1, 0, 0, 0, 1, 1, -1, 1, 1, 0,
  0, 0, 1, 0, 0, 1, 1, 0, 0, 0, 1, -1, 1, 1, 1, 0, 0, 0, 1, 1, 1, 1, 1, 0, 0, 0, 1, 0,
  -1, -1, -1, 1, 0, 0, 1, -1, 0, -1, -1, 1, 0, 0, 1, -1, -1, 0, -1, 1, 0, 0, 1, 0, 0, 0,
  -1, 1, 0, 0, 1, 0, 1, 0, -1, 1, 0, 0, 1, -1, -1, 1, -1, 1, 0, 0, 1, 1, 0, 1, -1, 1, 0,
  0, 1, 0, 1, 1, -1, 1, 0, 0, 1, 0, 0, -1, 0, 1, 0, 0, 1, -1, 1, -1, 0, 1, 0, 0, 1, 0,
  1, -1, 0, 1, 0, 0, 1, 0, -1, 0, 0, 1, 0, 0, 1, 1, -1, 0, 0, 1, 0, 0, 1, 0, 0, 0, 0, 1,
  0, 0, 1, 1, 0, 0, 0, 1, 0, 0, 1, 0, 1, 0, 0, 1, 0, 0, 1, 0, 0, 1, 0, 1, 0, 0, 1, -1,
  1, 1, 0, 1, 0, 0, 1, 1, -1, -1, 1, 1, 0, 0, 1, -1, 0, -1, 1, 1, 0, 0, 1, 0, 1, -1, 1,
  1, 0, 0, 1, 1, 1, -1, 1, 1, 0, 0, 1, 1, -1, 0, 1, 1, 0, 0, 1, -1, 0, 0, 1, 1, 0, 0, 1,
  0, 0, 0, 1, 1, 0, 0, 1, -1, 0, 1, 1, 1, 0, 0, 1, 1, 0, 1, 1, 1, 0, 0, 1, 0, 1, 1, 1,
  1, 0, 0, 1, 0, 0, -1, -1, -1, 1, 0, 1, 1, 0, 0, -1, -1, 1, 0, 1, 0, 1, 0, -1, -1, 1,
  0, 1, 0, 0, 1, -1, -1, 1, 0, 1, 0, -1, -1, 0, -1, 1, 0, 1, 1, 0, -1, 0, -1, 1, 0, 1,
  -1, -1, 0, 0, -1, 1, 0, 1, 1, -1, 0, 0, -1, 1, 0, 1, 0, 0, 0, 0, -1, 1, 0, 1, 1, 0, 0,
  0, -1, 1, 0, 1, 1, 1, 0, 0, -1, 1, 0, 1, -1, 0, 1, 0, -1, 1, 0, 1, 0, 0, 1, 0, -1, 1,
  0, 1, 0, 0, -1, 1, -1, 1, 0, 1, 0, -1, 0, 1, -1, 1, 0, 1, 1, 0, 0, 1, -1, 1, 0, 1, 0,
  1, 0, 1, -1, 1, 0, 1, 0, 0, 1, 1, -1, 1, 0, 1, -1, 0, -1, -1, 0, 1, 0, 1, 1, 0, -1,
  -1, 0, 1, 0, 1, 0, 1, -1, -1, 0, 1, 0, 1, -1, -1, 0, -1, 0, 1, 0, 1, 1, -1, 0, -1, 0,
  1, 0, 1, 0, 0, 0, -1, 0, 1, 0, 1, -1, 1, 0, -1, 0, 1, 0, 1, 1, 1, 0, -1, 0, 1, 0, 1,
  -1, -1, 1, -1, 0, 1, 0, 1, 0, -1, 1, -1, 0, 1, 0, 1, -1, 0, 1, -1, 0, 1, 0, 1, 1, 0,
  1, -1, 0, 1, 0, 1, -1, -1, -1, 0, 0, 1, 0, 1, 1, -1, -1, 0, 0, 1, 0, 1, 0, 0, -1, 0,
  0, 1, 0, 1, -1, 1, -1, 0, 0, 1, 0, 1, 1, 1, -1, 0, 0, 1, 0, 1, 0, -1, 0, 0, 0, 1, 0,
  1, -1, 0, 0, 0, 0, 1, 0, 1, 0, 0, 0, 0, 0, 1, 0, 1, 1, 0, 0, 0, 0, 1, 0, 1, 0, 1, 0,
  0, 0, 1, 0, 1, 1, -1, 1, 0, 0, 1, 0, 1, 0, 0, 1, 0, 0, 1, 0, 1, 1, 0, 1, 0, 0, 1, 0,
  1, 1, 1, 1, 0, 0, 1, 0, 1, 0, -1, -1, 1, 0, 1, 0, 1, -1, 0, -1, 1, 0, 1, 0, 1, -1, -1,
  0, 1, 0, 1, 0, 1, 1, -1, 0, 1, 0, 1, 0, 1, 0, 0, 0, 1, 0, 1, 0, 1, 1, 1, 0, 1, 0, 1,
  0, 1, 0, -1, 1, 1, 0, 1, 0, 1, 1, 0, 1, 1, 0, 1, 0, 1, 0, 0, -1, -1, 1, 1, 0, 1, 0, 0,
  0, -1, 1, 1, 0, 1, 0, 0, 1, -1, 1, 1, 0, 1, -1, 0, -1, 0, 1, 1, 0, 1, 1, 0, -1, 0, 1,
  1, 0, 1, 0, 1, -1, 0, 1, 1, 0, 1, -1, -1, 0, 0, 1, 1, 0, 1, 0, 0, 0, 0, 1, 1, 0, 1,
  -1, 1, 0, 0, 1, 1, 0, 1, 0, -1, 1, 0, 1, 1, 0, 1, 0, 0, -1, 1, 1, 1, 0, 1, 0, -1, 0,
  1, 1, 1, 0, 1, -1, 0, 0, 1, 1, 1, 0, 1, 0, 0, 0, 1, 1, 1, 0, 1, 1, 0, 0, 1, 1, 1, 0,
  1, -1, -1, -1, -1, -1, -1, 1, 1, 1, -1, -1, -1, -1, -1, 1, 1, -1, 1, -1, -1, -1, -1,
  1, 1, 1, 1, -1, -1, -1, -1, 1, 1, 0, 0, 0, -1, -1, -1, 1, 1, -1, -1, 1, -1, -1, -1, 1,
  1, 1, -1, 1, -1, -1, -1, 1, 1, -1, 1, 1, -1, -1, -1, 1, 1, 1, 1, 1, -1, -1, -1, 1, 1,
  0, 0, -1, 0, -1, -1, 1, 1, 0, -1, 0, 0, -1, -1, 1, 1, -1, 0, 0, 0, -1, -1, 1, 1, 1, 0,
  0, 0, -1, -1, 1, 1, 0, 1, 0, 0, -1, -1, 1, 1, -1, -1, -1, 1, -1, -1, 1, 1, 1, -1, -1,
  1, -1, -1, 1, 1, -1, 1, -1, 1, -1, -1, 1, 1, 1, 1, -1, 1, -1, -1, 1, 1, 0, 0, 0, 1,
  -1, -1, 1, 1, -1, -1, 1, 1, -1, -1, 1, 1, 1, -1, 1, 1, -1, -1, 1, 1, -1, 1, 1, 1, -1,
  -1, 1, 1, 1, 1, 1, 1, -1, -1, 1, 1, 0, 0, -1, -1, 0, -1, 1, 1, 0, 1, -1, -1, 0, -1, 1,
  1, 0, -1, 0, -1, 0, -1, 1, 1, -1, 0, 0, -1, 0, -1, 1, 1, 1, 0, 0, -1, 0, -1, 1, 1, 0,
  1, 0, -1, 0, -1, 1, 1, 1, 1, 0, -1, 0, -1, 1, 1, 1, 0, -1, 0, 0, -1, 1, 1, 0, 1, -1,
  0, 0, -1, 1, 1, 0, -1, 0, 0, 0, -1, 1, 1, 0, 0, 0, 0, 0, -1, 1, 1, -1, 1, 0, 0, 0, -1,
  1, 1, 1, 1, 0, 0, 0, -1, 1, 1, 0, -1, 1, 0, 0, -1, 1, 1, -1, 0, 1, 0, 0, -1, 1, 1, 0,
  0, -1, 1, 0, -1, 1, 1, -1, -1, 0, 1, 0, -1, 1, 1, 1, -1, 0, 1, 0, -1, 1, 1, 1, 0, 0,
  1, 0, -1, 1, 1, 0, 1, 0, 1, 0, -1, 1, 1, 1, -1, -1, -1, 1, -1, 1, 1, -1, 1, -1, -1, 1,
  -1, 1, 1, 1, 1, -1, -1, 1, -1, 1, 1, -1, -1, 0, -1, 1, -1, 1, 1, 0, 1, 0, -1, 1, -1,
  1, 1, 1, -1, 1, -1, 1, -1, 1, 1, -1, 1, 1, -1, 1, -1, 1, 1, 1, 1, 1, -1, 1, -1, 1, 1,
  0, 0, -1, 0, 1, -1, 1, 1, 0, -1, 0, 0, 1, -1, 1, 1, 1, 0, 0, 0, 1, -1, 1, 1, 0, 1, 0,
  0, 1, -1, 1, 1, 0, 0, 1, 0, 1, -1, 1, 1, -1, -1, -1, 1, 1, -1, 1, 1, 1, -1, -1, 1, 1,
  -1, 1, 1, -1, 1, -1, 1, 1, -1, 1, 1, 1, 1, -1, 1, 1, -1, 1, 1, 0, 0, 0, 1, 1, -1, 1,
  1, -1, -1, 1, 1, 1, -1, 1, 1, 1, -1, 1, 1, 1, -1, 1, 1, -1, 1, 1, 1, 1, -1, 1, 1, 1,
  1, 1, 1, 1, -1, 1, 1, 0, 1, 0, -1, -1, 0, 1, 1, 0, 0, 1, -1, -1, 0, 1, 1, 0, -1, -1,
  0, -1, 0, 1, 1, -1, 0, -1, 0, -1, 0, 1, 1, -1, -1, 0, 0, -1, 0, 1, 1, -1, 0, 0, 0, -1,
  0, 1, 1, 0, 0, 0, 0, -1, 0, 1, 1, -1, 1, 0, 0, -1, 0, 1, 1, 1, 1, 0, 0, -1, 0, 1, 1,
  0, -1, 1, 0, -1, 0, 1, 1, 0, 0, 1, 0, -1, 0, 1, 1, 1, 0, 1, 0, -1, 0, 1, 1, -1, 1, 1,
  0, -1, 0, 1, 1, 0, 1, 1, 0, -1, 0, 1, 1, 0, 0, -1, 1, -1, 0, 1, 1, 1, 0, -1, -1, 0, 0,
  1, 1, 0, 1, -1, -1, 0, 0, 1, 1, -1, -1, 0, -1, 0, 0, 1, 1, 1, -1, 0, -1, 0, 0, 1, 1,
  0, 0, 0, -1, 0, 0, 1, 1, -1, 1, 0, -1, 0, 0, 1, 1, 1, 0, 1, -1, 0, 0, 1, 1, 0, 1, 1,
  -1, 0, 0, 1, 1, 1, -1, -1, 0, 0, 0, 1, 1, 0, 0, -1, 0, 0, 0, 1, 1, 0, -1, 0, 0, 0, 0,
  1, 1, -1, 0, 0, 0, 0, 0, 1, 1, 0, 0, 0, 0, 0, 0, 1, 1, 1, 0, 0, 0, 0, 0, 1, 1, 0, 1,
  0, 0, 0, 0, 1, 1, 0, 0, 1, 0, 0, 0, 1, 1, 1, 1, 1, 0, 0, 0, 1, 1, 0, -1, -1, 1, 0, 0,
  1, 1, -1, 0, -1, 1, 0, 0, 1, 1, 0, 0, -1, 1, 0, 0, 1, 1, 1, 0, -1, 1, 0, 0, 1, 1, 0,
  1, -1, 1, 0, 0, 1, 1, 1, -1, 0, 1, 0, 0, 1, 1, 0, 0, 0, 1, 0, 0, 1, 1, -1, 1, 0, 1, 0,
  0, 1, 1, 0, 0, -1, -1, 1, 0, 1, 1, 0, -1, 0, -1, 1, 0, 1, 1, 1, 0, 0, -1, 1, 0, 1, 1,
  1, 1, 0, -1, 1, 0, 1, 1, 0, -1, 1, -1, 1, 0, 1, 1, 0, 0, 1, -1, 1, 0, 1, 1, -1, 0, -1,
  0, 1, 0, 1, 1, 1, 0, -1, 0, 1, 0, 1, 1, 1, 1, -1, 0, 1, 0, 1, 1, 1, -1, 0, 0, 1, 0, 1,
  1, 0, 0, 0, 0, 1, 0, 1, 1, 1, 0, 0, 0, 1, 0, 1, 1, -1, 1, 0, 0, 1, 0, 1, 1, -1, -1, 1,
  0, 1, 0, 1, 1, 1, -1, 1, 0, 1, 0, 1, 1, 1, 0, -1, 1, 1, 0, 1, 1, -1, -1, 0, 1, 1, 0,
  1, 1, 0, 0, 0, 1, 1, 0, 1, 1, 1, 0, 0, 1, 1, 0, 1, 1, 0, 1, 0, 1, 1, 0, 1, 1, 0, -1,
  1, 1, 1, 0, 1, 1, -1, 0, 1, 1, 1, 0, 1, 1, 1, 0, 1, 1, 1, 0, 1, 1, -1, -1, -1, -1, -1,
  1, 1, 1, 1, -1, -1, -1, -1, 1, 1, 1, -1, 1, -1, -1, -1, 1, 1, 1, 1, 1, -1, -1, -1, 1,
  1, 1, -1, -1, 1, -1, -1, 1, 1, 1, 1, -1, 1, -1, -1, 1, 1, 1, -1, 1, 1, -1, -1, 1, 1,
  1, 1, 1, 1, -1, -1, 1, 1, 1, 0, -1, 0, 0, -1, 1, 1, 1, -1, 0, 0, 0, -1, 1, 1, 1, 1, 0,
  0, 0, -1, 1, 1, 1, 0, 1, 0, 0, -1, 1, 1, 1, -1, -1, -1, 1, -1, 1, 1, 1, 1, -1, -1, 1,
  -1, 1, 1, 1, -1, 1, -1, 1, -1, 1, 1, 1, 1, 1, -1, 1, -1, 1, 1, 1, 0, 0, 0, 1, -1, 1,
  1, 1, -1, -1, 1, 1, -1, 1, 1, 1, 1, -1, 1, 1, -1, 1, 1, 1, -1, 1, 1, 1, -1, 1, 1, 1,
  1, 1, 1, 1, -1, 1, 1, 1, 0, 0, -1, -1, 0, 1, 1, 1, -1, 0, 0, -1, 0, 1, 1, 1, 0, 1, 0,
  -1, 0, 1, 1, 1, 0, -1, 1, -1, 0, 1, 1, 1, 0, 0, 1, -1, 0, 1, 1, 1, 0, -1, -1, 0, 0, 1,
  1, 1, -1, -1, 0, 0, 0, 1, 1, 1, 0, 0, 0, 0, 0, 1, 1, 1, 1, 1, 0, 0, 0, 1, 1, 1, 0, -1,
  1, 0, 0, 1, 1, 1, 1, 0, 1, 0, 0, 1, 1, 1, 0, 1, 1, 0, 0, 1, 1, 1, -1, -1, 0, 1, 0, 1,
  1, 1, 1, 0, 0, 1, 0, 1, 1, 1, -1, -1, -1, -1, 1, 1, 1, 1, 1, -1, -1, -1, 1, 1, 1, 1,
  -1, 1, -1, -1, 1, 1, 1, 1, 1, 1, -1, -1, 1, 1, 1, 1, -1, -1, 1, -1, 1, 1, 1, 1, 1, -1,
  1, -1, 1, 1, 1, 1, -1, 1, 1, -1, 1, 1, 1, 1, 1, 1, 1, -1, 1, 1, 1, 1, 0, -1, 0, 0, 1,
  1, 1, 1, -1, 0, 0, 0, 1, 1, 1, 1, 1, 0, 0, 0, 1, 1, 1, 1, -1, -1, -1, 1, 1, 1, 1, 1,
  1, -1, -1, 1, 1, 1, 1, 1, -1, 1, -1, 1, 1, 1, 1, 1, 1, 1, -1, 1, 1, 1, 1, 1, 0, 0, 0,
  1, 1, 1, 1, 1, -1, -1, 1, 1, 1, 1, 1, 1, 1, -1, 1, 1, 1, 1, 1, 1, -1, 1, 1, 1, 1, 1,
  1, 1, 1, 1, 1, 1, 1, 1, 1, 1,
]);

/**
 * `IQ1S_DELTA` — ggml's `0.125f`, added to every codebook value before scaling.
 *
 * Its sign comes from the sub-block's high bit, so a ternary grid yields six
 * distinct magnitudes rather than three: `dl x {-1.125, -0.875, 0.125}` or
 * `dl x {-0.875, 0.125, 1.125}` depending on that bit.
 */
const IQ1S_DELTA = 0.125;

/**
 * `block_iq2_xxs { ggml_half d; uint16_t qs[32]; }` — 66 bytes, 256 elements.
 *
 * The same shape as `decodeIQ3_XXS` with three differences, each of which is a
 * place to get it wrong:
 *
 * 1. **A grid entry is eight bytes, not four**, so one index supplies all eight
 *    outputs of a step rather than two indices supplying four each.
 * 2. **The scale factor is `0.25`, not `0.5`.** Same four-bit field in the same
 *    position, halved again.
 * 3. **The indices share a word with nothing.** IQ3_XXS keeps its indices in a
 *    separate 64-byte region; here each 8-byte group is `[four index bytes]`
 *    then `[one packed sign/scale word]`, interleaved per sub-block.
 *
 * ggml reads that group as `uint32 aux32[2]` and then aliases `aux32[0]` back to
 * bytes. Reading the four index bytes directly is the same four bytes on any
 * little-endian host and needs no alias, so that is what this does — the
 * endianness question is settled once, at the grid, rather than twice.
 */
const decodeIQ2_XXS: DecodeBlock = (view, at, out, base) => {
  const d = half(view, at);
  const qsAt = at + 2;
  for (let ib32 = 0; ib32 < QK_K / 32; ib32++) {
    // Eight bytes per sub-block: four index bytes, then the sign/scale word.
    const group = qsAt + 8 * ib32;
    const packed = view.getUint32(group + 4, true);
    /*
     * `0.25`, where IQ3_XXS uses `0.5`. Both roundings kept for the reason given
     * on `decodeIQ3_XXS`: ggml holds `db` in a C `float`.
     *
     * `>>> 28`, not `>> 28` — a signed shift on a word with its top bit set
     * turns the largest scale into a negative one.
     */
    const db = f32(f32(d * (0.5 + (packed >>> 28))) * 0.25);
    for (let l = 0; l < 4; l++) {
      const signs = KSIGNS_IQ2XS[(packed >>> (7 * l)) & 127] as number;
      // One index, eight outputs — the whole difference from IQ3_XXS's pairing.
      const g = (view.getUint8(group + l) as number) * 8;
      const dst = base + ib32 * 32 + l * 8;
      for (let j = 0; j < 8; j++) {
        const s = signs & (KMASK_IQ2XS[j] as number) ? -1 : 1;
        out[dst + j] = f32(db * (IQ2XXS_GRID[g + j] as number)) * s;
      }
    }
  }
};

/**
 * `block_iq1_s { ggml_half d; uint8_t qs[32]; uint16_t qh[8]; }` — 50 bytes, 256
 * elements, 1.5625 bits per weight.
 *
 * Three departures from the IQ2/IQ3 pair, and each is a distinct way to be
 * wrong:
 *
 * 1. **The grid is signed.** Read through an `int8_t *`, so `IQ1S_GRID` is an
 *    `Int8Array`. Reading it unsigned would turn every -1 into 255.
 * 2. **The index is eleven bits from two places** — eight from `qs[l]` and three
 *    more from `qh[ib]`, shifted up. Neither field alone selects a grid entry,
 *    and 2048 is exactly what eleven bits address.
 * 3. **A `delta` is added to the codebook value before scaling**, with its sign
 *    taken from the sub-block's top bit. So the ternary grid does not decode to
 *    three values but to six, and never to zero.
 *
 * The scale is three bits, `2k+1` for k in 0..7 — odd multiples only, so the
 * smallest is `d` itself rather than zero.
 */
const decodeIQ1_S: DecodeBlock = (view, at, out, base) => {
  const d = half(view, at);
  const qsAt = at + 2;
  const qhAt = qsAt + QK_K / 8;
  for (let ib = 0; ib < QK_K / 32; ib++) {
    const qh = view.getUint16(qhAt + 2 * ib, true);
    // `2k+1`, never zero: the scale field cannot silence a sub-block.
    const dl = f32(d * (2 * ((qh >> 12) & 7) + 1));
    /*
     * The sign of the offset, from the block's top bit — not a sign applied to
     * the weights. It shifts the whole ternary codebook off centre in one
     * direction or the other, which is what lets a 1-bit format represent an
     * asymmetric distribution at all.
     */
    const delta = qh & 0x8000 ? -IQ1S_DELTA : IQ1S_DELTA;
    for (let l = 0; l < 4; l++) {
      // Eleven bits: eight low from `qs`, three high from `qh`.
      const idx =
        ((view.getUint8(qsAt + 4 * ib + l) as number) | (((qh >> (3 * l)) & 7) << 8)) *
        8;
      const dst = base + ib * 32 + l * 8;
      for (let j = 0; j < 8; j++) {
        /*
         * `grid + delta` rounds before the scale multiplies it: ggml adds an
         * `int8` promoted to `float` to a `float` constant, then multiplies.
         * Two roundings, in that order.
         */
        out[dst + j] = dl * f32((IQ1S_GRID[idx + j] as number) + delta);
      }
    }
  }
};

/**
 * `block_iq2_s { ggml_half d; uint8_t qs[64]; uint8_t qh[8]; uint8_t scales[8]; }`
 * — 82 bytes, 256 elements, 2.5625 bits per weight.
 *
 * **The sign bytes live in the upper half of `qs`**, which the struct does not
 * hint at: ggml sets `signs = qs + QK_K/8`, so the field declared as 64 index
 * bytes is really 32 indices followed by 32 sign bytes. Reading `qs` as 64
 * indices would decode the second half of every block from sign data and look
 * like noise in exactly the region a histogram summarises away.
 *
 * Two scales per sub-block, not one: the low and high nibbles of `scales[ib32]`
 * cover the first and second pairs of the four groups (`db[l/2]`), so a 32-value
 * sub-block carries two independent scales.
 *
 * The index is ten bits — eight from `qs`, two more from `qh`, a different pair
 * per group.
 */
const decodeIQ2_S: DecodeBlock = (view, at, out, base) => {
  const d = half(view, at);
  const qsAt = at + 2;
  // The split the struct hides. Everything after it is signs, not indices.
  const signsAt = qsAt + QK_K / 8;
  const qhAt = qsAt + QK_K / 4;
  const scalesAt = qhAt + QK_K / 32;

  for (let ib32 = 0; ib32 < QK_K / 32; ib32++) {
    const sc = view.getUint8(scalesAt + ib32) as number;
    const db0 = f32(f32(d * (0.5 + (sc & 0xf))) * 0.25);
    const db1 = f32(f32(d * (0.5 + (sc >> 4))) * 0.25);
    const qh = view.getUint8(qhAt + ib32) as number;
    for (let l = 0; l < 4; l++) {
      // `l/2` in C is integer division: groups 0,1 take the low nibble's scale
      // and groups 2,3 the high one.
      const dl = l < 2 ? db0 : db1;
      const signs = view.getUint8(signsAt + 4 * ib32 + l) as number;
      // Ten bits: eight from `qs`, two from `qh`, shifted into 0x300.
      const g =
        (((view.getUint8(qsAt + 4 * ib32 + l) as number) |
          ((qh << (8 - 2 * l)) & 0x300)) as number) * 8;
      const dst = base + ib32 * 32 + l * 8;
      for (let j = 0; j < 8; j++) {
        const s = signs & (KMASK_IQ2XS[j] as number) ? -1 : 1;
        out[dst + j] = f32(dl * (IQ2S_GRID[g + j] as number)) * s;
      }
    }
  }
};

/**
 * `block_iq3_s { ggml_half d; uint8_t qs[64]; uint8_t qh[8]; uint8_t signs[32];
 * uint8_t scales[4]; }` — 110 bytes, 256 elements, 3.4375 bits per weight.
 *
 * The only format here that walks **two sub-blocks per step**, because one
 * `scales` byte serves both: its low nibble scales the first and its high nibble
 * the second. Four scale bytes for eight sub-blocks, so the outer loop runs four
 * times and each pass consumes two `qh` bytes, sixteen `qs` bytes and eight sign
 * bytes.
 *
 * Written as an explicit outer index rather than as ggml's pointer walk. The C
 * advances `qs`, `signs` and `qh` by different amounts at three different points
 * inside one iteration, and transcribing that as running offsets is how a
 * half-block slips.
 *
 * This is the **clarity** half of the rule stated on `decodeIQ1_M`: fidelity for
 * the codebook and the arithmetic, clarity for control flow. Nothing verifies
 * this function by resembling ggml — the golden does that — so resembling a
 * structure that is error-prone to copy buys nothing and costs a maintainer.
 *
 * The index is nine bits: eight from `qs`, one from `qh` — and the bit differs
 * between the two grid entries of a group, `8-2l` for the first and `7-2l` for
 * the second, so each `qh` byte supplies eight distinct bits.
 */
const decodeIQ3_S: DecodeBlock = (view, at, out, base) => {
  const d = half(view, at);
  const qsAt = at + 2;
  const qhAt = qsAt + QK_K / 4;
  const signsAt = qhAt + QK_K / 32;
  const scalesAt = signsAt + QK_K / 8;

  for (let ob = 0; ob < QK_K / 64; ob++) {
    const sc = view.getUint8(scalesAt + ob) as number;
    // `1 + 2s`: odd multiples only, so no scale silences a sub-block.
    const db = [f32(d * (1 + 2 * (sc & 0xf))), f32(d * (1 + 2 * (sc >> 4)))];
    for (let half2 = 0; half2 < 2; half2++) {
      const qh = view.getUint8(qhAt + 2 * ob + half2) as number;
      const dl = db[half2] as number;
      const qsBase = qsAt + 16 * ob + 8 * half2;
      const signBase = signsAt + 8 * ob + 4 * half2;
      for (let l = 0; l < 4; l++) {
        const signs = view.getUint8(signBase + l) as number;
        const g1 =
          (((view.getUint8(qsBase + 2 * l) as number) |
            ((qh << (8 - 2 * l)) & 256)) as number) * 4;
        const g2 =
          (((view.getUint8(qsBase + 2 * l + 1) as number) |
            ((qh << (7 - 2 * l)) & 256)) as number) * 4;
        const dst = base + ob * 64 + half2 * 32 + l * 8;
        for (let j = 0; j < 4; j++) {
          const s1 = signs & (KMASK_IQ2XS[j] as number) ? -1 : 1;
          const s2 = signs & (KMASK_IQ2XS[j + 4] as number) ? -1 : 1;
          out[dst + j] = f32(dl * (IQ3S_GRID[g1 + j] as number)) * s1;
          out[dst + j + 4] = f32(dl * (IQ3S_GRID[g2 + j] as number)) * s2;
        }
      }
    }
  }
};

/**
 * `block_iq1_m { uint8_t qs[32]; uint8_t qh[16]; uint8_t scales[8]; }` — 56
 * bytes, 256 elements, 1.75 bits per weight.
 *
 * **There is no `d` field.** Every other block format here opens with an f16
 * scale; this one has none, and its scale is assembled from four nibbles
 * scattered one per `uint16` of `scales`, then *reinterpreted* as an f16:
 *
 *     scale.u16 = (sc[0] >> 12) | ((sc[1] >> 8) & 0x00f0)
 *               | ((sc[2] >> 4) & 0x0f00) | (sc[3] & 0xf000)
 *
 * Each word's remaining twelve bits carry the per-sub-block scales, so the
 * format spends no whole byte on either. Reading `scales` as eight independent
 * bytes — which is what its declaration suggests — recovers neither.
 *
 * ## Why this one mirrors ggml's structure closely
 *
 * The rule these decoders follow (reviewer, 2026-08-18): **fidelity for the
 * codebook and the arithmetic, clarity for control flow.**
 *
 * Mirror the reference where fidelity *is* the verification. The scale gather
 * above is exactly that: four nibbles from four words, reinterpreted as a half,
 * with the remaining twelve bits of each word doing something else entirely. A
 * reader trusts that only because it visibly tracks ggml line for line, so it is
 * written to.
 *
 * Depart from the reference where its control flow is a hazard to copy — see
 * `decodeIQ3_S`, which replaces a three-cursor pointer walk with an explicit
 * index for exactly that reason. The two decisions look inconsistent and sit on
 * opposite sides of the same line deliberately.
 *
 * It shares `IQ1S_GRID` and `IQ1S_DELTA` with IQ1_S, and like it the delta's
 * sign is per group. Unlike it, there are two scales per sub-block (`dl1` for
 * the first two groups, `dl2` for the last two) and the delta bits live at
 * `0x08` and `0x80` of each `qh` byte rather than in the block's top bit.
 */
const decodeIQ1_M: DecodeBlock = (view, at, out, base) => {
  const qsAt = at;
  const qhAt = qsAt + QK_K / 8;
  const scalesAt = qhAt + QK_K / 16;
  const sc = [0, 1, 2, 3].map((i) => view.getUint16(scalesAt + 2 * i, true) as number);
  /*
   * Sixteen bits gathered from four places and read as a half. `decodeFloat16Bits`
   * rather than `half()`, because there is no f16 *in the file* to read — the
   * bit pattern only exists once assembled.
   */
  const d = decodeFloat16Bits(
    (((sc[0] as number) >> 12) |
      (((sc[1] as number) >> 8) & 0x00f0) |
      (((sc[2] as number) >> 4) & 0x0f00) |
      ((sc[3] as number) & 0xf000)) &
      0xffff,
  );

  for (let ib = 0; ib < QK_K / 32; ib++) {
    const w = sc[ib >> 1] as number;
    const shift = 6 * (ib & 1);
    const dl1 = f32(d * (2 * ((w >> shift) & 7) + 1));
    const dl2 = f32(d * (2 * ((w >> (shift + 3)) & 7) + 1));
    const qh0 = view.getUint8(qhAt + 2 * ib) as number;
    const qh1 = view.getUint8(qhAt + 2 * ib + 1) as number;
    // Eleven bits again, but assembled differently from IQ1_S: two groups per
    // `qh` byte, taking its low and high nibbles.
    const idx = [
      (view.getUint8(qsAt + 4 * ib) as number) | ((qh0 << 8) & 0x700),
      (view.getUint8(qsAt + 4 * ib + 1) as number) | ((qh0 << 4) & 0x700),
      (view.getUint8(qsAt + 4 * ib + 2) as number) | ((qh1 << 8) & 0x700),
      (view.getUint8(qsAt + 4 * ib + 3) as number) | ((qh1 << 4) & 0x700),
    ];
    const deltas = [
      qh0 & 0x08 ? -IQ1S_DELTA : IQ1S_DELTA,
      qh0 & 0x80 ? -IQ1S_DELTA : IQ1S_DELTA,
      qh1 & 0x08 ? -IQ1S_DELTA : IQ1S_DELTA,
      qh1 & 0x80 ? -IQ1S_DELTA : IQ1S_DELTA,
    ];
    for (let l = 0; l < 4; l++) {
      const dl = l < 2 ? dl1 : dl2;
      const g = (idx[l] as number) * 8;
      const delta = deltas[l] as number;
      const dst = base + ib * 32 + l * 8;
      for (let j = 0; j < 8; j++) {
        out[dst + j] = dl * f32((IQ1S_GRID[g + j] as number) + delta);
      }
    }
  }
};

/**
 * `block_tq1_0 { uint8_t qs[48]; uint8_t qh[4]; ggml_half d; }` — 54 bytes, 256
 * elements, 1.6875 bits per weight.
 *
 * Base 3, not base 2. Five ternary digits are packed into one byte because
 * `3^5 = 243 < 256`, and they are recovered by **multiplying rather than
 * shifting**: `q = qs[m] * pow3[n]` in `uint8`, so the multiply *wraps*, and the
 * digit falls out of the top bits as `(q * 3) >> 8`.
 *
 * The wraparound is load-bearing rather than incidental — it is what discards
 * the digits already consumed — so `& 0xff` is doing real work here and is not
 * defensive. Written with an explicit mask because JavaScript has no `uint8`.
 *
 * The layout is three runs, not one loop: 32 bytes covering five digits each,
 * then the 16-byte remainder at a stride of 16, then four digits from `qh`. The
 * strides differ because `sizeof(qs)` is 48 and only its first 32 bytes divide
 * evenly.
 *
 * Also note the scale sits **after** the payload, as it does in TQ2_0 and
 * nowhere else here.
 */
const decodeTQ1_0: DecodeBlock = (view, at, out, base) => {
  const QS = 48;
  const QH = 4;
  const d = half(view, at + QS + QH);
  const pow3 = [1, 3, 9, 27, 81, 243];
  let o = base;
  // A digit, from the top of a deliberately-wrapped byte.
  const trit = (byte: number, n: number): number =>
    ((((byte * (pow3[n] as number)) & 0xff) * 3) >> 8) - 1;

  for (let j = 0; j < 32; j += 32) {
    for (let n = 0; n < 5; n++) {
      for (let m = 0; m < 32; m++) {
        out[o++] = trit(view.getUint8(at + j + m) as number, n) * d;
      }
    }
  }
  for (let j = 32; j < QS; j += 16) {
    for (let n = 0; n < 5; n++) {
      for (let m = 0; m < 16; m++) {
        out[o++] = trit(view.getUint8(at + j + m) as number, n) * d;
      }
    }
  }
  // The tail: four more digits per `qh` byte, n outermost.
  for (let n = 0; n < 4; n++) {
    for (let j = 0; j < QH; j++) {
      out[o++] = trit(view.getUint8(at + QS + j) as number, n) * d;
    }
  }
};

/**
 * `block_tq2_0 { uint8_t qs[64]; ggml_half d; }` — 66 bytes, 256 elements,
 * 2.0625 bits per weight.
 *
 * The simplest format in this file, and the only trap is where the scale is:
 * **after** the 64 payload bytes, not before them. Reading `d` at offset 0 would
 * take two quant bytes as a half and scale the whole block by garbage.
 *
 * Two bits per element, but four *planes* rather than four consecutive pairs:
 * plane `l` takes bits `2l..2l+1` of all 32 bytes in a run before moving on, so
 * one byte contributes to four outputs 32 apart. `q - 1` maps `{0,1,2,3}` onto
 * `{-1,0,1,2}` — asymmetric, and not the ternary set the name suggests.
 */
const decodeTQ2_0: DecodeBlock = (view, at, out, base) => {
  const QS = 64;
  const d = half(view, at + QS);
  let o = base;
  for (let j = 0; j < QS; j += 32) {
    for (let l = 0; l < 4; l++) {
      for (let m = 0; m < 32; m++) {
        const q = ((view.getUint8(at + j + m) as number) >> (l * 2)) & 3;
        out[o++] = (q - 1) * d;
      }
    }
  }
};

const GGML_FORMATS: ReadonlyMap<string, GgmlBlockFormat> = new Map([
  ["Q4_0", { blockBytes: 18, blockElems: QK, decodeBlock: decodeQ4_0 }],
  ["Q4_1", { blockBytes: 20, blockElems: QK, decodeBlock: decodeQ4_1 }],
  ["Q5_0", { blockBytes: 22, blockElems: QK, decodeBlock: decodeQ5_0 }],
  ["Q5_1", { blockBytes: 24, blockElems: QK, decodeBlock: decodeQ5_1 }],
  ["Q8_0", { blockBytes: 34, blockElems: QK, decodeBlock: decodeQ8_0 }],
  ["Q2_K", { blockBytes: 84, blockElems: QK_K, decodeBlock: decodeQ2_K }],
  ["Q3_K", { blockBytes: 110, blockElems: QK_K, decodeBlock: decodeQ3_K }],
  ["Q4_K", { blockBytes: 144, blockElems: QK_K, decodeBlock: decodeQ4_K }],
  ["Q5_K", { blockBytes: 176, blockElems: QK_K, decodeBlock: decodeQ5_K }],
  ["Q6_K", { blockBytes: 210, blockElems: QK_K, decodeBlock: decodeQ6_K }],
  ["IQ4_NL", { blockBytes: 18, blockElems: QK, decodeBlock: decodeIQ4_NL }],
  ["IQ4_XS", { blockBytes: 136, blockElems: QK_K, decodeBlock: decodeIQ4_XS }],
  ["IQ3_XXS", { blockBytes: 98, blockElems: QK_K, decodeBlock: decodeIQ3_XXS }],
  ["IQ2_XXS", { blockBytes: 66, blockElems: QK_K, decodeBlock: decodeIQ2_XXS }],
  ["IQ1_S", { blockBytes: 50, blockElems: QK_K, decodeBlock: decodeIQ1_S }],
  ["IQ2_S", { blockBytes: 82, blockElems: QK_K, decodeBlock: decodeIQ2_S }],
  ["IQ3_S", { blockBytes: 110, blockElems: QK_K, decodeBlock: decodeIQ3_S }],
  ["IQ1_M", { blockBytes: 56, blockElems: QK_K, decodeBlock: decodeIQ1_M }],
  ["TQ1_0", { blockBytes: 54, blockElems: QK_K, decodeBlock: decodeTQ1_0 }],
  ["TQ2_0", { blockBytes: 66, blockElems: QK_K, decodeBlock: decodeTQ2_0 }],
]);

/** ggml type names this module can decode. */
export const DEQUANTIZABLE_GGML_TYPES: ReadonlySet<string> = new Set(
  GGML_FORMATS.keys(),
);

export function isDequantizableGgml(dtype: string): boolean {
  return GGML_FORMATS.has(dtype);
}

/** Bytes per block for the ggml types this module decodes. */
export const GGML_BLOCK_BYTES: ReadonlyMap<string, number> = new Map(
  [...GGML_FORMATS].map(([name, format]) => [name, format.blockBytes]),
);

/**
 * Decode block-format bytes into f32.
 *
 * `bytes` must start on a block boundary and contain whole blocks — a partial
 * block has no scale of its own and there is nothing sensible to do with it.
 * Throws rather than truncating, because a silently short read would produce a
 * histogram of the right shape from the wrong values.
 */
function dequantizeBlocks(
  bytes: ArrayBufferLike | ArrayBufferView,
  dtype: string,
  format: GgmlBlockFormat,
): Float32Array {
  const view = ArrayBuffer.isView(bytes)
    ? new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength)
    : new DataView(bytes);

  if (view.byteLength % format.blockBytes !== 0) {
    throw new Error(
      `${dtype} payload of ${view.byteLength} bytes is not a whole number of ${format.blockBytes}-byte blocks — a partial block carries no scale of its own.`,
    );
  }

  const blocks = view.byteLength / format.blockBytes;
  const out = new Float32Array(blocks * format.blockElems);
  for (let block = 0; block < blocks; block++) {
    format.decodeBlock(view, block * format.blockBytes, out, block * format.blockElems);
  }
  return out;
}

/**
 * Decode `Q8_0` bytes into f32.
 *
 * Kept as its own export because it is the format with an independent
 * real-repo check behind it, and the one the tests reach for directly.
 */
export function dequantizeQ8_0(bytes: ArrayBufferLike | ArrayBufferView): Float32Array {
  const format = GGML_FORMATS.get("Q8_0") as GgmlBlockFormat;
  return dequantizeBlocks(bytes, "Q8_0", format);
}

/**
 * The bound `Q8_0` guarantees, for a block of original values.
 *
 * llama.cpp picks `d = max|x| / 127` and rounds to nearest, so no element can be
 * off by more than half a step. This is the property the verification asserts,
 * and it is stated here rather than in the test so that the claim lives beside
 * the code that has to keep satisfying it.
 */
export function q8_0MaxError(originalBlock: readonly number[] | Float32Array): number {
  let max = 0;
  for (const value of originalBlock) max = Math.max(max, Math.abs(value));
  return max / 127 / 2;
}

/**
 * Decode a tensor's bytes, whatever the dtype says they are.
 *
 * One entry point so a caller does not have to know which formats are blocks —
 * `decodeFloats` throws a message naming the dtype for anything neither float
 * nor decodable.
 *
 * That refusal used to cover the whole IQ* and TQ* families, and this comment
 * said so long after it stopped being true: the grid-based i-quants and both
 * ternary formats decode as of 2026-08-18. What is left is `IQ2_XS`, the
 * intermediate `Q8_1`/`Q8_K`, and anything unpublished.
 */
export function decodeValues(
  bytes: ArrayBufferLike | ArrayBufferView,
  dtype: string,
): Float32Array {
  const format = GGML_FORMATS.get(dtype);
  if (format) return dequantizeBlocks(bytes, dtype, format);
  return decodeFloats(bytes, dtype);
}
